/**
 * Texts of the audience pages (AudiencePage.tsx): /schools and
 * /special-needs, and the links to them from the landing. Same languages as
 * landingText.ts. Only what Make It really does: no figures or testimonials.
 */
import { currentLanguage } from "../player/i18n";

export interface AudienceSection {
  kicker: string;
  title: string;
  text: string;
  bullets: string[];
}

export interface AudiencePageText {
  /** Browser tab title. */
  title: string;
  eyebrow: string;
  heroTitle: string;
  heroSub: string;
  /** The main button (sign up). */
  start: string;
  /** The second button (schools: contact; special needs: demos). */
  second: string;
  tilesTitle: string;
  tiles: { title: string; text: string }[];
  sections: AudienceSection[];
  finalTitle: string;
  finalSub: string;
}

export interface AudienceText {
  /** Footer column title and the landing band's heading. */
  forTitle: string;
  forSub: string;
  /** Link labels. */
  schools: string;
  special: string;
  /** One line under each link in the landing band. */
  schoolsLine: string;
  specialLine: string;
  more: string;
  pages: { schools: AudiencePageText; special: AudiencePageText };
}

const en: AudienceText = {
  forTitle: "Make It for…",
  forSub: "See how Make It helps in each setting.",
  schools: "Schools",
  special: "Special needs",
  schoolsLine: "Classrooms, results and licences for the whole school.",
  specialLine: "Activities adapted to every learner.",
  more: "Learn more",
  pages: {
    schools: {
      title: "Make It for schools",
      eyebrow: "Make It for schools",
      heroTitle: "Learning games for the whole school",
      heroSub: "Teachers create their own games and quizzes in minutes, share them with their classrooms and follow every student's progress. On the web, on tablets and on phones.",
      start: "Start for free",
      second: "Contact us",
      tilesTitle: "Why schools choose Make It",
      tiles: [
        { title: "For every subject and age", text: "More than 20 kinds of activity and libraries with thousands of images and sounds, ready for any subject, from infant school to secondary." },
        { title: "Students without accounts", text: "Students join a classroom with a code and their name. No email addresses, no passwords and nothing to install." },
        { title: "Results of every student", text: "See how each student did in every game and every quiz: scores, errors, time and the pages they reached." },
        { title: "Any device", text: "It works in the browser and in the apps for iPad, iPhone, Android and Mac, so it fits the devices your school already has." },
        { title: "Privacy first", text: "No advertising and no tracking. Classroom data is only used to run the classrooms, and we sign a data processing agreement with the school when it needs one." },
        { title: "Licences for teams and schools", text: "One licence for several teachers, with a licence manager. Students always use Make It for free." },
      ],
      sections: [
        {
          kicker: "Classrooms",
          title: "A classroom for every group",
          text: "Create a classroom for each group, give the students its code and share projects with them: with the whole class, or only with the students who need them.",
          bullets: ["Students join with a code", "Share with the whole class or with a few", "Move students between classrooms when groups change"],
        },
        {
          kicker: "Follow-up",
          title: "Know how every student is doing",
          text: "Games record each student's score, errors and time, and quizzes add every answer, a live ranking and the statistics of every question.",
          bullets: ["Results of every game and every quiz", "Answers and statistics per question", "Rankings that motivate the class"],
        },
        {
          kicker: "Create and share",
          title: "Teachers make their own materials",
          text: "Each teacher adapts the activities to their students in minutes, with their own texts, pictures and voice. Projects are saved in the cloud and can be shared with other teachers of the school.",
          bullets: ["No coding needed", "Copy and adapt projects from colleagues", "Synced on the web and in the apps"],
        },
      ],
      finalTitle: "Bring Make It to your school",
      finalSub: "Start for free with one classroom, or write to us to get a licence for all your teachers.",
    },
    special: {
      title: "Make It for special needs",
      eyebrow: "Make It for special educational needs",
      heroTitle: "Activities adapted to every learner",
      heroSub: "With Make It, teachers, therapists and families create games tailored to each person: their pictures, their voice, their pace. For special education, speech therapy, inclusive classrooms and at home.",
      start: "Start for free",
      second: "Try a demo",
      tilesTitle: "Designed to remove barriers",
      tiles: [
        { title: "No reading needed", text: "Instructions and elements can have sound: a tap and the learner hears what to do." },
        { title: "Mistakes don't count against them", text: "Games reward every success, never penalise mistakes and allow as many attempts as needed." },
        { title: "Their own world", text: "Use photos of their own things, places and people, and record your voice so they hear someone they know." },
        { title: "Readable text", text: "Choose the size of every text and fonts such as OpenDyslexic or a school handwriting font." },
        { title: "Calm when needed", text: "Turn off the sound effects or the music, and decide whether the learner can move between pages freely." },
        { title: "Simple gestures", text: "Tap, drag, connect and trace with a finger on a tablet: activities that also work on fine motor skills." },
      ],
      sections: [
        {
          kicker: "Personalise",
          title: "Made to measure for each learner",
          text: "Every project can be adapted to one person: their interests, their vocabulary and their level. Duplicate an activity and adjust it for another learner in a moment.",
          bullets: ["Your own photos and drawings", "Your own recorded voice", "Text size and fonts to choose from", "Thousands of images, including emotions and everyday life"],
        },
        {
          kicker: "Motivate",
          title: "Positive reinforcement on every page",
          text: "Each success is celebrated with stars and sounds, and mistakes just let the learner try again. No time limits in games, so everyone can go at their own pace.",
          bullets: ["Unlimited attempts", "Stars and sounds after every page", "No time pressure"],
        },
        {
          kicker: "Follow up",
          title: "One learner, their own activities",
          text: "Share specific projects with specific students in a classroom and see how each of them does: score, errors and time, page by page.",
          bullets: ["Share with only one or a few students", "Results of every game", "Works on tablets, computers and phones"],
        },
      ],
      finalTitle: "Create the first activity for your learners",
      finalSub: "It's free to start and you don't need any technical knowledge.",
    },
  },
};

const ca: AudienceText = {
  forTitle: "Make It per a…",
  forSub: "Descobreix com t'ajuda Make It en cada context.",
  schools: "Escoles",
  special: "Necessitats especials",
  schoolsLine: "Aules, resultats i llicències per a tota l'escola.",
  specialLine: "Activitats adaptades a cada alumne.",
  more: "Més informació",
  pages: {
    schools: {
      title: "Make It per a escoles",
      eyebrow: "Make It per a escoles",
      heroTitle: "Jocs educatius per a tota l'escola",
      heroSub: "Els docents creen els seus propis jocs i tests en pocs minuts, els comparteixen amb les seves aules i segueixen el progrés de cada alumne. A la web, a la tauleta i al mòbil.",
      start: "Comença gratis",
      second: "Contacta'ns",
      tilesTitle: "Per què les escoles trien Make It",
      tiles: [
        { title: "Per a totes les matèries i edats", text: "Més de 20 tipus d'activitats i biblioteques amb milers d'imatges i sons, a punt per a qualsevol matèria, d'infantil a secundària." },
        { title: "Alumnes sense comptes", text: "Els alumnes s'apunten a una aula amb un codi i el seu nom. Sense adreces de correu, sense contrasenyes i sense instal·lar res." },
        { title: "Resultats de cada alumne", text: "Mira com li ha anat a cada alumne en cada joc i cada test: puntuació, errors, temps i les pàgines a què ha arribat." },
        { title: "Qualsevol dispositiu", text: "Funciona al navegador i a les apps per a iPad, iPhone, Android i Mac, així que s'adapta als dispositius que ja té la teva escola." },
        { title: "La privacitat, primer", text: "Sense publicitat i sense rastreig. Les dades de les aules només es fan servir per fer funcionar les aules, i signem un acord d'encàrrec de tractament de dades amb l'escola quan el necessita." },
        { title: "Llicències per a equips i escoles", text: "Una llicència per a diversos docents, amb un gestor de llicències. Els alumnes sempre fan servir Make It gratis." },
      ],
      sections: [
        {
          kicker: "Aules",
          title: "Una aula per a cada grup",
          text: "Crea una aula per a cada grup, dona'n el codi als alumnes i comparteix-hi projectes: amb tota la classe o només amb els alumnes que els necessitin.",
          bullets: ["Els alumnes s'apunten amb un codi", "Comparteix amb tota la classe o amb uns quants", "Mou alumnes d'una aula a una altra quan canvien els grups"],
        },
        {
          kicker: "Seguiment",
          title: "Sàpigues com va cada alumne",
          text: "Els jocs registren la puntuació, els errors i el temps de cada alumne, i els tests hi afegeixen cada resposta, un rànquing en directe i les estadístiques de cada pregunta.",
          bullets: ["Resultats de cada joc i de cada test", "Respostes i estadístiques per pregunta", "Rànquings que motiven la classe"],
        },
        {
          kicker: "Crea i comparteix",
          title: "Els docents fan els seus propis materials",
          text: "Cada docent adapta les activitats als seus alumnes en pocs minuts, amb els seus propis textos, imatges i la seva veu. Els projectes es desen al núvol i es poden compartir amb altres docents de l'escola.",
          bullets: ["Sense saber programar", "Copia i adapta projectes dels companys", "Sincronitzat a la web i a les apps"],
        },
      ],
      finalTitle: "Porta Make It a la teva escola",
      finalSub: "Comença gratis amb una aula, o escriu-nos per obtenir una llicència per a tots els teus docents.",
    },
    special: {
      title: "Make It per a necessitats especials",
      eyebrow: "Make It per a necessitats educatives especials",
      heroTitle: "Activitats adaptades a cada alumne",
      heroSub: "Amb Make It, docents, terapeutes i famílies creen jocs a la mida de cada persona: les seves imatges, la seva veu, el seu ritme. Per a l'educació especial, la logopèdia, les aules inclusives i a casa.",
      start: "Comença gratis",
      second: "Prova una demo",
      tilesTitle: "Pensat per eliminar barreres",
      tiles: [
        { title: "No cal saber llegir", text: "Les instruccions i els elements poden tenir so: un toc i l'alumne sent què ha de fer." },
        { title: "Els errors no penalitzen", text: "Els jocs premien cada encert, mai no penalitzen els errors i permeten tants intents com calgui." },
        { title: "El seu propi món", text: "Fes servir fotos de les seves coses, llocs i persones, i grava la teva veu perquè sentin algú que coneixen." },
        { title: "Text llegible", text: "Tria la mida de cada text i tipus de lletra com OpenDyslexic o la lletra escolar." },
        { title: "Calma quan cal", text: "Desactiva els efectes de so o la música, i decideix si l'alumne pot moure's lliurement entre pàgines." },
        { title: "Gestos senzills", text: "Tocar, arrossegar, relacionar i traçar amb el dit a la tauleta: activitats que també treballen la motricitat fina." },
      ],
      sections: [
        {
          kicker: "Personalitza",
          title: "A mida per a cada alumne",
          text: "Cada projecte es pot adaptar a una persona: els seus interessos, el seu vocabulari i el seu nivell. Duplica una activitat i ajusta-la per a un altre alumne en un moment.",
          bullets: ["Les teves pròpies fotos i dibuixos", "La teva pròpia veu gravada", "Mida del text i tipus de lletra a triar", "Milers d'imatges, incloses emocions i vida quotidiana"],
        },
        {
          kicker: "Motiva",
          title: "Reforç positiu a cada pàgina",
          text: "Cada encert se celebra amb estrelles i sons, i els errors només fan que l'alumne torni a provar. Els jocs no tenen límit de temps, així que cadascú pot anar al seu ritme.",
          bullets: ["Intents il·limitats", "Estrelles i sons després de cada pàgina", "Sense pressió de temps"],
        },
        {
          kicker: "Seguiment",
          title: "Un alumne, les seves pròpies activitats",
          text: "Comparteix projectes concrets amb alumnes concrets d'una aula i mira com li va a cadascun: puntuació, errors i temps, pàgina per pàgina.",
          bullets: ["Comparteix amb un sol alumne o amb uns quants", "Resultats de cada joc", "Funciona a tauletes, ordinadors i mòbils"],
        },
      ],
      finalTitle: "Crea la primera activitat per als teus alumnes",
      finalSub: "Començar és gratis i no cal cap coneixement tècnic.",
    },
  },
};

const es: AudienceText = {
  forTitle: "Make It para…",
  forSub: "Descubre cómo ayuda Make It en cada contexto.",
  schools: "Escuelas",
  special: "Necesidades especiales",
  schoolsLine: "Aulas, resultados y licencias para toda la escuela.",
  specialLine: "Actividades adaptadas a cada alumno.",
  more: "Más información",
  pages: {
    schools: {
      title: "Make It para escuelas",
      eyebrow: "Make It para escuelas",
      heroTitle: "Juegos educativos para toda la escuela",
      heroSub: "Los docentes crean sus propios juegos y tests en pocos minutos, los comparten con sus aulas y siguen el progreso de cada alumno. En la web, en la tableta y en el móvil.",
      start: "Empieza gratis",
      second: "Contáctanos",
      tilesTitle: "Por qué las escuelas eligen Make It",
      tiles: [
        { title: "Para todas las materias y edades", text: "Más de 20 tipos de actividades y bibliotecas con miles de imágenes y sonidos, listas para cualquier materia, de infantil a secundaria." },
        { title: "Alumnos sin cuentas", text: "Los alumnos se apuntan a un aula con un código y su nombre. Sin correos electrónicos, sin contraseñas y sin instalar nada." },
        { title: "Resultados de cada alumno", text: "Mira cómo le ha ido a cada alumno en cada juego y cada test: puntuación, errores, tiempo y las páginas a las que ha llegado." },
        { title: "Cualquier dispositivo", text: "Funciona en el navegador y en las apps para iPad, iPhone, Android y Mac, así que se adapta a los dispositivos que ya tiene tu escuela." },
        { title: "La privacidad, primero", text: "Sin publicidad y sin rastreo. Los datos de las aulas solo se usan para que las aulas funcionen, y firmamos un contrato de encargo del tratamiento con la escuela cuando lo necesita." },
        { title: "Licencias para equipos y escuelas", text: "Una licencia para varios docentes, con un gestor de licencias. Los alumnos siempre usan Make It gratis." },
      ],
      sections: [
        {
          kicker: "Aulas",
          title: "Un aula para cada grupo",
          text: "Crea un aula para cada grupo, da su código a los alumnos y comparte proyectos con ellos: con toda la clase o solo con los alumnos que los necesiten.",
          bullets: ["Los alumnos se apuntan con un código", "Comparte con toda la clase o con unos pocos", "Mueve alumnos entre aulas cuando cambian los grupos"],
        },
        {
          kicker: "Seguimiento",
          title: "Conoce cómo le va a cada alumno",
          text: "Los juegos registran la puntuación, los errores y el tiempo de cada alumno, y los tests añaden cada respuesta, un ranking en directo y las estadísticas de cada pregunta.",
          bullets: ["Resultados de cada juego y de cada test", "Respuestas y estadísticas por pregunta", "Rankings que motivan a la clase"],
        },
        {
          kicker: "Crea y comparte",
          title: "Los docentes hacen sus propios materiales",
          text: "Cada docente adapta las actividades a sus alumnos en pocos minutos, con sus propios textos, imágenes y voz. Los proyectos se guardan en la nube y se pueden compartir con otros docentes de la escuela.",
          bullets: ["Sin saber programar", "Copia y adapta proyectos de los compañeros", "Sincronizado en la web y en las apps"],
        },
      ],
      finalTitle: "Lleva Make It a tu escuela",
      finalSub: "Empieza gratis con un aula, o escríbenos para conseguir una licencia para todos tus docentes.",
    },
    special: {
      title: "Make It para necesidades especiales",
      eyebrow: "Make It para necesidades educativas especiales",
      heroTitle: "Actividades adaptadas a cada alumno",
      heroSub: "Con Make It, docentes, terapeutas y familias crean juegos a la medida de cada persona: sus imágenes, su voz, su ritmo. Para educación especial, logopedia, aulas inclusivas y en casa.",
      start: "Empieza gratis",
      second: "Prueba una demo",
      tilesTitle: "Pensado para eliminar barreras",
      tiles: [
        { title: "No hace falta saber leer", text: "Las instrucciones y los elementos pueden tener sonido: un toque y el alumno oye qué tiene que hacer." },
        { title: "Los errores no penalizan", text: "Los juegos premian cada acierto, nunca penalizan los errores y permiten tantos intentos como hagan falta." },
        { title: "Su propio mundo", text: "Usa fotos de sus cosas, lugares y personas, y graba tu voz para que oigan a alguien que conocen." },
        { title: "Texto legible", text: "Elige el tamaño de cada texto y fuentes como OpenDyslexic o una letra escolar." },
        { title: "Calma cuando hace falta", text: "Desactiva los efectos de sonido o la música, y decide si el alumno puede moverse libremente entre páginas." },
        { title: "Gestos sencillos", text: "Tocar, arrastrar, relacionar y trazar con el dedo en la tableta: actividades que también trabajan la motricidad fina." },
      ],
      sections: [
        {
          kicker: "Personaliza",
          title: "A medida para cada alumno",
          text: "Cada proyecto se puede adaptar a una persona: sus intereses, su vocabulario y su nivel. Duplica una actividad y ajústala para otro alumno en un momento.",
          bullets: ["Tus propias fotos y dibujos", "Tu propia voz grabada", "Tamaño de texto y fuentes a elegir", "Miles de imágenes, incluidas emociones y vida cotidiana"],
        },
        {
          kicker: "Motiva",
          title: "Refuerzo positivo en cada página",
          text: "Cada acierto se celebra con estrellas y sonidos, y los errores solo hacen que el alumno vuelva a probar. Los juegos no tienen límite de tiempo, así que cada uno puede ir a su ritmo.",
          bullets: ["Intentos ilimitados", "Estrellas y sonidos después de cada página", "Sin presión de tiempo"],
        },
        {
          kicker: "Seguimiento",
          title: "Un alumno, sus propias actividades",
          text: "Comparte proyectos concretos con alumnos concretos de un aula y mira cómo le va a cada uno: puntuación, errores y tiempo, página a página.",
          bullets: ["Comparte con un solo alumno o con unos pocos", "Resultados de cada juego", "Funciona en tabletas, ordenadores y móviles"],
        },
      ],
      finalTitle: "Crea la primera actividad para tus alumnos",
      finalSub: "Empezar es gratis y no necesitas conocimientos técnicos.",
    },
  },
};

const fr: AudienceText = {
  forTitle: "Make It pour…",
  forSub: "Découvrez comment Make It aide dans chaque contexte.",
  schools: "Écoles",
  special: "Besoins particuliers",
  schoolsLine: "Classes, résultats et licences pour toute l'école.",
  specialLine: "Des activités adaptées à chaque élève.",
  more: "En savoir plus",
  pages: {
    schools: {
      title: "Make It pour les écoles",
      eyebrow: "Make It pour les écoles",
      heroTitle: "Des jeux éducatifs pour toute l'école",
      heroSub: "Les enseignants créent leurs propres jeux et quiz en quelques minutes, les partagent avec leurs classes et suivent la progression de chaque élève. Sur le web, sur tablette et sur mobile.",
      start: "Commencer gratuitement",
      second: "Nous contacter",
      tilesTitle: "Pourquoi les écoles choisissent Make It",
      tiles: [
        { title: "Pour toutes les matières et tous les âges", text: "Plus de 20 types d'activités et des bibliothèques de milliers d'images et de sons, prêtes pour n'importe quelle matière, de la maternelle au secondaire." },
        { title: "Des élèves sans compte", text: "Les élèves rejoignent une classe avec un code et leur nom. Ni adresse e-mail, ni mot de passe, ni rien à installer." },
        { title: "Les résultats de chaque élève", text: "Voyez comment chaque élève s'en est sorti à chaque jeu et à chaque quiz : score, erreurs, temps et pages atteintes." },
        { title: "Tous les appareils", text: "Make It fonctionne dans le navigateur et dans les applications pour iPad, iPhone, Android et Mac : il s'adapte aux appareils que votre école possède déjà." },
        { title: "La confidentialité d'abord", text: "Ni publicité ni pistage. Les données des classes servent uniquement au fonctionnement des classes, et nous signons un accord de traitement des données avec l'école quand elle en a besoin." },
        { title: "Des licences pour les équipes et les écoles", text: "Une licence pour plusieurs enseignants, avec un gestionnaire de licences. Les élèves utilisent toujours Make It gratuitement." },
      ],
      sections: [
        {
          kicker: "Classes",
          title: "Une classe pour chaque groupe",
          text: "Créez une classe pour chaque groupe, donnez son code aux élèves et partagez-y des projets : avec toute la classe ou seulement avec les élèves qui en ont besoin.",
          bullets: ["Les élèves rejoignent la classe avec un code", "Partage avec toute la classe ou quelques élèves", "Déplacez des élèves d'une classe à l'autre quand les groupes changent"],
        },
        {
          kicker: "Suivi",
          title: "Sachez où en est chaque élève",
          text: "Les jeux enregistrent le score, les erreurs et le temps de chaque élève, et les quiz y ajoutent chaque réponse, un classement en direct et les statistiques de chaque question.",
          bullets: ["Résultats de chaque jeu et de chaque quiz", "Réponses et statistiques par question", "Des classements qui motivent la classe"],
        },
        {
          kicker: "Créer et partager",
          title: "Les enseignants créent leurs propres supports",
          text: "Chaque enseignant adapte les activités à ses élèves en quelques minutes, avec ses propres textes, ses images et sa voix. Les projets sont enregistrés dans le cloud et peuvent être partagés avec d'autres enseignants de l'école.",
          bullets: ["Sans programmer", "Copiez et adaptez les projets de vos collègues", "Synchronisé sur le web et dans les applications"],
        },
      ],
      finalTitle: "Faites entrer Make It dans votre école",
      finalSub: "Commencez gratuitement avec une classe, ou écrivez-nous pour obtenir une licence pour tous vos enseignants.",
    },
    special: {
      title: "Make It pour les besoins particuliers",
      eyebrow: "Make It pour les besoins éducatifs particuliers",
      heroTitle: "Des activités adaptées à chaque élève",
      heroSub: "Avec Make It, enseignants, thérapeutes et familles créent des jeux sur mesure pour chaque personne : ses images, sa voix, son rythme. Pour l'enseignement spécialisé, l'orthophonie, les classes inclusives et à la maison.",
      start: "Commencer gratuitement",
      second: "Essayer une démo",
      tilesTitle: "Conçu pour lever les obstacles",
      tiles: [
        { title: "Pas besoin de savoir lire", text: "Les consignes et les éléments peuvent avoir du son : un simple toucher et l'élève entend ce qu'il doit faire." },
        { title: "Les erreurs ne sont pas pénalisées", text: "Les jeux récompensent chaque réussite, ne pénalisent jamais les erreurs et permettent autant d'essais que nécessaire." },
        { title: "Leur propre univers", text: "Utilisez des photos de leurs propres objets, lieux et proches, et enregistrez votre voix pour qu'ils entendent quelqu'un qu'ils connaissent." },
        { title: "Un texte lisible", text: "Choisissez la taille de chaque texte et des polices comme OpenDyslexic ou une écriture scolaire." },
        { title: "Le calme quand il le faut", text: "Désactivez les effets sonores ou la musique, et décidez si l'élève peut passer librement d'une page à l'autre." },
        { title: "Des gestes simples", text: "Toucher, glisser, relier et tracer du doigt sur une tablette : des activités qui travaillent aussi la motricité fine." },
      ],
      sections: [
        {
          kicker: "Personnaliser",
          title: "Sur mesure pour chaque élève",
          text: "Chaque projet peut être adapté à une personne : ses centres d'intérêt, son vocabulaire et son niveau. Dupliquez une activité et ajustez-la pour un autre élève en un instant.",
          bullets: ["Vos propres photos et dessins", "Votre propre voix enregistrée", "Taille du texte et polices au choix", "Des milliers d'images, dont les émotions et la vie quotidienne"],
        },
        {
          kicker: "Motiver",
          title: "Renforcement positif à chaque page",
          text: "Chaque réussite est célébrée par des étoiles et des sons, et après une erreur l'élève réessaie simplement. Pas de limite de temps dans les jeux : chacun peut avancer à son rythme.",
          bullets: ["Essais illimités", "Étoiles et sons après chaque page", "Aucune pression du temps"],
        },
        {
          kicker: "Suivre",
          title: "Un élève, ses propres activités",
          text: "Partagez des projets précis avec des élèves précis d'une classe et voyez comment chacun s'en sort : score, erreurs et temps, page par page.",
          bullets: ["Partage avec un seul élève ou quelques-uns", "Résultats de chaque jeu", "Fonctionne sur tablette, ordinateur et mobile"],
        },
      ],
      finalTitle: "Créez la première activité pour vos élèves",
      finalSub: "C'est gratuit pour commencer et aucune connaissance technique n'est nécessaire.",
    },
  },
};

const pt: AudienceText = {
  forTitle: "Make It para…",
  forSub: "Veja como o Make It ajuda em cada contexto.",
  schools: "Escolas",
  special: "Necessidades especiais",
  schoolsLine: "Salas de aula, resultados e licenças para toda a escola.",
  specialLine: "Atividades adaptadas a cada aluno.",
  more: "Saber mais",
  pages: {
    schools: {
      title: "Make It para escolas",
      eyebrow: "Make It para escolas",
      heroTitle: "Jogos educativos para toda a escola",
      heroSub: "Os professores criam os seus próprios jogos e testes em poucos minutos, partilham-nos com as suas salas de aula e acompanham o progresso de cada aluno. Na web, no tablet e no telemóvel.",
      start: "Comece grátis",
      second: "Contacte-nos",
      tilesTitle: "Porque é que as escolas escolhem o Make It",
      tiles: [
        { title: "Para todas as disciplinas e idades", text: "Mais de 20 tipos de atividades e bibliotecas com milhares de imagens e sons, prontas para qualquer disciplina, do pré-escolar ao secundário." },
        { title: "Alunos sem contas", text: "Os alunos entram numa sala de aula com um código e o seu nome. Sem endereços de email, sem palavras-passe e sem instalar nada." },
        { title: "Resultados de cada aluno", text: "Veja como correu cada jogo e cada teste a cada aluno: pontuação, erros, tempo e as páginas a que chegou." },
        { title: "Qualquer dispositivo", text: "Funciona no navegador e nas aplicações para iPad, iPhone, Android e Mac, por isso adapta-se aos dispositivos que a sua escola já tem." },
        { title: "Privacidade em primeiro lugar", text: "Sem publicidade e sem rastreamento. Os dados das salas de aula só são usados para o funcionamento das salas, e assinamos um acordo de tratamento de dados com a escola quando ela precisa." },
        { title: "Licenças para equipas e escolas", text: "Uma licença para vários professores, com um gestor de licenças. Os alunos usam sempre o Make It gratuitamente." },
      ],
      sections: [
        {
          kicker: "Salas de aula",
          title: "Uma sala de aula para cada grupo",
          text: "Crie uma sala de aula para cada grupo, dê o código aos alunos e partilhe projetos com eles: com toda a turma ou só com os alunos que precisam deles.",
          bullets: ["Os alunos entram com um código", "Partilhe com toda a turma ou com alguns", "Mude alunos de sala quando os grupos mudam"],
        },
        {
          kicker: "Acompanhamento",
          title: "Saiba como está cada aluno",
          text: "Os jogos registam a pontuação, os erros e o tempo de cada aluno, e os testes acrescentam cada resposta, uma classificação em direto e as estatísticas de cada pergunta.",
          bullets: ["Resultados de cada jogo e de cada teste", "Respostas e estatísticas por pergunta", "Classificações que motivam a turma"],
        },
        {
          kicker: "Criar e partilhar",
          title: "Os professores criam os seus próprios materiais",
          text: "Cada professor adapta as atividades aos seus alunos em poucos minutos, com os seus próprios textos, imagens e voz. Os projetos ficam guardados na nuvem e podem ser partilhados com outros professores da escola.",
          bullets: ["Sem saber programar", "Copie e adapte projetos dos colegas", "Sincronizado na web e nas aplicações"],
        },
      ],
      finalTitle: "Leve o Make It para a sua escola",
      finalSub: "Comece grátis com uma sala de aula, ou escreva-nos para obter uma licença para todos os seus professores.",
    },
    special: {
      title: "Make It para necessidades especiais",
      eyebrow: "Make It para necessidades educativas especiais",
      heroTitle: "Atividades adaptadas a cada aluno",
      heroSub: "Com o Make It, professores, terapeutas e famílias criam jogos à medida de cada pessoa: as suas imagens, a sua voz, o seu ritmo. Para educação especial, terapia da fala, salas de aula inclusivas e em casa.",
      start: "Comece grátis",
      second: "Experimente uma demo",
      tilesTitle: "Pensado para eliminar barreiras",
      tiles: [
        { title: "Não é preciso saber ler", text: "As instruções e os elementos podem ter som: um toque e o aluno ouve o que tem de fazer." },
        { title: "Os erros não penalizam", text: "Os jogos premeiam cada acerto, nunca penalizam os erros e permitem as tentativas que forem precisas." },
        { title: "O seu próprio mundo", text: "Use fotos das coisas, dos lugares e das pessoas deles, e grave a sua voz para que ouçam alguém que conhecem." },
        { title: "Texto legível", text: "Escolha o tamanho de cada texto e tipos de letra como OpenDyslexic ou uma letra escolar." },
        { title: "Calma quando é preciso", text: "Desligue os efeitos sonoros ou a música, e decida se o aluno pode passar livremente entre páginas." },
        { title: "Gestos simples", text: "Tocar, arrastar, relacionar e traçar com o dedo no tablet: atividades que também trabalham a motricidade fina." },
      ],
      sections: [
        {
          kicker: "Personalizar",
          title: "À medida de cada aluno",
          text: "Cada projeto pode ser adaptado a uma pessoa: os seus interesses, o seu vocabulário e o seu nível. Duplique uma atividade e ajuste-a para outro aluno num instante.",
          bullets: ["As suas próprias fotos e desenhos", "A sua própria voz gravada", "Tamanho do texto e tipos de letra à escolha", "Milhares de imagens, incluindo emoções e o dia a dia"],
        },
        {
          kicker: "Motivar",
          title: "Reforço positivo em cada página",
          text: "Cada acerto é celebrado com estrelas e sons, e os erros apenas deixam o aluno tentar de novo. Os jogos não têm limite de tempo, por isso cada um pode avançar ao seu ritmo.",
          bullets: ["Tentativas ilimitadas", "Estrelas e sons depois de cada página", "Sem pressão de tempo"],
        },
        {
          kicker: "Acompanhar",
          title: "Um aluno, as suas próprias atividades",
          text: "Partilhe projetos específicos com alunos específicos de uma sala de aula e veja como corre a cada um: pontuação, erros e tempo, página a página.",
          bullets: ["Partilhe só com um aluno ou com alguns", "Resultados de cada jogo", "Funciona em tablets, computadores e telemóveis"],
        },
      ],
      finalTitle: "Crie a primeira atividade para os seus alunos",
      finalSub: "Começar é grátis e não precisa de conhecimentos técnicos.",
    },
  },
};

const it: AudienceText = {
  forTitle: "Make It per…",
  forSub: "Scopri come Make It aiuta in ogni contesto.",
  schools: "Scuole",
  special: "Bisogni speciali",
  schoolsLine: "Classi, risultati e licenze per tutta la scuola.",
  specialLine: "Attività adattate a ogni studente.",
  more: "Scopri di più",
  pages: {
    schools: {
      title: "Make It per le scuole",
      eyebrow: "Make It per le scuole",
      heroTitle: "Giochi educativi per tutta la scuola",
      heroSub: "I docenti creano i propri giochi e quiz in pochi minuti, li condividono con le loro classi e seguono i progressi di ogni studente. Sul web, sui tablet e sui cellulari.",
      start: "Inizia gratis",
      second: "Contattaci",
      tilesTitle: "Perché le scuole scelgono Make It",
      tiles: [
        { title: "Per ogni materia ed età", text: "Più di 20 tipi di attività e librerie con migliaia di immagini e suoni, pronte per qualsiasi materia, dalla scuola dell'infanzia alla secondaria." },
        { title: "Studenti senza account", text: "Gli studenti entrano in una classe con un codice e il loro nome. Niente indirizzi email, niente password e niente da installare." },
        { title: "I risultati di ogni studente", text: "Vedi com'è andato ogni studente in ogni gioco e in ogni quiz: punteggio, errori, tempo e le pagine raggiunte." },
        { title: "Qualsiasi dispositivo", text: "Funziona nel browser e nelle app per iPad, iPhone, Android e Mac, quindi si adatta ai dispositivi che la tua scuola ha già." },
        { title: "Prima di tutto la privacy", text: "Niente pubblicità e nessun tracciamento. I dati delle classi servono solo a far funzionare le classi, e firmiamo un accordo sul trattamento dei dati con la scuola quando ne ha bisogno." },
        { title: "Licenze per team e scuole", text: "Una licenza per più docenti, con la gestione licenze. Gli studenti usano sempre Make It gratis." },
      ],
      sections: [
        {
          kicker: "Classi",
          title: "Una classe per ogni gruppo",
          text: "Crea una classe per ogni gruppo, dai il codice agli studenti e condividi progetti con loro: con tutta la classe o solo con gli studenti che ne hanno bisogno.",
          bullets: ["Gli studenti entrano con un codice", "Condividi con tutta la classe o con pochi", "Sposta gli studenti tra le classi quando i gruppi cambiano"],
        },
        {
          kicker: "Monitoraggio",
          title: "Scopri come va ogni studente",
          text: "I giochi registrano punteggio, errori e tempo di ogni studente, e i quiz aggiungono ogni risposta, una classifica in diretta e le statistiche di ogni domanda.",
          bullets: ["Risultati di ogni gioco e di ogni quiz", "Risposte e statistiche per domanda", "Classifiche che motivano la classe"],
        },
        {
          kicker: "Crea e condividi",
          title: "I docenti creano i propri materiali",
          text: "Ogni docente adatta le attività ai propri studenti in pochi minuti, con i propri testi, le proprie immagini e la propria voce. I progetti sono salvati nel cloud e si possono condividere con altri docenti della scuola.",
          bullets: ["Senza programmare", "Copia e adatta i progetti dei colleghi", "Sincronizzato sul web e nelle app"],
        },
      ],
      finalTitle: "Porta Make It nella tua scuola",
      finalSub: "Inizia gratis con una classe, oppure scrivici per ottenere una licenza per tutti i tuoi docenti.",
    },
    special: {
      title: "Make It per i bisogni speciali",
      eyebrow: "Make It per i bisogni educativi speciali",
      heroTitle: "Attività adattate a ogni studente",
      heroSub: "Con Make It, docenti, terapisti e famiglie creano giochi su misura per ogni persona: le sue immagini, la sua voce, il suo ritmo. Per la didattica speciale, la logopedia, le classi inclusive e a casa.",
      start: "Inizia gratis",
      second: "Prova una demo",
      tilesTitle: "Pensato per abbattere le barriere",
      tiles: [
        { title: "Non serve saper leggere", text: "Istruzioni ed elementi possono avere un suono: basta un tocco e lo studente sente cosa fare." },
        { title: "Gli errori non penalizzano", text: "I giochi premiano ogni successo, non penalizzano mai gli errori e permettono tutti i tentativi necessari." },
        { title: "Il suo mondo", text: "Usa foto delle sue cose, dei suoi luoghi e delle sue persone, e registra la tua voce perché senta qualcuno che conosce." },
        { title: "Testo leggibile", text: "Scegli la dimensione di ogni testo e caratteri come OpenDyslexic o un carattere in stile scolastico." },
        { title: "Calma quando serve", text: "Disattiva gli effetti sonori o la musica e decidi se lo studente può spostarsi liberamente tra le pagine." },
        { title: "Gesti semplici", text: "Tocca, trascina, collega e traccia con un dito sul tablet: attività che allenano anche la motricità fine." },
      ],
      sections: [
        {
          kicker: "Personalizza",
          title: "Su misura per ogni studente",
          text: "Ogni progetto si può adattare a una persona: i suoi interessi, il suo vocabolario e il suo livello. Duplica un'attività e adattala a un altro studente in un attimo.",
          bullets: ["Le tue foto e i tuoi disegni", "La tua voce registrata", "Dimensione del testo e caratteri a scelta", "Migliaia di immagini, comprese emozioni e vita quotidiana"],
        },
        {
          kicker: "Motiva",
          title: "Rinforzo positivo in ogni pagina",
          text: "Ogni successo viene festeggiato con stelle e suoni, e dopo un errore lo studente semplicemente riprova. Nei giochi non ci sono limiti di tempo, così ognuno può andare al proprio ritmo.",
          bullets: ["Tentativi illimitati", "Stelle e suoni dopo ogni pagina", "Nessuna pressione del tempo"],
        },
        {
          kicker: "Segui",
          title: "Uno studente, le sue attività",
          text: "Condividi progetti specifici con studenti specifici di una classe e vedi come va ciascuno: punteggio, errori e tempo, pagina per pagina.",
          bullets: ["Condividi con un solo studente o con pochi", "Risultati di ogni gioco", "Funziona su tablet, computer e cellulari"],
        },
      ],
      finalTitle: "Crea la prima attività per i tuoi studenti",
      finalSub: "Iniziare è gratis e non servono competenze tecniche.",
    },
  },
};

const de: AudienceText = {
  forTitle: "Make It für…",
  forSub: "Sieh, wie Make It in jedem Bereich hilft.",
  schools: "Schulen",
  special: "Förderbedarf",
  schoolsLine: "Klassen, Ergebnisse und Lizenzen für die ganze Schule.",
  specialLine: "Aktivitäten, angepasst an alle Lernenden.",
  more: "Mehr erfahren",
  pages: {
    schools: {
      title: "Make It für Schulen",
      eyebrow: "Make It für Schulen",
      heroTitle: "Lernspiele für die ganze Schule",
      heroSub: "Lehrkräfte erstellen in wenigen Minuten eigene Spiele und Quiz, teilen sie mit ihren Klassen und begleiten den Fortschritt jedes Schülers. Im Web, auf Tablets und auf Handys.",
      start: "Kostenlos starten",
      second: "Kontakt aufnehmen",
      tilesTitle: "Warum Schulen Make It wählen",
      tiles: [
        { title: "Für jedes Fach und jedes Alter", text: "Über 20 Arten von Aktivitäten und Bibliotheken mit Tausenden Bildern und Tönen, bereit für jedes Fach, vom Kindergarten bis zur Sekundarstufe." },
        { title: "Schüler ohne Konto", text: "Schüler treten einer Klasse mit einem Code und ihrem Namen bei. Keine E-Mail-Adressen, keine Passwörter und nichts zu installieren." },
        { title: "Ergebnisse jedes Schülers", text: "Sieh, wie jeder Schüler in jedem Spiel und jedem Quiz abgeschnitten hat: Punkte, Fehler, Zeit und die erreichten Seiten." },
        { title: "Jedes Gerät", text: "Es läuft im Browser und in den Apps für iPad, iPhone, Android und Mac und passt so zu den Geräten, die deine Schule schon hat." },
        { title: "Datenschutz zuerst", text: "Keine Werbung und kein Tracking. Klassendaten werden nur für den Betrieb der Klassen verwendet, und wir schließen mit der Schule einen Auftragsverarbeitungsvertrag, wenn sie einen braucht." },
        { title: "Lizenzen für Teams und Schulen", text: "Eine Lizenz für mehrere Lehrkräfte, mit Lizenzverwaltung. Schüler nutzen Make It immer kostenlos." },
      ],
      sections: [
        {
          kicker: "Klassen",
          title: "Eine Klasse für jede Gruppe",
          text: "Erstelle für jede Gruppe eine Klasse, gib den Schülern den Code und teile Projekte mit ihnen – mit der ganzen Klasse oder nur mit den Schülern, die sie brauchen.",
          bullets: ["Schüler treten mit einem Code bei", "Mit der ganzen Klasse oder einigen teilen", "Schüler zwischen Klassen verschieben, wenn sich Gruppen ändern"],
        },
        {
          kicker: "Begleitung",
          title: "Wisse, wo jeder Schüler steht",
          text: "Spiele speichern Punkte, Fehler und Zeit jedes Schülers, und Quiz ergänzen jede Antwort, eine Live-Rangliste und die Statistik jeder Frage.",
          bullets: ["Ergebnisse jedes Spiels und jedes Quiz", "Antworten und Statistiken pro Frage", "Ranglisten, die die Klasse motivieren"],
        },
        {
          kicker: "Erstellen und teilen",
          title: "Lehrkräfte gestalten ihre eigenen Materialien",
          text: "Jede Lehrkraft passt die Aktivitäten in wenigen Minuten an ihre Schüler an, mit eigenen Texten, Bildern und der eigenen Stimme. Projekte werden in der Cloud gespeichert und lassen sich mit anderen Lehrkräften der Schule teilen.",
          bullets: ["Ohne Programmieren", "Projekte von Kollegen kopieren und anpassen", "Synchron im Web und in den Apps"],
        },
      ],
      finalTitle: "Bring Make It an deine Schule",
      finalSub: "Starte kostenlos mit einer Klasse oder schreib uns, um eine Lizenz für alle deine Lehrkräfte zu bekommen.",
    },
    special: {
      title: "Make It für Förderbedarf",
      eyebrow: "Make It für sonderpädagogischen Förderbedarf",
      heroTitle: "Aktivitäten, angepasst an alle Lernenden",
      heroSub: "Mit Make It erstellen Lehrkräfte, Therapeuten und Familien Spiele, die zu jeder Person passen: ihre Bilder, ihre Stimme, ihr Tempo. Für Sonderpädagogik, Logopädie, inklusive Klassen und zu Hause.",
      start: "Kostenlos starten",
      second: "Demo ausprobieren",
      tilesTitle: "Gemacht, um Barrieren abzubauen",
      tiles: [
        { title: "Lesen nicht nötig", text: "Anweisungen und Elemente können Ton haben: einmal tippen und man hört, was zu tun ist." },
        { title: "Fehler werden nicht bestraft", text: "Spiele belohnen jeden Erfolg, bestrafen Fehler nie und erlauben so viele Versuche wie nötig." },
        { title: "Ihre eigene Welt", text: "Nutze Fotos von ihren eigenen Dingen, Orten und Menschen und nimm deine Stimme auf, damit sie jemanden hören, den sie kennen." },
        { title: "Gut lesbarer Text", text: "Wähle die Größe jedes Textes und Schriften wie OpenDyslexic oder eine Schulschrift." },
        { title: "Ruhe, wenn nötig", text: "Schalte die Soundeffekte oder die Musik aus und entscheide, ob man frei zwischen den Seiten wechseln kann." },
        { title: "Einfache Gesten", text: "Tippen, ziehen, verbinden und mit dem Finger nachfahren auf dem Tablet: Aktivitäten, die auch die Feinmotorik trainieren." },
      ],
      sections: [
        {
          kicker: "Personalisieren",
          title: "Maßgeschneidert für jeden Lernenden",
          text: "Jedes Projekt lässt sich an eine Person anpassen: ihre Interessen, ihren Wortschatz und ihr Niveau. Dupliziere eine Aktivität und passe sie im Handumdrehen für jemand anderen an.",
          bullets: ["Deine eigenen Fotos und Zeichnungen", "Deine eigene aufgenommene Stimme", "Textgröße und Schriften zur Auswahl", "Tausende Bilder, auch zu Gefühlen und Alltag"],
        },
        {
          kicker: "Motivieren",
          title: "Positive Verstärkung auf jeder Seite",
          text: "Jeder Erfolg wird mit Sternen und Tönen gefeiert, und nach einem Fehler versucht man es einfach noch einmal. Spiele haben kein Zeitlimit, so kann jeder im eigenen Tempo vorankommen.",
          bullets: ["Unbegrenzte Versuche", "Sterne und Töne nach jeder Seite", "Kein Zeitdruck"],
        },
        {
          kicker: "Begleiten",
          title: "Jedem Lernenden seine eigenen Aktivitäten",
          text: "Teile bestimmte Projekte mit bestimmten Schülern einer Klasse und sieh, wie es bei jedem läuft: Punkte, Fehler und Zeit, Seite für Seite.",
          bullets: ["Mit nur einem oder wenigen Schülern teilen", "Ergebnisse jedes Spiels", "Läuft auf Tablets, Computern und Handys"],
        },
      ],
      finalTitle: "Erstelle die erste Aktivität für deine Lernenden",
      finalSub: "Der Start ist kostenlos und du brauchst keine technischen Kenntnisse.",
    },
  },
};

const ar: AudienceText = {
  forTitle: "Make It من أجل…",
  forSub: "اكتشف كيف يساعد Make It في كل بيئة.",
  schools: "المدارس",
  special: "الاحتياجات الخاصة",
  schoolsLine: "فصول ونتائج وتراخيص للمدرسة كلها.",
  specialLine: "أنشطة مكيّفة لكل متعلّم.",
  more: "اعرف المزيد",
  pages: {
    schools: {
      title: "Make It للمدارس",
      eyebrow: "Make It للمدارس",
      heroTitle: "ألعاب تعليمية للمدرسة كلها",
      heroSub: "يُنشئ المعلمون ألعابهم واختباراتهم الخاصة في دقائق، ويشاركونها مع فصولهم، ويتابعون تقدّم كل طالب. على الويب وعلى الأجهزة اللوحية وعلى الهواتف.",
      start: "ابدأ مجانًا",
      second: "تواصل معنا",
      tilesTitle: "لماذا تختار المدارس Make It",
      tiles: [
        { title: "لكل المواد والأعمار", text: "أكثر من 20 نوعًا من الأنشطة ومكتبات بآلاف الصور والأصوات، جاهزة لأي مادة، من رياض الأطفال إلى المرحلة الثانوية." },
        { title: "طلاب بلا حسابات", text: "ينضم الطلاب إلى الفصل برمز وأسمائهم. دون عناوين بريد إلكتروني ودون كلمات مرور ودون تثبيت أي شيء." },
        { title: "نتائج كل طالب", text: "اطّلع على أداء كل طالب في كل لعبة وكل اختبار: النقاط والأخطاء والوقت والصفحات التي وصل إليها." },
        { title: "أي جهاز", text: "يعمل في المتصفح وفي تطبيقات iPad وiPhone وAndroid وMac، فيناسب الأجهزة الموجودة في مدرستك أصلًا." },
        { title: "الخصوصية أولًا", text: "لا إعلانات ولا تتبّع. لا تُستخدم بيانات الفصول إلا لتشغيل الفصول، ونوقّع اتفاقية معالجة البيانات مع المدرسة عندما تحتاج إليها." },
        { title: "تراخيص للفرق والمدارس", text: "ترخيص واحد لعدة معلمين، مع إدارة التراخيص. يستخدم الطلاب Make It مجانًا دائمًا." },
      ],
      sections: [
        {
          kicker: "الفصول",
          title: "فصل لكل مجموعة",
          text: "أنشئ فصلًا لكل مجموعة، وأعطِ رمزه للطلاب، وشارك المشاريع معهم: مع الفصل كله أو مع الطلاب الذين يحتاجون إليها فقط.",
          bullets: ["ينضم الطلاب برمز", "شارك مع الفصل كله أو مع بعضه", "انقل الطلاب بين الفصول عندما تتغيّر المجموعات"],
        },
        {
          kicker: "المتابعة",
          title: "اعرف كيف يتقدّم كل طالب",
          text: "تسجّل الألعاب نقاط كل طالب وأخطاءه ووقته، وتضيف الاختبارات كل إجابة وترتيبًا مباشرًا وإحصاءات كل سؤال.",
          bullets: ["نتائج كل لعبة وكل اختبار", "الإجابات والإحصاءات لكل سؤال", "ترتيب يحفّز الفصل"],
        },
        {
          kicker: "الإنشاء والمشاركة",
          title: "المعلمون يصنعون موادهم بأنفسهم",
          text: "يكيّف كل معلم الأنشطة لطلابه في دقائق، بنصوصه وصوره وصوته. تُحفظ المشاريع في السحابة ويمكن مشاركتها مع معلمين آخرين في المدرسة.",
          bullets: ["دون برمجة", "انسخ مشاريع زملائك وكيّفها", "متزامن على الويب وفي التطبيقات"],
        },
      ],
      finalTitle: "أدخل Make It إلى مدرستك",
      finalSub: "ابدأ مجانًا بفصل واحد، أو راسلنا للحصول على ترخيص لجميع معلميك.",
    },
    special: {
      title: "Make It للاحتياجات الخاصة",
      eyebrow: "Make It للاحتياجات التعليمية الخاصة",
      heroTitle: "أنشطة مكيّفة لكل متعلّم",
      heroSub: "مع Make It، يُنشئ المعلمون والمعالجون والعائلات ألعابًا مصمّمة لكل شخص: صوره، وصوته، وإيقاعه. للتربية الخاصة وعلاج النطق والفصول الدامجة وفي المنزل.",
      start: "ابدأ مجانًا",
      second: "جرّب عرضًا تجريبيًا",
      tilesTitle: "مصمّم لإزالة العوائق",
      tiles: [
        { title: "لا حاجة إلى القراءة", text: "يمكن أن يكون للتعليمات والعناصر صوت: لمسة واحدة ويسمع المتعلّم ما عليه فعله." },
        { title: "الأخطاء لا تُحسب ضدهم", text: "تكافئ الألعاب كل نجاح، ولا تعاقب الأخطاء أبدًا، وتسمح بعدد المحاولات الذي يحتاجون إليه." },
        { title: "عالمهم الخاص", text: "استخدم صورًا لأشيائهم وأماكنهم والأشخاص من حولهم، وسجّل صوتك ليسمعوا شخصًا يعرفونه." },
        { title: "نص سهل القراءة", text: "اختر حجم كل نص وخطوطًا مثل OpenDyslexic أو خطًا مدرسيًا." },
        { title: "الهدوء عند الحاجة", text: "أوقف المؤثرات الصوتية أو الموسيقى، وقرّر ما إذا كان المتعلّم يستطيع التنقّل بين الصفحات بحرية." },
        { title: "إيماءات بسيطة", text: "النقر والسحب والتوصيل والتتبّع بالإصبع على الجهاز اللوحي: أنشطة تنمّي المهارات الحركية الدقيقة أيضًا." },
      ],
      sections: [
        {
          kicker: "التخصيص",
          title: "مفصّل على مقاس كل متعلّم",
          text: "يمكن تكييف كل مشروع لشخص واحد: اهتماماته ومفرداته ومستواه. انسخ نشاطًا وعدّله لمتعلّم آخر في لحظة.",
          bullets: ["صورك ورسوماتك الخاصة", "صوتك المسجّل", "حجم النص والخطوط حسب اختيارك", "آلاف الصور، منها المشاعر والحياة اليومية"],
        },
        {
          kicker: "التحفيز",
          title: "تعزيز إيجابي في كل صفحة",
          text: "يُحتفى بكل نجاح بالنجوم والأصوات، أما الأخطاء فتعني ببساطة المحاولة من جديد. لا حدود زمنية في الألعاب، فيمكن لكل واحد أن يتقدّم بإيقاعه.",
          bullets: ["محاولات غير محدودة", "نجوم وأصوات بعد كل صفحة", "دون ضغط الوقت"],
        },
        {
          kicker: "المتابعة",
          title: "متعلّم واحد وأنشطته الخاصة",
          text: "شارك مشاريع محددة مع طلاب محددين في الفصل، واطّلع على أداء كل منهم: النقاط والأخطاء والوقت، صفحة بصفحة.",
          bullets: ["شارك مع طالب واحد أو بعض الطلاب فقط", "نتائج كل لعبة", "يعمل على الأجهزة اللوحية والحواسيب والهواتف"],
        },
      ],
      finalTitle: "أنشئ النشاط الأول لمتعلّميك",
      finalSub: "البدء مجاني ولا تحتاج إلى أي معرفة تقنية.",
    },
  },
};

const he: AudienceText = {
  forTitle: "Make It עבור…",
  forSub: "גלו איך Make It עוזר בכל מסגרת.",
  schools: "בתי ספר",
  special: "צרכים מיוחדים",
  schoolsLine: "כיתות, תוצאות ורישיונות לכל בית הספר.",
  specialLine: "פעילויות מותאמות לכל לומד.",
  more: "למידע נוסף",
  pages: {
    schools: {
      title: "Make It לבתי ספר",
      eyebrow: "Make It לבתי ספר",
      heroTitle: "משחקים חינוכיים לכל בית הספר",
      heroSub: "מורים יוצרים משחקים ומבחנים משלהם בדקות, משתפים אותם עם הכיתות שלהם ועוקבים אחרי ההתקדמות של כל תלמיד. באתר, בטאבלטים ובטלפונים.",
      start: "מתחילים בחינם",
      second: "צרו קשר",
      tilesTitle: "למה בתי ספר בוחרים ב־Make It",
      tiles: [
        { title: "לכל מקצוע ולכל גיל", text: "יותר מ־20 סוגי פעילויות וספריות עם אלפי תמונות וצלילים, מוכנות לכל מקצוע, מהגן ועד התיכון." },
        { title: "תלמידים בלי חשבונות", text: "התלמידים מצטרפים לכיתה עם קוד ועם השם שלהם. בלי כתובות דוא״ל, בלי סיסמאות ובלי להתקין כלום." },
        { title: "התוצאות של כל תלמיד", text: "ראו איך הצליח כל תלמיד בכל משחק ובכל מבחן: ניקוד, טעויות, זמן והדפים שאליהם הגיע." },
        { title: "כל מכשיר", text: "עובד בדפדפן ובאפליקציות ל־iPad, iPhone, Android ו־Mac, כך שהוא מתאים למכשירים שכבר יש בבית הספר שלכם." },
        { title: "פרטיות קודם כול", text: "בלי פרסומות ובלי מעקב. נתוני הכיתות משמשים רק להפעלת הכיתות, ואנחנו חותמים עם בית הספר על הסכם עיבוד נתונים כשהוא צריך אחד." },
        { title: "רישיונות לצוותים ולבתי ספר", text: "רישיון אחד לכמה מורים, עם ניהול רישיונות. תלמידים משתמשים ב־Make It תמיד בחינם." },
      ],
      sections: [
        {
          kicker: "כיתות",
          title: "כיתה לכל קבוצה",
          text: "צרו כיתה לכל קבוצה, תנו לתלמידים את הקוד ושתפו איתם פרויקטים: עם כל הכיתה או רק עם התלמידים שצריכים אותם.",
          bullets: ["התלמידים מצטרפים עם קוד", "שיתוף עם כל הכיתה או עם חלק ממנה", "העברת תלמידים בין כיתות כשהקבוצות משתנות"],
        },
        {
          kicker: "מעקב",
          title: "יודעים מה מצבו של כל תלמיד",
          text: "המשחקים רושמים את הניקוד, הטעויות והזמן של כל תלמיד, והמבחנים מוסיפים כל תשובה, דירוג בזמן אמת וסטטיסטיקות לכל שאלה.",
          bullets: ["תוצאות של כל משחק ושל כל מבחן", "תשובות וסטטיסטיקות לכל שאלה", "דירוגים שמניעים את הכיתה"],
        },
        {
          kicker: "יצירה ושיתוף",
          title: "המורים מכינים חומרים משלהם",
          text: "כל מורה מתאים את הפעילויות לתלמידים שלו בדקות, עם טקסטים, תמונות וקול משלו. הפרויקטים נשמרים בענן ואפשר לשתף אותם עם מורים אחרים בבית הספר.",
          bullets: ["בלי לתכנת", "העתקה והתאמה של פרויקטים של עמיתים", "מסונכרן באתר ובאפליקציות"],
        },
      ],
      finalTitle: "הביאו את Make It לבית הספר שלכם",
      finalSub: "התחילו בחינם עם כיתה אחת, או כתבו לנו כדי לקבל רישיון לכל המורים שלכם.",
    },
    special: {
      title: "Make It לצרכים מיוחדים",
      eyebrow: "Make It לצרכים חינוכיים מיוחדים",
      heroTitle: "פעילויות מותאמות לכל לומד",
      heroSub: "עם Make It, מורים, מטפלים ומשפחות יוצרים משחקים שמותאמים לכל אדם: התמונות שלו, הקול שלו, הקצב שלו. לחינוך מיוחד, לקלינאות תקשורת, לכיתות משלבות ולבית.",
      start: "מתחילים בחינם",
      second: "נסו הדגמה",
      tilesTitle: "נבנה כדי להסיר מחסומים",
      tiles: [
        { title: "לא צריך לדעת לקרוא", text: "להוראות ולרכיבים יכול להיות קול: נגיעה אחת והלומד שומע מה לעשות." },
        { title: "טעויות לא נענשות", text: "המשחקים מתגמלים כל הצלחה, אף פעם לא מענישים על טעויות ומאפשרים כמה ניסיונות שצריך." },
        { title: "העולם שלהם", text: "השתמשו בתמונות של החפצים, המקומות והאנשים שלהם, והקליטו את הקול שלכם כדי שישמעו מישהו שהם מכירים." },
        { title: "טקסט קריא", text: "בחרו את הגודל של כל טקסט וגופנים כמו OpenDyslexic או גופן של כתב יד בית־ספרי." },
        { title: "רוגע כשצריך", text: "כבו את אפקטי הקול או את המוזיקה, והחליטו אם הלומד יכול לעבור בין הדפים בחופשיות." },
        { title: "תנועות פשוטות", text: "נגיעה, גרירה, חיבור ומעקב באצבע בטאבלט: פעילויות שמתרגלות גם מוטוריקה עדינה." },
      ],
      sections: [
        {
          kicker: "התאמה אישית",
          title: "תפור למידותיו של כל לומד",
          text: "כל פרויקט אפשר להתאים לאדם אחד: לתחומי העניין שלו, לאוצר המילים שלו ולרמה שלו. שכפלו פעילות והתאימו אותה ללומד אחר ברגע.",
          bullets: ["התמונות והציורים שלכם", "הקול המוקלט שלכם", "גודל טקסט וגופנים לבחירה", "אלפי תמונות, כולל רגשות וחיי היומיום"],
        },
        {
          kicker: "מוטיבציה",
          title: "חיזוק חיובי בכל דף",
          text: "כל הצלחה נחגגת בכוכבים ובצלילים, וטעות פשוט מאפשרת ללומד לנסות שוב. במשחקים אין מגבלת זמן, כך שכל אחד יכול להתקדם בקצב שלו.",
          bullets: ["ניסיונות ללא הגבלה", "כוכבים וצלילים אחרי כל דף", "בלי לחץ של זמן"],
        },
        {
          kicker: "מעקב",
          title: "לומד אחד, פעילויות משלו",
          text: "שתפו פרויקטים מסוימים עם תלמידים מסוימים בכיתה וראו איך מצליח כל אחד מהם: ניקוד, טעויות וזמן, דף אחר דף.",
          bullets: ["שיתוף עם תלמיד אחד או עם כמה", "תוצאות של כל משחק", "עובד בטאבלטים, במחשבים ובטלפונים"],
        },
      ],
      finalTitle: "צרו את הפעילות הראשונה ללומדים שלכם",
      finalSub: "ההתחלה בחינם ולא צריך שום ידע טכני.",
    },
  },
};

const eu: AudienceText = {
  forTitle: "Make It, norentzat?",
  forSub: "Ikusi nola laguntzen duen Make It-ek eremu bakoitzean.",
  schools: "Ikastetxeak",
  special: "Premia bereziak",
  schoolsLine: "Ikasgelak, emaitzak eta lizentziak ikastetxe osorako.",
  specialLine: "Ikasle bakoitzari egokitutako jarduerak.",
  more: "Informazio gehiago",
  pages: {
    schools: {
      title: "Make It ikastetxeentzat",
      eyebrow: "Make It ikastetxeentzat",
      heroTitle: "Ikasteko jokoak ikastetxe osorako",
      heroSub: "Irakasleek beren jokoak eta galdetegiak sortzen dituzte minutu gutxitan, ikasgelekin partekatzen dituzte eta ikasle bakoitzaren aurrerapenaren jarraipena egiten dute. Webean, tabletetan eta mugikorretan.",
      start: "Hasi doan",
      second: "Jarri gurekin harremanetan",
      tilesTitle: "Zergatik aukeratzen duten ikastetxeek Make It",
      tiles: [
        {
          title: "Irakasgai eta adin guztietarako",
          text: "20 jarduera mota baino gehiago eta milaka irudi eta soinu dituzten liburutegiak, edozein irakasgaitarako prest, Haur Hezkuntzatik Bigarren Hezkuntzara.",
        },
        {
          title: "Ikasleak konturik gabe",
          text: "Ikasleak kode batekin eta beren izenarekin sartzen dira ikasgelan. Ez helbide elektronikorik, ez pasahitzik, ez ezer instalatu beharrik.",
        },
        {
          title: "Ikasle guztien emaitzak",
          text: "Ikusi ikasle bakoitzak nola egin duen joko eta galdetegi bakoitzean: puntuazioak, akatsak, denbora eta iritsi diren orriak.",
        },
        {
          title: "Edozein gailu",
          text: "Nabigatzailean eta iPad, iPhone, Android eta Mac gailuetarako aplikazioetan dabil, eta, beraz, ikastetxeak dagoeneko dituen gailuetara egokitzen da.",
        },
        {
          title: "Pribatutasuna lehenik",
          text: "Ez dago publizitaterik ez jarraipenik. Ikasgeletako datuak ikasgelak kudeatzeko bakarrik erabiltzen dira, eta datuen tratamendurako akordioa sinatzen dugu ikastetxearekin behar duenean.",
        },
        {
          title: "Talde eta ikastetxeentzako lizentziak",
          text: "Lizentzia bakarra hainbat irakaslerentzat, lizentzia-kudeatzaile batekin. Ikasleek beti doan erabiltzen dute Make It.",
        },
      ],
      sections: [
        {
          kicker: "Ikasgelak",
          title: "Ikasgela bat talde bakoitzeko",
          text: "Sortu ikasgela bat talde bakoitzarentzat, eman haren kodea ikasleei eta partekatu proiektuak haiekin: gela osoarekin edo behar dituzten ikasleekin bakarrik.",
          bullets: [
            "Ikasleak kode batekin sartzen dira",
            "Partekatu gela osoarekin edo ikasle batzuekin",
            "Eraman ikasleak ikasgela batetik bestera taldeak aldatzen direnean",
          ],
        },
        {
          kicker: "Jarraipena",
          title: "Jakin nola doan ikasle bakoitza",
          text: "Jokoek ikasle bakoitzaren puntuazioa, akatsak eta denbora erregistratzen dituzte, eta galdetegiek, gainera, erantzun guztiak, zuzeneko sailkapena eta galdera bakoitzaren estatistikak.",
          bullets: ["Joko eta galdetegi guztien emaitzak", "Erantzunak eta estatistikak galderaka", "Gela motibatzen duten sailkapenak"],
        },
        {
          kicker: "Sortu eta partekatu",
          title: "Irakasleek beren materialak sortzen dituzte",
          text: "Irakasle bakoitzak minutu gutxitan egokitzen ditu jarduerak bere ikasleetara, bere testu, irudi eta ahotsarekin. Proiektuak hodeian gordetzen dira, eta ikastetxeko beste irakasle batzuekin parteka daitezke.",
          bullets: ["Ez da programatu beharrik", "Kopiatu eta egokitu lankideen proiektuak", "Sinkronizatuta webean eta aplikazioetan"],
        },
      ],
      finalTitle: "Ekarri Make It zure ikastetxera",
      finalSub: "Hasi doan ikasgela batekin, edo idatzi iezaguzu irakasle guztientzako lizentzia bat lortzeko.",
    },
    special: {
      title: "Make It premia berezietarako",
      eyebrow: "Make It hezkuntza-premia berezietarako",
      heroTitle: "Ikasle bakoitzari egokitutako jarduerak",
      heroSub: "Make It-ekin, irakasleek, terapeutek eta familiek pertsona bakoitzaren neurriko jokoak sortzen dituzte: haren irudiak, haren ahotsa, haren erritmoa. Hezkuntza berezirako, logopediarako, ikasgela inklusiboetarako eta etxerako.",
      start: "Hasi doan",
      second: "Probatu demo bat",
      tilesTitle: "Oztopoak kentzeko pentsatua",
      tiles: [
        {
          title: "Irakurtzen jakin beharrik gabe",
          text: "Jarraibideek eta elementuek soinua izan dezakete: ukitu bat, eta ikasleak entzuten du zer egin behar duen.",
        },
        {
          title: "Akatsek ez dute zigortzen",
          text: "Jokoek asmatze bakoitza saritzen dute, ez dituzte inoiz akatsak zigortzen eta behar adina saiakera egiteko aukera ematen dute.",
        },
        {
          title: "Beren mundua",
          text: "Erabili haien gauzen, lekuen eta pertsonen argazkiak, eta grabatu zure ahotsa, ezagutzen duten norbait entzun dezaten.",
        },
        {
          title: "Testu irakurgarria",
          text: "Aukeratu testu bakoitzaren tamaina, eta OpenDyslexic bezalako letra-tipoak edo eskolako idazkera-letra bat.",
        },
        {
          title: "Lasaitasuna behar denean",
          text: "Desaktibatu soinu-efektuak edo musika, eta erabaki ikasleak orrien artean libreki mugi daitekeen ala ez.",
        },
        { title: "Keinu errazak", text: "Ukitu, arrastatu, lotu eta trazatu hatzarekin tabletan: motrizitate fina ere lantzen duten jarduerak." },
      ],
      sections: [
        {
          kicker: "Pertsonalizatu",
          title: "Ikasle bakoitzaren neurrira",
          text: "Proiektu bakoitza pertsona bakar bati egokitu daiteke: haren interesei, hiztegiari eta mailari. Bikoiztu jarduera bat eta doitu beste ikasle batentzat berehala.",
          bullets: [
            "Zure argazkiak eta marrazkiak",
            "Zuk grabatutako ahotsa",
            "Testuaren tamaina eta letra-tipoak aukeran",
            "Milaka irudi, emozioak eta eguneroko bizitza barne",
          ],
        },
        {
          kicker: "Motibatu",
          title: "Indartze positiboa orri guztietan",
          text: "Asmatze bakoitza izarrekin eta soinuekin ospatzen da, eta akats baten ondoren ikasleak berriro saiatu besterik ez du egin behar. Jokoetan ez dago denbora-mugarik; beraz, bakoitzak bere erritmoan aurrera egin dezake.",
          bullets: ["Saiakera mugagabeak", "Izarrak eta soinuak orri bakoitzaren ondoren", "Denbora-presiorik gabe"],
        },
        {
          kicker: "Jarraipena egin",
          title: "Ikasle bakoitzak, bere jarduerak",
          text: "Partekatu proiektu jakinak ikasgela bateko ikasle jakin batzuekin, eta ikusi bakoitzak nola egiten duen: puntuazioa, akatsak eta denbora, orriz orri.",
          bullets: ["Partekatu ikasle bakar batekin edo batzuekin", "Joko guztien emaitzak", "Tabletetan, ordenagailuetan eta mugikorretan dabil"],
        },
      ],
      finalTitle: "Sortu zure ikasleentzako lehen jarduera",
      finalSub: "Doan has zaitezke, eta ez duzu ezagutza teknikorik behar.",
    },
  },
};

const gl: AudienceText = {
  forTitle: "Make It para…",
  forSub: "Descobre como axuda Make It en cada ámbito.",
  schools: "Centros educativos",
  special: "Necesidades especiais",
  schoolsLine: "Aulas, resultados e licenzas para todo o centro.",
  specialLine: "Actividades adaptadas a cada alumno.",
  more: "Saber máis",
  pages: {
    schools: {
      title: "Make It para centros educativos",
      eyebrow: "Make It para centros educativos",
      heroTitle: "Xogos educativos para todo o centro",
      heroSub: "O profesorado crea os seus propios xogos e cuestionarios en minutos, compárteos coas súas aulas e segue o progreso de cada alumno. Na web, en tabletas e en móbiles.",
      start: "Comeza gratis",
      second: "Contacta connosco",
      tilesTitle: "Por que os centros escollen Make It",
      tiles: [
        {
          title: "Para todas as materias e idades",
          text: "Máis de 20 tipos de actividade e bibliotecas con miles de imaxes e sons, listas para calquera materia, desde a educación infantil ata a secundaria.",
        },
        {
          title: "Alumnado sen contas",
          text: "O alumnado entra nunha aula co código e o seu nome. Sen enderezos de correo, sen contrasinais e sen instalar nada.",
        },
        {
          title: "Resultados de cada alumno",
          text: "Mira como lle foi a cada alumno en cada xogo e en cada cuestionario: puntuacións, erros, tempo e as páxinas ás que chegou.",
        },
        {
          title: "Calquera dispositivo",
          text: "Funciona no navegador e nas aplicacións para iPad, iPhone, Android e Mac, así que se adapta aos dispositivos que xa ten o teu centro.",
        },
        {
          title: "A privacidade, primeiro",
          text: "Sen publicidade e sen rastrexo. Os datos das aulas só se usan para que as aulas funcionen, e asinamos un contrato de tratamento de datos co centro cando o necesita.",
        },
        {
          title: "Licenzas para equipos e centros",
          text: "Unha licenza para varios docentes, cun xestor de licenzas. O alumnado sempre usa Make It gratis.",
        },
      ],
      sections: [
        {
          kicker: "Aulas",
          title: "Unha aula para cada grupo",
          text: "Crea unha aula para cada grupo, dálle o código ao alumnado e comparte proxectos con el: con toda a clase ou só cos alumnos que os necesiten.",
          bullets: ["O alumnado entra cun código", "Comparte con toda a clase ou só con algúns", "Move alumnos entre aulas cando cambien os grupos"],
        },
        {
          kicker: "Seguimento",
          title: "Coñece como lle vai a cada alumno",
          text: "Os xogos rexistran a puntuación, os erros e o tempo de cada alumno, e os cuestionarios engaden cada resposta, unha clasificación en directo e as estatísticas de cada pregunta.",
          bullets: ["Resultados de cada xogo e de cada cuestionario", "Respostas e estatísticas por pregunta", "Clasificacións que motivan a clase"],
        },
        {
          kicker: "Crea e comparte",
          title: "O profesorado crea os seus propios materiais",
          text: "Cada docente adapta as actividades ao seu alumnado en minutos, cos seus propios textos, imaxes e voz. Os proxectos gárdanse na nube e pódense compartir con outros docentes do centro.",
          bullets: ["Sen necesidade de programar", "Copia e adapta proxectos dos compañeiros", "Sincronizado na web e nas aplicacións"],
        },
      ],
      finalTitle: "Leva Make It ao teu centro",
      finalSub: "Comeza gratis cunha aula ou escríbenos para obter unha licenza para todo o teu profesorado.",
    },
    special: {
      title: "Make It para necesidades especiais",
      eyebrow: "Make It para necesidades educativas especiais",
      heroTitle: "Actividades adaptadas a cada alumno",
      heroSub: "Con Make It, docentes, terapeutas e familias crean xogos feitos á medida de cada persoa: as súas imaxes, a súa voz, o seu ritmo. Para educación especial, logopedia, aulas inclusivas e na casa.",
      start: "Comeza gratis",
      second: "Proba unha demo",
      tilesTitle: "Pensado para eliminar barreiras",
      tiles: [
        { title: "Sen necesidade de ler", text: "As instrucións e os elementos poden ter son: un toque e o alumno escoita o que ten que facer." },
        {
          title: "Os erros non contan en contra",
          text: "Os xogos premian cada acerto, nunca penalizan os erros e permiten tantos intentos como faga falta.",
        },
        { title: "O seu propio mundo", text: "Usa fotos das súas cousas, lugares e persoas, e grava a túa voz para que escoite alguén coñecido." },
        { title: "Texto lexible", text: "Escolle o tamaño de cada texto e tipos de letra como OpenDyslexic ou unha letra manuscrita escolar." },
        {
          title: "Calma cando fai falta",
          text: "Desactiva os efectos de son ou a música, e decide se o alumno pode moverse libremente entre as páxinas.",
        },
        {
          title: "Xestos sinxelos",
          text: "Tocar, arrastrar, conectar e repasar co dedo nunha tableta: actividades que tamén traballan a motricidade fina.",
        },
      ],
      sections: [
        {
          kicker: "Personaliza",
          title: "Feito á medida de cada alumno",
          text: "Cada proxecto pódese adaptar a unha persoa: os seus intereses, o seu vocabulario e o seu nivel. Duplica unha actividade e axústaa para outro alumno nun momento.",
          bullets: [
            "As túas propias fotos e debuxos",
            "A túa propia voz gravada",
            "Tamaño do texto e tipos de letra a escoller",
            "Miles de imaxes, incluídas emocións e vida cotiá",
          ],
        },
        {
          kicker: "Motiva",
          title: "Reforzo positivo en cada páxina",
          text: "Cada acerto celébrase con estrelas e sons, e os erros só permiten que o alumno o volva tentar. Os xogos non teñen límite de tempo, para que cada quen vaia ao seu ritmo.",
          bullets: ["Intentos ilimitados", "Estrelas e sons despois de cada páxina", "Sen presión de tempo"],
        },
        {
          kicker: "Seguimento",
          title: "Un alumno, as súas propias actividades",
          text: "Comparte proxectos concretos con alumnos concretos dunha aula e mira como lle vai a cada un: puntuación, erros e tempo, páxina a páxina.",
          bullets: ["Comparte só cun alumno ou con algúns", "Resultados de cada xogo", "Funciona en tabletas, ordenadores e móbiles"],
        },
      ],
      finalTitle: "Crea a primeira actividade para o teu alumnado",
      finalSub: "Comezar é gratis e non necesitas coñecementos técnicos.",
    },
  },
};

const nl: AudienceText = {
  forTitle: "Make It voor…",
  forSub: "Ontdek hoe Make It helpt in elke situatie.",
  schools: "Scholen",
  special: "Speciale behoeften",
  schoolsLine: "Klassen, resultaten en licenties voor de hele school.",
  specialLine: "Activiteiten aangepast aan elke leerling.",
  more: "Meer informatie",
  pages: {
    schools: {
      title: "Make It voor scholen",
      eyebrow: "Make It voor scholen",
      heroTitle: "Leerzame spellen voor de hele school",
      heroSub: "Leerkrachten maken in een paar minuten hun eigen spellen en quizzen, delen ze met hun klassen en volgen de voortgang van elke leerling. Op het web, op tablets en op telefoons.",
      start: "Gratis beginnen",
      second: "Neem contact op",
      tilesTitle: "Waarom scholen voor Make It kiezen",
      tiles: [
        {
          title: "Voor elk vak en elke leeftijd",
          text: "Meer dan 20 soorten activiteiten en bibliotheken met duizenden afbeeldingen en geluiden, klaar voor elk vak, van de kleuterklas tot de middelbare school.",
        },
        {
          title: "Leerlingen zonder account",
          text: "Leerlingen doen mee aan een klas met een code en hun naam. Geen e-mailadressen, geen wachtwoorden en niets te installeren.",
        },
        {
          title: "Resultaten van elke leerling",
          text: "Bekijk hoe elke leerling het deed in elk spel en elke quiz: scores, fouten, tijd en tot welke pagina ze kwamen.",
        },
        {
          title: "Elk apparaat",
          text: "Het werkt in de browser en in de apps voor iPad, iPhone, Android en Mac, dus het past bij de apparaten die je school al heeft.",
        },
        {
          title: "Privacy voorop",
          text: "Geen advertenties en geen tracking. Klasgegevens worden alleen gebruikt om de klassen te laten werken, en we sluiten een verwerkersovereenkomst met de school als die daarom vraagt.",
        },
        {
          title: "Licenties voor teams en scholen",
          text: "Eén licentie voor meerdere leerkrachten, met een licentiebeheerder. Leerlingen gebruiken Make It altijd gratis.",
        },
      ],
      sections: [
        {
          kicker: "Klassen",
          title: "Een klas voor elke groep",
          text: "Maak een klas voor elke groep, geef de leerlingen de code en deel projecten met hen: met de hele klas, of alleen met de leerlingen die ze nodig hebben.",
          bullets: [
            "Leerlingen doen mee met een code",
            "Deel met de hele klas of met een paar leerlingen",
            "Verplaats leerlingen naar een andere klas als groepen veranderen",
          ],
        },
        {
          kicker: "Voortgang",
          title: "Weet hoe elke leerling ervoor staat",
          text: "Spellen registreren de score, fouten en tijd van elke leerling, en quizzen voegen daar elk antwoord, een live ranglijst en de statistieken van elke vraag aan toe.",
          bullets: ["Resultaten van elk spel en elke quiz", "Antwoorden en statistieken per vraag", "Ranglijsten die de klas motiveren"],
        },
        {
          kicker: "Maken en delen",
          title: "Leerkrachten maken hun eigen materiaal",
          text: "Elke leerkracht past de activiteiten in een paar minuten aan op de eigen leerlingen, met eigen teksten, afbeeldingen en stem. Projecten worden in de cloud bewaard en kunnen met andere leerkrachten van de school worden gedeeld.",
          bullets: ["Je hoeft niet te programmeren", "Kopieer projecten van collega's en pas ze aan", "Gesynchroniseerd op het web en in de apps"],
        },
      ],
      finalTitle: "Breng Make It naar je school",
      finalSub: "Begin gratis met één klas, of mail ons voor een licentie voor al je leerkrachten.",
    },
    special: {
      title: "Make It voor speciale behoeften",
      eyebrow: "Make It voor speciale onderwijsbehoeften",
      heroTitle: "Activiteiten aangepast aan elke leerling",
      heroSub: "Met Make It maken leerkrachten, therapeuten en gezinnen spellen op maat van elke persoon: met eigen afbeeldingen, een vertrouwde stem en in een eigen tempo. Voor speciaal onderwijs, logopedie, inclusieve klassen en thuis.",
      start: "Gratis beginnen",
      second: "Probeer een demo",
      tilesTitle: "Ontworpen om drempels weg te nemen",
      tiles: [
        { title: "Lezen is niet nodig", text: "Instructies en elementen kunnen geluid hebben: één tik en de leerling hoort wat er moet gebeuren." },
        {
          title: "Fouten worden niet bestraft",
          text: "Spellen belonen elk succes, bestraffen nooit fouten en laten zoveel pogingen toe als nodig is.",
        },
        {
          title: "Hun eigen wereld",
          text: "Gebruik foto's van hun eigen spullen, plekken en mensen, en neem je stem op zodat ze iemand horen die ze kennen.",
        },
        { title: "Leesbare tekst", text: "Kies de grootte van elke tekst en lettertypen zoals OpenDyslexic of een schoolschrift." },
        {
          title: "Rust wanneer dat nodig is",
          text: "Zet de geluidseffecten of de muziek uit en bepaal of de leerling vrij tussen de pagina's kan bewegen.",
        },
        {
          title: "Eenvoudige gebaren",
          text: "Tikken, slepen, verbinden en natekenen met een vinger op een tablet: activiteiten die ook de fijne motoriek oefenen.",
        },
      ],
      sections: [
        {
          kicker: "Personaliseren",
          title: "Op maat van elke leerling",
          text: "Elk project kan worden aangepast aan één persoon: aan zijn of haar interesses, woordenschat en niveau. Dupliceer een activiteit en pas haar in een handomdraai aan voor een andere leerling.",
          bullets: [
            "Je eigen foto's en tekeningen",
            "Je eigen opgenomen stem",
            "Tekstgrootte en lettertypen naar keuze",
            "Duizenden afbeeldingen, ook van emoties en het dagelijks leven",
          ],
        },
        {
          kicker: "Motiveren",
          title: "Positieve bekrachtiging op elke pagina",
          text: "Elk succes wordt gevierd met sterren en geluiden, en na een fout probeert de leerling het gewoon opnieuw. Spellen hebben geen tijdslimiet, zodat iedereen in zijn eigen tempo kan werken.",
          bullets: ["Onbeperkt aantal pogingen", "Sterren en geluiden na elke pagina", "Geen tijdsdruk"],
        },
        {
          kicker: "Voortgang",
          title: "Eén leerling, eigen activiteiten",
          text: "Deel bepaalde projecten met bepaalde leerlingen in een klas en zie hoe het elk van hen afgaat: score, fouten en tijd, pagina per pagina.",
          bullets: ["Deel met maar één of een paar leerlingen", "Resultaten van elk spel", "Werkt op tablets, computers en telefoons"],
        },
      ],
      finalTitle: "Maak de eerste activiteit voor je leerlingen",
      finalSub: "Je begint gratis en je hebt geen technische kennis nodig.",
    },
  },
};

const pl: AudienceText = {
  forTitle: "Make It dla…",
  forSub: "Zobacz, jak Make It sprawdza się w różnych miejscach.",
  schools: "Szkoły",
  special: "Specjalne potrzeby",
  schoolsLine: "Klasy, wyniki i licencje dla całej szkoły.",
  specialLine: "Aktywności dopasowane do każdego ucznia.",
  more: "Dowiedz się więcej",
  pages: {
    schools: {
      title: "Make It dla szkół",
      eyebrow: "Make It dla szkół",
      heroTitle: "Gry edukacyjne dla całej szkoły",
      heroSub: "Nauczyciele w kilka minut tworzą własne gry i quizy, udostępniają je swoim klasom i śledzą postępy każdego ucznia. W przeglądarce, na tabletach i na telefonach.",
      start: "Zacznij za darmo",
      second: "Skontaktuj się z nami",
      tilesTitle: "Dlaczego szkoły wybierają Make It",
      tiles: [
        {
          title: "Do każdego przedmiotu i w każdym wieku",
          text: "Ponad 20 rodzajów aktywności i biblioteki z tysiącami obrazów i dźwięków, gotowe do każdego przedmiotu – od przedszkola po szkołę średnią.",
        },
        {
          title: "Uczniowie bez kont",
          text: "Uczniowie dołączają do klasy za pomocą kodu i swojego imienia. Bez adresów e-mail, bez haseł i bez instalowania czegokolwiek.",
        },
        {
          title: "Wyniki każdego ucznia",
          text: "Zobacz, jak każdy uczeń poradził sobie w każdej grze i każdym quizie: wyniki, błędy, czas i strony, do których doszedł.",
        },
        {
          title: "Dowolne urządzenie",
          text: "Działa w przeglądarce i w aplikacjach na iPada, iPhone'a, Androida i Maca, więc pasuje do urządzeń, które twoja szkoła już ma.",
        },
        {
          title: "Prywatność przede wszystkim",
          text: "Bez reklam i bez śledzenia. Dane klas służą wyłącznie do prowadzenia klas, a jeśli szkoła tego potrzebuje, podpisujemy z nią umowę powierzenia przetwarzania danych.",
        },
        {
          title: "Licencje dla zespołów i szkół",
          text: "Jedna licencja dla wielu nauczycieli, z osobą zarządzającą licencją. Uczniowie zawsze korzystają z Make It za darmo.",
        },
      ],
      sections: [
        {
          kicker: "Klasy",
          title: "Osobna klasa dla każdej grupy",
          text: "Utwórz klasę dla każdej grupy, podaj uczniom jej kod i udostępniaj im projekty: całej klasie albo tylko tym uczniom, którzy ich potrzebują.",
          bullets: [
            "Uczniowie dołączają za pomocą kodu",
            "Udostępniaj całej klasie lub wybranym uczniom",
            "Przenoś uczniów między klasami, gdy zmieniają się grupy",
          ],
        },
        {
          kicker: "Śledzenie postępów",
          title: "Wiedz, jak radzi sobie każdy uczeń",
          text: "Gry zapisują wynik, błędy i czas każdego ucznia, a quizy dodatkowo każdą odpowiedź, ranking na żywo i statystyki każdego pytania.",
          bullets: ["Wyniki każdej gry i każdego quizu", "Odpowiedzi i statystyki dla każdego pytania", "Rankingi, które motywują klasę"],
        },
        {
          kicker: "Twórz i udostępniaj",
          title: "Nauczyciele tworzą własne materiały",
          text: "Każdy nauczyciel w kilka minut dopasowuje aktywności do swoich uczniów – z własnymi tekstami, zdjęciami i głosem. Projekty są zapisywane w chmurze i można je udostępniać innym nauczycielom ze szkoły.",
          bullets: ["Bez programowania", "Kopiuj i dopasowuj projekty koleżanek i kolegów", "Synchronizacja w przeglądarce i w aplikacjach"],
        },
      ],
      finalTitle: "Wprowadź Make It do swojej szkoły",
      finalSub: "Zacznij za darmo z jedną klasą albo napisz do nas, aby uzyskać licencję dla wszystkich swoich nauczycieli.",
    },
    special: {
      title: "Make It w edukacji specjalnej",
      eyebrow: "Make It dla specjalnych potrzeb edukacyjnych",
      heroTitle: "Aktywności dopasowane do każdego ucznia",
      heroSub: "Z Make It nauczyciele, terapeuci i rodziny tworzą gry skrojone na miarę każdej osoby: z jej zdjęciami, znajomym głosem i w jej własnym tempie. Do edukacji specjalnej, terapii logopedycznej, klas integracyjnych i do domu.",
      start: "Zacznij za darmo",
      second: "Wypróbuj demo",
      tilesTitle: "Zaprojektowane, by usuwać bariery",
      tiles: [
        { title: "Bez czytania", text: "Instrukcje i elementy mogą mieć dźwięk: jedno dotknięcie i uczeń słyszy, co ma zrobić." },
        { title: "Błędy się nie liczą", text: "Gry nagradzają każdy sukces, nigdy nie karzą za błędy i pozwalają próbować tyle razy, ile trzeba." },
        {
          title: "Ich własny świat",
          text: "Używaj zdjęć ich własnych rzeczy, miejsc i bliskich osób i nagrywaj swój głos, żeby słyszeli kogoś znajomego.",
        },
        { title: "Czytelny tekst", text: "Wybierz rozmiar każdego tekstu i czcionki takie jak OpenDyslexic czy szkolne pismo odręczne." },
        {
          title: "Spokój, kiedy trzeba",
          text: "Wyłącz efekty dźwiękowe lub muzykę i zdecyduj, czy uczeń może swobodnie przechodzić między stronami.",
        },
        {
          title: "Proste gesty",
          text: "Dotykaj, przeciągaj, łącz i rysuj palcem po śladzie na tablecie: aktywności, które przy okazji ćwiczą motorykę małą.",
        },
      ],
      sections: [
        {
          kicker: "Personalizuj",
          title: "Na miarę każdego ucznia",
          text: "Każdy projekt można dopasować do jednej osoby: jej zainteresowań, słownictwa i poziomu. Zduplikuj aktywność i w chwilę dostosuj ją dla innego ucznia.",
          bullets: [
            "Twoje własne zdjęcia i rysunki",
            "Twój nagrany głos",
            "Rozmiar tekstu i czcionki do wyboru",
            "Tysiące obrazów, w tym emocje i życie codzienne",
          ],
        },
        {
          kicker: "Motywuj",
          title: "Pozytywne wzmocnienie na każdej stronie",
          text: "Każdy sukces jest nagradzany gwiazdkami i dźwiękami, a po błędzie uczeń po prostu próbuje jeszcze raz. W grach nie ma limitów czasu, więc każdy może iść we własnym tempie.",
          bullets: ["Nieograniczona liczba prób", "Gwiazdki i dźwięki po każdej stronie", "Bez presji czasu"],
        },
        {
          kicker: "Śledź postępy",
          title: "Jeden uczeń, jego własne aktywności",
          text: "Udostępniaj konkretne projekty konkretnym uczniom w klasie i sprawdzaj, jak radzi sobie każdy z nich: wynik, błędy i czas, strona po stronie.",
          bullets: ["Udostępniaj jednemu lub kilku uczniom", "Wyniki każdej gry", "Działa na tabletach, komputerach i telefonach"],
        },
      ],
      finalTitle: "Stwórz pierwszą aktywność dla swoich uczniów",
      finalSub: "Start jest bezpłatny i nie potrzebujesz żadnej wiedzy technicznej.",
    },
  },
};

const ro: AudienceText = {
  forTitle: "Make It pentru…",
  forSub: "Vezi cum ajută Make It în fiecare context.",
  schools: "Școli",
  special: "Nevoi speciale",
  schoolsLine: "Clase, rezultate și licențe pentru toată școala.",
  specialLine: "Activități adaptate fiecărui copil.",
  more: "Află mai multe",
  pages: {
    schools: {
      title: "Make It pentru școli",
      eyebrow: "Make It pentru școli",
      heroTitle: "Jocuri educative pentru toată școala",
      heroSub: "Profesorii își creează propriile jocuri și chestionare în câteva minute, le partajează cu clasele lor și urmăresc progresul fiecărui elev. Pe web, pe tablete și pe telefoane.",
      start: "Începe gratuit",
      second: "Contactează-ne",
      tilesTitle: "De ce aleg școlile Make It",
      tiles: [
        {
          title: "Pentru orice materie și vârstă",
          text: "Peste 20 de tipuri de activități și biblioteci cu mii de imagini și sunete, potrivite pentru orice materie, de la grădiniță până la liceu.",
        },
        {
          title: "Elevi fără conturi",
          text: "Elevii intră într-o clasă cu un cod și cu numele lor. Fără adrese de e-mail, fără parole și fără nimic de instalat.",
        },
        {
          title: "Rezultatele fiecărui elev",
          text: "Vezi cum s-a descurcat fiecare elev la fiecare joc și la fiecare chestionar: scoruri, greșeli, timp și paginile la care a ajuns.",
        },
        {
          title: "Orice dispozitiv",
          text: "Funcționează în browser și în aplicațiile pentru iPad, iPhone, Android și Mac, așa că se potrivește cu dispozitivele pe care școala ta le are deja.",
        },
        {
          title: "Confidențialitatea pe primul loc",
          text: "Fără reclame și fără urmărire. Datele claselor sunt folosite doar pentru funcționarea claselor, iar atunci când școala are nevoie, semnăm cu ea un acord de prelucrare a datelor.",
        },
        {
          title: "Licențe pentru echipe și școli",
          text: "O singură licență pentru mai mulți profesori, cu un administrator de licențe. Elevii folosesc mereu Make It gratuit.",
        },
      ],
      sections: [
        {
          kicker: "Clase",
          title: "O clasă pentru fiecare grup",
          text: "Creează o clasă pentru fiecare grup, dă-le elevilor codul ei și partajează proiecte cu ei: cu toată clasa sau doar cu elevii care au nevoie de ele.",
          bullets: ["Elevii intră cu un cod", "Partajează cu toată clasa sau doar cu câțiva", "Mută elevii dintr-o clasă în alta când se schimbă grupurile"],
        },
        {
          kicker: "Urmărire",
          title: "Află cum se descurcă fiecare elev",
          text: "Jocurile înregistrează scorul, greșelile și timpul fiecărui elev, iar chestionarele adaugă fiecare răspuns, un clasament în timp real și statisticile fiecărei întrebări.",
          bullets: [
            "Rezultatele fiecărui joc și ale fiecărui chestionar",
            "Răspunsuri și statistici pentru fiecare întrebare",
            "Clasamente care motivează clasa",
          ],
        },
        {
          kicker: "Creează și partajează",
          title: "Profesorii își fac propriile materiale",
          text: "Fiecare profesor adaptează activitățile pentru elevii săi în câteva minute, cu propriile texte, imagini și voce. Proiectele sunt salvate în cloud și pot fi partajate cu alți profesori din școală.",
          bullets: ["Nu trebuie să știi programare", "Copiază și adaptează proiectele colegilor", "Sincronizat pe web și în aplicații"],
        },
      ],
      finalTitle: "Adu Make It în școala ta",
      finalSub: "Începe gratuit cu o clasă sau scrie-ne ca să obții o licență pentru toți profesorii tăi.",
    },
    special: {
      title: "Make It pentru nevoi speciale",
      eyebrow: "Make It pentru cerințe educaționale speciale",
      heroTitle: "Activități adaptate fiecărui copil",
      heroSub: "Cu Make It, profesorii, terapeuții și familiile creează jocuri pe măsura fiecărei persoane: cu imaginile ei, cu vocea ei, în ritmul ei. Pentru educația specială, logopedie, clase incluzive și acasă.",
      start: "Începe gratuit",
      second: "Încearcă un demo",
      tilesTitle: "Gândit ca să elimine barierele",
      tiles: [
        { title: "Nu trebuie să știe să citească", text: "Instrucțiunile și elementele pot avea sunet: o atingere și copilul aude ce are de făcut." },
        {
          title: "Greșelile nu se penalizează",
          text: "Jocurile răsplătesc fiecare reușită, nu penalizează niciodată greșelile și permit oricâte încercări sunt necesare.",
        },
        {
          title: "Lumea lor",
          text: "Folosește fotografii cu lucrurile, locurile și oamenii lor și înregistrează-ți vocea, ca să audă pe cineva cunoscut.",
        },
        { title: "Text ușor de citit", text: "Alege mărimea fiecărui text și fonturi precum OpenDyslexic sau un font de scris de mână școlar." },
        {
          title: "Liniște când e nevoie",
          text: "Dezactivează efectele sonore sau muzica și decide dacă copilul poate trece liber de la o pagină la alta.",
        },
        { title: "Gesturi simple", text: "Atinge, trage, unește și trasează cu degetul pe tabletă: activități care exersează și motricitatea fină." },
      ],
      sections: [
        {
          kicker: "Personalizează",
          title: "Pe măsura fiecărui copil",
          text: "Fiecare proiect poate fi adaptat unei singure persoane: intereselor, vocabularului și nivelului ei. Duplică o activitate și ajusteaz-o pentru alt copil într-o clipă.",
          bullets: [
            "Propriile fotografii și desene",
            "Propria voce înregistrată",
            "Mărimea textului și fonturi la alegere",
            "Mii de imagini, inclusiv emoții și viața de zi cu zi",
          ],
        },
        {
          kicker: "Motivează",
          title: "Întărire pozitivă pe fiecare pagină",
          text: "Fiecare reușită este sărbătorită cu stele și sunete, iar o greșeală înseamnă doar că copilul mai încearcă o dată. Jocurile nu au limită de timp, așa că fiecare poate merge în ritmul său.",
          bullets: ["Încercări nelimitate", "Stele și sunete după fiecare pagină", "Fără presiunea timpului"],
        },
        {
          kicker: "Urmărește",
          title: "Un copil, activitățile lui",
          text: "Partajează anumite proiecte cu anumiți elevi dintr-o clasă și vezi cum se descurcă fiecare: scor, greșeli și timp, pagină cu pagină.",
          bullets: ["Partajează cu un singur elev sau cu câțiva", "Rezultatele fiecărui joc", "Funcționează pe tablete, calculatoare și telefoane"],
        },
      ],
      finalTitle: "Creează prima activitate pentru copiii tăi",
      finalSub: "Începi gratuit și nu ai nevoie de cunoștințe tehnice.",
    },
  },
};

const sv: AudienceText = {
  forTitle: "Make It för…",
  forSub: "Se hur Make It hjälper till i olika sammanhang.",
  schools: "Skolor",
  special: "Särskilda behov",
  schoolsLine: "Klasser, resultat och licenser för hela skolan.",
  specialLine: "Aktiviteter anpassade efter varje individ.",
  more: "Läs mer",
  pages: {
    schools: {
      title: "Make It för skolor",
      eyebrow: "Make It för skolor",
      heroTitle: "Lärospel för hela skolan",
      heroSub: "Lärarna skapar sina egna spel och test på några minuter, delar dem med sina klasser och följer varje elevs framsteg. På webben, på surfplattor och i mobiler.",
      start: "Kom igång gratis",
      second: "Kontakta oss",
      tilesTitle: "Därför väljer skolor Make It",
      tiles: [
        {
          title: "För alla ämnen och åldrar",
          text: "Fler än 20 typer av aktiviteter och bibliotek med tusentals bilder och ljud, redo för alla ämnen, från förskolan till gymnasiet.",
        },
        {
          title: "Elever utan konton",
          text: "Eleverna går med i en klass med en kod och sitt namn. Inga e-postadresser, inga lösenord och inget att installera.",
        },
        {
          title: "Resultat för varje elev",
          text: "Se hur det gick för varje elev i varje spel och varje test: poäng, fel, tid och hur långt eleven kom.",
        },
        {
          title: "Alla enheter",
          text: "Det fungerar i webbläsaren och i apparna för iPad, iPhone, Android och Mac, så det passar de enheter som din skola redan har.",
        },
        {
          title: "Integriteten först",
          text: "Ingen reklam och ingen spårning. Klassernas data används bara för att klasserna ska fungera, och vi skriver ett personuppgiftsbiträdesavtal med skolan när den behöver det.",
        },
        {
          title: "Licenser för team och skolor",
          text: "En licens för flera lärare, som en licensansvarig hanterar. Eleverna använder alltid Make It gratis.",
        },
      ],
      sections: [
        {
          kicker: "Klasser",
          title: "En klass för varje grupp",
          text: "Skapa en klass för varje grupp, ge eleverna klassens kod och dela projekt med dem: med hela klassen eller bara med de elever som behöver dem.",
          bullets: ["Eleverna går med via en kod", "Dela med hela klassen eller med några få", "Flytta elever mellan klasser när grupperna ändras"],
        },
        {
          kicker: "Uppföljning",
          title: "Vet hur det går för varje elev",
          text: "Spelen registrerar varje elevs poäng, fel och tid, och testen lägger till varje svar, en topplista i realtid och statistiken för varje fråga.",
          bullets: ["Resultat från varje spel och varje test", "Svar och statistik per fråga", "Topplistor som motiverar klassen"],
        },
        {
          kicker: "Skapa och dela",
          title: "Lärarna gör sitt eget material",
          text: "Varje lärare anpassar aktiviteterna efter sina elever på några minuter, med egna texter, bilder och sin egen röst. Projekten sparas i molnet och kan delas med andra lärare på skolan.",
          bullets: ["Ingen programmering behövs", "Kopiera och anpassa kollegornas projekt", "Synkat på webben och i apparna"],
        },
      ],
      finalTitle: "Ta in Make It på din skola",
      finalSub: "Kom igång gratis med en klass, eller skriv till oss för att få en licens för alla dina lärare.",
    },
    special: {
      title: "Make It för särskilda behov",
      eyebrow: "Make It för elever i behov av särskilt stöd",
      heroTitle: "Aktiviteter anpassade efter varje individ",
      heroSub: "Med Make It skapar lärare, terapeuter och familjer spel som är skräddarsydda för varje person: egna bilder, egen röst, eget tempo. För specialundervisning, logopedi, inkluderande klassrum och hemma.",
      start: "Kom igång gratis",
      second: "Prova en demo",
      tilesTitle: "Utformat för att ta bort hinder",
      tiles: [
        { title: "Ingen läsning behövs", text: "Instruktioner och element kan ha ljud: ett tryck, och eleven hör vad som ska göras." },
        { title: "Misstag straffas inte", text: "Spelen belönar varje framgång, bestraffar aldrig misstag och tillåter så många försök som behövs." },
        {
          title: "Deras egen värld",
          text: "Använd foton på deras egna saker, platser och personer, och spela in din röst så att de hör någon de känner.",
        },
        { title: "Lättläst text", text: "Välj storleken på varje text och typsnitt som OpenDyslexic eller ett typsnitt med skolstil." },
        { title: "Lugnt när det behövs", text: "Stäng av ljudeffekterna eller musiken, och bestäm om eleven får röra sig fritt mellan sidorna." },
        { title: "Enkla gester", text: "Tryck, dra, koppla ihop och spåra med fingret på en surfplatta: aktiviteter som också tränar finmotoriken." },
      ],
      sections: [
        {
          kicker: "Anpassa",
          title: "Skräddarsytt för varje elev",
          text: "Varje projekt kan anpassas efter en person: intressen, ordförråd och nivå. Duplicera en aktivitet och justera den för en annan elev på ett ögonblick.",
          bullets: [
            "Dina egna foton och teckningar",
            "Din egen inspelade röst",
            "Valfri textstorlek och valfria typsnitt",
            "Tusentals bilder, bland annat känslor och vardagsliv",
          ],
        },
        {
          kicker: "Motivera",
          title: "Positiv förstärkning på varje sida",
          text: "Varje framgång firas med stjärnor och ljud, och misstag betyder bara att eleven får försöka igen. Spelen har inga tidsgränser, så alla kan gå fram i sin egen takt.",
          bullets: ["Obegränsat antal försök", "Stjärnor och ljud efter varje sida", "Ingen tidspress"],
        },
        {
          kicker: "Följ upp",
          title: "En elev, egna aktiviteter",
          text: "Dela utvalda projekt med utvalda elever i en klass och se hur det går för var och en: poäng, fel och tid, sida för sida.",
          bullets: ["Dela med bara en eller några få elever", "Resultat från varje spel", "Fungerar på surfplattor, datorer och mobiler"],
        },
      ],
      finalTitle: "Skapa den första aktiviteten för dina elever",
      finalSub: "Det är gratis att komma igång och du behöver inga tekniska förkunskaper.",
    },
  },
};

const el: AudienceText = {
  forTitle: "Το Make It για…",
  forSub: "Δες πώς βοηθά το Make It σε κάθε περίπτωση.",
  schools: "Σχολεία",
  special: "Ειδική αγωγή",
  schoolsLine: "Τάξεις, αποτελέσματα και άδειες για όλο το σχολείο.",
  specialLine: "Δραστηριότητες προσαρμοσμένες σε κάθε μαθητή.",
  more: "Μάθε περισσότερα",
  pages: {
    schools: {
      title: "Το Make It για σχολεία",
      eyebrow: "Το Make It για σχολεία",
      heroTitle: "Παιχνίδια μάθησης για όλο το σχολείο",
      heroSub: "Οι εκπαιδευτικοί δημιουργούν τα δικά τους παιχνίδια και κουίζ σε λίγα λεπτά, τα μοιράζονται με τις τάξεις τους και παρακολουθούν την πρόοδο κάθε μαθητή. Στον ιστότοπο, σε τάμπλετ και σε κινητά.",
      start: "Ξεκίνα δωρεάν",
      second: "Επικοινώνησε μαζί μας",
      tilesTitle: "Γιατί τα σχολεία επιλέγουν το Make It",
      tiles: [
        {
          title: "Για κάθε μάθημα και ηλικία",
          text: "Περισσότερα από 20 είδη δραστηριοτήτων και βιβλιοθήκες με χιλιάδες εικόνες και ήχους, έτοιμα για οποιοδήποτε μάθημα, από το νηπιαγωγείο έως τη δευτεροβάθμια εκπαίδευση.",
        },
        {
          title: "Μαθητές χωρίς λογαριασμούς",
          text: "Οι μαθητές μπαίνουν σε μια τάξη με έναν κωδικό και το όνομά τους. Χωρίς διευθύνσεις email, χωρίς κωδικούς πρόσβασης και χωρίς τίποτα να εγκαταστήσουν.",
        },
        {
          title: "Αποτελέσματα κάθε μαθητή",
          text: "Δες πώς τα πήγε κάθε μαθητής σε κάθε παιχνίδι και κάθε κουίζ: βαθμολογία, λάθη, χρόνος και οι σελίδες στις οποίες έφτασε.",
        },
        {
          title: "Οποιαδήποτε συσκευή",
          text: "Λειτουργεί στο πρόγραμμα περιήγησης και στις εφαρμογές για iPad, iPhone, Android και Mac, οπότε ταιριάζει με τις συσκευές που ήδη έχει το σχολείο σου.",
        },
        {
          title: "Πρώτα το απόρρητο",
          text: "Χωρίς διαφημίσεις και χωρίς παρακολούθηση. Τα δεδομένα των τάξεων χρησιμοποιούνται μόνο για τη λειτουργία τους, και υπογράφουμε συμφωνία επεξεργασίας δεδομένων με το σχολείο όταν τη χρειάζεται.",
        },
        {
          title: "Άδειες για ομάδες και σχολεία",
          text: "Μία άδεια για πολλούς εκπαιδευτικούς, με εργαλείο διαχείρισης αδειών. Οι μαθητές χρησιμοποιούν πάντα το Make It δωρεάν.",
        },
      ],
      sections: [
        {
          kicker: "Τάξεις",
          title: "Μια τάξη για κάθε τμήμα",
          text: "Δημιούργησε μια τάξη για κάθε τμήμα, δώσε στους μαθητές τον κωδικό της και μοιράσου έργα μαζί τους: με όλη την τάξη ή μόνο με τους μαθητές που τα χρειάζονται.",
          bullets: [
            "Οι μαθητές μπαίνουν με έναν κωδικό",
            "Μοιράσου με όλη την τάξη ή με μερικούς",
            "Μετακίνησε μαθητές από τάξη σε τάξη όταν αλλάζουν τα τμήματα",
          ],
        },
        {
          kicker: "Παρακολούθηση",
          title: "Μάθε πώς τα πάει κάθε μαθητής",
          text: "Τα παιχνίδια καταγράφουν τη βαθμολογία, τα λάθη και τον χρόνο κάθε μαθητή, και τα κουίζ προσθέτουν κάθε απάντηση, κατάταξη σε πραγματικό χρόνο και τα στατιστικά κάθε ερώτησης.",
          bullets: ["Αποτελέσματα κάθε παιχνιδιού και κάθε κουίζ", "Απαντήσεις και στατιστικά ανά ερώτηση", "Κατατάξεις που δίνουν κίνητρο στην τάξη"],
        },
        {
          kicker: "Δημιουργία και κοινοποίηση",
          title: "Οι εκπαιδευτικοί φτιάχνουν το δικό τους υλικό",
          text: "Κάθε εκπαιδευτικός προσαρμόζει τις δραστηριότητες στους μαθητές του σε λίγα λεπτά, με τα δικά του κείμενα, εικόνες και φωνή. Τα έργα αποθηκεύονται στο νέφος και μπορούν να κοινοποιηθούν σε άλλους εκπαιδευτικούς του σχολείου.",
          bullets: ["Δεν χρειάζεται προγραμματισμός", "Αντίγραψε και προσάρμοσε έργα συναδέλφων", "Συγχρονισμός στον ιστότοπο και στις εφαρμογές"],
        },
      ],
      finalTitle: "Φέρε το Make It στο σχολείο σου",
      finalSub: "Ξεκίνα δωρεάν με μία τάξη ή γράψε μας για να αποκτήσεις άδεια για όλους τους εκπαιδευτικούς του σχολείου σου.",
    },
    special: {
      title: "Το Make It για την ειδική αγωγή",
      eyebrow: "Το Make It για ειδικές εκπαιδευτικές ανάγκες",
      heroTitle: "Δραστηριότητες προσαρμοσμένες σε κάθε μαθητή",
      heroSub: "Με το Make It, εκπαιδευτικοί, θεραπευτές και οικογένειες δημιουργούν παιχνίδια στα μέτρα του καθενός: με τις δικές του εικόνες, τη δική του φωνή, τον δικό του ρυθμό. Για την ειδική αγωγή, τη λογοθεραπεία, τις συμπεριληπτικές τάξεις και το σπίτι.",
      start: "Ξεκίνα δωρεάν",
      second: "Δοκίμασε ένα παράδειγμα",
      tilesTitle: "Σχεδιασμένο για να γκρεμίζει εμπόδια",
      tiles: [
        {
          title: "Χωρίς ανάγκη για ανάγνωση",
          text: "Οι οδηγίες και τα στοιχεία μπορούν να έχουν ήχο: ένα άγγιγμα και ο μαθητής ακούει τι πρέπει να κάνει.",
        },
        {
          title: "Τα λάθη δεν μετράνε εναντίον τους",
          text: "Τα παιχνίδια επιβραβεύουν κάθε επιτυχία, δεν τιμωρούν ποτέ τα λάθη και επιτρέπουν όσες προσπάθειες χρειάζονται.",
        },
        {
          title: "Ο δικός τους κόσμος",
          text: "Χρησιμοποίησε φωτογραφίες από τα δικά τους πράγματα, μέρη και ανθρώπους, και ηχογράφησε τη φωνή σου για να ακούνε κάποιον που γνωρίζουν.",
        },
        {
          title: "Ευανάγνωστο κείμενο",
          text: "Διάλεξε το μέγεθος κάθε κειμένου και γραμματοσειρές όπως η OpenDyslexic ή μια γραμματοσειρά σχολικής γραφής.",
        },
        {
          title: "Ηρεμία όταν χρειάζεται",
          text: "Απενεργοποίησε τα ηχητικά εφέ ή τη μουσική και αποφάσισε αν ο μαθητής μπορεί να μετακινείται ελεύθερα από σελίδα σε σελίδα.",
        },
        {
          title: "Απλές κινήσεις",
          text: "Άγγιξε, σύρε, ένωσε και ιχνηλάτησε με το δάχτυλο σε τάμπλετ: δραστηριότητες που δουλεύουν και τη λεπτή κινητικότητα.",
        },
      ],
      sections: [
        {
          kicker: "Εξατομίκευση",
          title: "Στα μέτρα κάθε μαθητή",
          text: "Κάθε έργο μπορεί να προσαρμοστεί σε ένα άτομο: στα ενδιαφέροντά του, στο λεξιλόγιό του και στο επίπεδό του. Φτιάξε ένα αντίγραφο μιας δραστηριότητας και προσάρμοσέ το για έναν άλλον μαθητή σε μια στιγμή.",
          bullets: [
            "Οι δικές σου φωτογραφίες και ζωγραφιές",
            "Η δική σου ηχογραφημένη φωνή",
            "Μέγεθος κειμένου και γραμματοσειρές της επιλογής σου",
            "Χιλιάδες εικόνες, μεταξύ των οποίων συναισθήματα και καθημερινή ζωή",
          ],
        },
        {
          kicker: "Κίνητρο",
          title: "Θετική ενίσχυση σε κάθε σελίδα",
          text: "Κάθε επιτυχία γιορτάζεται με αστέρια και ήχους, και με τα λάθη ο μαθητής απλώς ξαναδοκιμάζει. Τα παιχνίδια δεν έχουν όριο χρόνου, ώστε ο καθένας να προχωρά με τον δικό του ρυθμό.",
          bullets: ["Απεριόριστες προσπάθειες", "Αστέρια και ήχοι μετά από κάθε σελίδα", "Χωρίς πίεση χρόνου"],
        },
        {
          kicker: "Παρακολούθηση",
          title: "Ένας μαθητής, οι δικές του δραστηριότητες",
          text: "Μοιράσου συγκεκριμένα έργα με συγκεκριμένους μαθητές μιας τάξης και δες πώς τα πάει ο καθένας: βαθμολογία, λάθη και χρόνος, σελίδα προς σελίδα.",
          bullets: ["Μοιράσου μόνο με έναν ή με λίγους μαθητές", "Αποτελέσματα κάθε παιχνιδιού", "Λειτουργεί σε τάμπλετ, υπολογιστές και κινητά"],
        },
      ],
      finalTitle: "Δημιούργησε την πρώτη δραστηριότητα για τους μαθητές σου",
      finalSub: "Ξεκινάς δωρεάν και δεν χρειάζεσαι καμία τεχνική γνώση.",
    },
  },
};

const tr: AudienceText = {
  forTitle: "Make It kimler için?",
  forSub: "Make It'in her ortamda nasıl yardımcı olduğunu görün.",
  schools: "Okullar",
  special: "Özel eğitim",
  schoolsLine: "Tüm okul için sınıflar, sonuçlar ve lisanslar.",
  specialLine: "Her öğrenciye uyarlanmış etkinlikler.",
  more: "Daha fazla bilgi",
  pages: {
    schools: {
      title: "Okullar için Make It",
      eyebrow: "Okullar için Make It",
      heroTitle: "Tüm okul için eğitici oyunlar",
      heroSub: "Öğretmenler birkaç dakikada kendi oyunlarını ve testlerini oluşturur, bunları sınıflarıyla paylaşır ve her öğrencinin ilerlemesini takip eder. Web'de, tabletlerde ve telefonlarda.",
      start: "Ücretsiz başlayın",
      second: "Bize ulaşın",
      tilesTitle: "Okullar neden Make It'i seçiyor?",
      tiles: [
        {
          title: "Her ders ve her yaş için",
          text: "20'den fazla etkinlik türü ve binlerce görsel ve ses içeren kütüphaneler; okul öncesinden ortaöğretime kadar her derse hazır.",
        },
        {
          title: "Öğrencilere hesap gerekmez",
          text: "Öğrenciler bir sınıfa kod ve adlarıyla katılır. E-posta adresi yok, şifre yok, yüklenecek bir şey yok.",
        },
        {
          title: "Her öğrencinin sonuçları",
          text: "Her öğrencinin her oyunda ve her testte nasıl bir sonuç aldığını görün: puanlar, hatalar, süre ve ulaştığı sayfalar.",
        },
        {
          title: "Her cihazda",
          text: "Tarayıcıda ve iPad, iPhone, Android ve Mac uygulamalarında çalışır; böylece okulunuzda zaten bulunan cihazlara uyum sağlar.",
        },
        {
          title: "Önce gizlilik",
          text: "Reklam yok, izleme yok. Sınıf verileri yalnızca sınıfların işleyişi için kullanılır ve okul ihtiyaç duyduğunda okulla bir veri işleme sözleşmesi imzalarız.",
        },
        {
          title: "Ekipler ve okullar için lisanslar",
          text: "Birden fazla öğretmen için tek lisans ve bir lisans yöneticisi. Öğrenciler Make It'i her zaman ücretsiz kullanır.",
        },
      ],
      sections: [
        {
          kicker: "Sınıflar",
          title: "Her grup için bir sınıf",
          text: "Her grup için bir sınıf oluşturun, öğrencilere sınıfın kodunu verin ve projeleri onlarla paylaşın: tüm sınıfla ya da yalnızca ihtiyacı olan öğrencilerle.",
          bullets: [
            "Öğrenciler bir kodla katılır",
            "Tüm sınıfla ya da birkaç öğrenciyle paylaşın",
            "Gruplar değiştiğinde öğrencileri sınıflar arasında taşıyın",
          ],
        },
        {
          kicker: "Takip",
          title: "Her öğrencinin durumunu bilin",
          text: "Oyunlar her öğrencinin puanını, hatalarını ve süresini kaydeder; testler ise buna her cevabı, canlı sıralamayı ve her sorunun istatistiklerini ekler.",
          bullets: ["Her oyunun ve her testin sonuçları", "Soru bazında cevaplar ve istatistikler", "Sınıfı motive eden sıralamalar"],
        },
        {
          kicker: "Oluşturun ve paylaşın",
          title: "Öğretmenler kendi materyallerini hazırlar",
          text: "Her öğretmen, kendi metinleri, resimleri ve sesiyle etkinlikleri birkaç dakikada öğrencilerine göre uyarlar. Projeler bulutta kaydedilir ve okuldaki diğer öğretmenlerle paylaşılabilir.",
          bullets: ["Kodlama bilgisi gerekmez", "Meslektaşlarınızın projelerini kopyalayıp uyarlayın", "Web'de ve uygulamalarda eşitlenir"],
        },
      ],
      finalTitle: "Make It'i okulunuza getirin",
      finalSub: "Tek bir sınıfla ücretsiz başlayın ya da tüm öğretmenleriniz için lisans almak üzere bize yazın.",
    },
    special: {
      title: "Özel eğitim için Make It",
      eyebrow: "Özel eğitim gereksinimleri için Make It",
      heroTitle: "Her öğrenciye uyarlanmış etkinlikler",
      heroSub: "Make It ile öğretmenler, terapistler ve aileler her bireye özel oyunlar oluşturur: kendi resimleriyle, kendi sesiyle, kendi hızında. Özel eğitim, dil ve konuşma terapisi, kapsayıcı sınıflar ve ev için.",
      start: "Ücretsiz başlayın",
      second: "Bir demo deneyin",
      tilesTitle: "Engelleri kaldırmak için tasarlandı",
      tiles: [
        { title: "Okuma gerekmez", text: "Yönergeler ve öğeler sesli olabilir: tek bir dokunuşla öğrenci ne yapacağını duyar." },
        {
          title: "Hatalar aleyhe sayılmaz",
          text: "Oyunlar her başarıyı ödüllendirir, hataları asla cezalandırmaz ve gerektiği kadar denemeye izin verir.",
        },
        {
          title: "Kendi dünyaları",
          text: "Kendi eşyalarının, mekânlarının ve tanıdığı kişilerin fotoğraflarını kullanın; tanıdık birinin sesini duyması için sesinizi kaydedin.",
        },
        {
          title: "Okunaklı metin",
          text: "Her metnin boyutunu seçin; OpenDyslexic gibi yazı tiplerini ya da bir okul el yazısı yazı tipini kullanın.",
        },
        {
          title: "Gerektiğinde sakinlik",
          text: "Ses efektlerini veya müziği kapatın; öğrencinin sayfalar arasında serbestçe geçip geçemeyeceğine siz karar verin.",
        },
        {
          title: "Basit hareketler",
          text: "Tablette parmakla dokunun, sürükleyin, birleştirin ve çizgi üzerinden geçin: ince motor becerilerini de geliştiren etkinlikler.",
        },
      ],
      sections: [
        {
          kicker: "Kişiselleştirin",
          title: "Her öğrenciye özel",
          text: "Her proje tek bir kişiye göre uyarlanabilir: ilgi alanlarına, kelime dağarcığına ve seviyesine. Bir etkinliği çoğaltın ve başka bir öğrenci için hemen uyarlayın.",
          bullets: [
            "Kendi fotoğraflarınız ve çizimleriniz",
            "Kendi kaydettiğiniz sesiniz",
            "Seçilebilir metin boyutu ve yazı tipleri",
            "Duygular ve günlük yaşam dahil binlerce görsel",
          ],
        },
        {
          kicker: "Motive edin",
          title: "Her sayfada olumlu pekiştirme",
          text: "Her başarı yıldızlar ve seslerle kutlanır; hata yapıldığında ise öğrenci yeniden dener. Oyunlarda süre sınırı yoktur, böylece herkes kendi hızında ilerleyebilir.",
          bullets: ["Sınırsız deneme", "Her sayfadan sonra yıldızlar ve sesler", "Zaman baskısı yok"],
        },
        {
          kicker: "Takip edin",
          title: "Bir öğrenci, kendi etkinlikleri",
          text: "Bir sınıftaki belirli öğrencilerle belirli projeleri paylaşın ve her birinin nasıl ilerlediğini görün: puan, hatalar ve süre, sayfa sayfa.",
          bullets: ["Yalnızca bir veya birkaç öğrenciyle paylaşın", "Her oyunun sonuçları", "Tabletlerde, bilgisayarlarda ve telefonlarda çalışır"],
        },
      ],
      finalTitle: "Öğrencileriniz için ilk etkinliği oluşturun",
      finalSub: "Başlamak ücretsiz ve hiçbir teknik bilgi gerekmez.",
    },
  },
};

const ru: AudienceText = {
  forTitle: "Make It для…",
  forSub: "Узнайте, как Make It помогает в разных ситуациях.",
  schools: "Школы",
  special: "Особые потребности",
  schoolsLine: "Классы, результаты и лицензии для всей школы.",
  specialLine: "Задания, адаптированные для каждого ученика.",
  more: "Подробнее",
  pages: {
    schools: {
      title: "Make It для школ",
      eyebrow: "Make It для школ",
      heroTitle: "Обучающие игры для всей школы",
      heroSub: "Учителя за считаные минуты создают собственные игры и викторины, делятся ими со своими классами и следят за успехами каждого ученика. На сайте, на планшетах и на телефонах.",
      start: "Начать бесплатно",
      second: "Связаться с нами",
      tilesTitle: "Почему школы выбирают Make It",
      tiles: [
        {
          title: "Для любого предмета и возраста",
          text: "Более 20 видов заданий и библиотеки с тысячами изображений и звуков — для любого предмета, от детского сада до старших классов.",
        },
        { title: "Ученики без аккаунтов", text: "Ученики входят в класс по коду и своему имени. Без адресов эл. почты, без паролей и без установки." },
        {
          title: "Результаты каждого ученика",
          text: "Смотрите, как каждый ученик справился с каждой игрой и каждой викториной: баллы, ошибки, время и до какой страницы он дошёл.",
        },
        {
          title: "Любое устройство",
          text: "Работает в браузере и в приложениях для iPad, iPhone, Android и Mac, поэтому подходит для устройств, которые уже есть в вашей школе.",
        },
        {
          title: "Конфиденциальность прежде всего",
          text: "Никакой рекламы и никакого отслеживания. Данные классов используются только для работы классов, а при необходимости мы подписываем со школой соглашение об обработке данных.",
        },
        {
          title: "Лицензии для команд и школ",
          text: "Одна лицензия на нескольких учителей с управлением лицензиями. Для учеников Make It всегда бесплатен.",
        },
      ],
      sections: [
        {
          kicker: "Классы",
          title: "Свой класс для каждой группы",
          text: "Создайте класс для каждой группы, дайте ученикам его код и делитесь с ними проектами — со всем классом или только с теми учениками, которым они нужны.",
          bullets: [
            "Ученики входят по коду",
            "Делитесь со всем классом или с несколькими учениками",
            "Переводите учеников в другие классы, когда меняется состав групп",
          ],
        },
        {
          kicker: "Отслеживание",
          title: "Знайте, как успевает каждый ученик",
          text: "Игры сохраняют баллы, ошибки и время каждого ученика, а викторины добавляют все ответы, рейтинг в реальном времени и статистику по каждому вопросу.",
          bullets: ["Результаты каждой игры и каждой викторины", "Ответы и статистика по каждому вопросу", "Рейтинги, которые мотивируют класс"],
        },
        {
          kicker: "Создание и обмен",
          title: "Учителя создают собственные материалы",
          text: "Каждый учитель за считаные минуты адаптирует задания для своих учеников — со своими текстами, картинками и голосом. Проекты хранятся в облаке, и ими можно делиться с другими учителями школы.",
          bullets: ["Программировать не нужно", "Копируйте и адаптируйте проекты коллег", "Синхронизация на сайте и в приложениях"],
        },
      ],
      finalTitle: "Внедрите Make It в своей школе",
      finalSub: "Начните бесплатно с одним классом или напишите нам, чтобы получить лицензию для всех ваших учителей.",
    },
    special: {
      title: "Make It для особых потребностей",
      eyebrow: "Make It для детей с особыми образовательными потребностями",
      heroTitle: "Задания, адаптированные для каждого ученика",
      heroSub: "С Make It учителя, специалисты и семьи создают игры, подобранные под каждого человека: его картинки, его голос, его темп. Для коррекционного обучения, логопедических занятий, инклюзивных классов и дома.",
      start: "Начать бесплатно",
      second: "Попробовать демо",
      tilesTitle: "Создано, чтобы устранять барьеры",
      tiles: [
        { title: "Читать не обязательно", text: "Инструкции и элементы можно озвучить: одно касание — и ученик слышит, что нужно сделать." },
        { title: "Ошибки не в счёт", text: "Игры поощряют каждый успех, никогда не наказывают за ошибки и дают столько попыток, сколько нужно." },
        {
          title: "Свой собственный мир",
          text: "Используйте фотографии их вещей, знакомых мест и близких людей и записывайте свой голос, чтобы они слышали знакомого человека.",
        },
        { title: "Удобный для чтения текст", text: "Выбирайте размер каждого текста и шрифты, например OpenDyslexic или школьный прописной шрифт." },
        {
          title: "Спокойствие, когда нужно",
          text: "Отключайте звуковые эффекты или музыку и решайте, может ли ученик свободно переходить между страницами.",
        },
        {
          title: "Простые жесты",
          text: "Нажимать, перетаскивать, соединять и обводить пальцем на планшете: задания, которые заодно развивают мелкую моторику.",
        },
      ],
      sections: [
        {
          kicker: "Персонализация",
          title: "По мерке для каждого ученика",
          text: "Любой проект можно адаптировать под одного человека: его интересы, словарный запас и уровень. Дублируйте задание и за минуту настройте его для другого ученика.",
          bullets: [
            "Ваши собственные фото и рисунки",
            "Ваш собственный записанный голос",
            "Размер текста и шрифты на выбор",
            "Тысячи изображений, в том числе эмоции и повседневная жизнь",
          ],
        },
        {
          kicker: "Мотивация",
          title: "Положительное подкрепление на каждой странице",
          text: "Каждый успех отмечается звёздами и звуками, а после ошибки ученик просто пробует ещё раз. В играх нет ограничения по времени, поэтому каждый может заниматься в своём темпе.",
          bullets: ["Неограниченное число попыток", "Звёзды и звуки после каждой страницы", "Никакой спешки"],
        },
        {
          kicker: "Отслеживание",
          title: "Каждому ученику — свои задания",
          text: "Делитесь конкретными проектами с конкретными учениками класса и смотрите, как справляется каждый: баллы, ошибки и время, страница за страницей.",
          bullets: ["Делитесь только с одним или несколькими учениками", "Результаты каждой игры", "Работает на планшетах, компьютерах и телефонах"],
        },
      ],
      finalTitle: "Создайте первое задание для своих учеников",
      finalSub: "Начать можно бесплатно, и никаких технических знаний не требуется.",
    },
  },
};

const uk: AudienceText = {
  forTitle: "Make It для…",
  forSub: "Дізнайтеся, як Make It допомагає в різних умовах.",
  schools: "Школи",
  special: "Особливі потреби",
  schoolsLine: "Класи, результати та ліцензії для всієї школи.",
  specialLine: "Завдання, адаптовані до кожного учня.",
  more: "Докладніше",
  pages: {
    schools: {
      title: "Make It для шкіл",
      eyebrow: "Make It для шкіл",
      heroTitle: "Навчальні ігри для всієї школи",
      heroSub: "Учителі за лічені хвилини створюють власні ігри та вікторини, діляться ними зі своїми класами й стежать за прогресом кожного учня. На сайті, на планшетах і на телефонах.",
      start: "Почати безкоштовно",
      second: "Зв’язатися з нами",
      tilesTitle: "Чому школи обирають Make It",
      tiles: [
        {
          title: "Для будь-якого предмета й віку",
          text: "Понад 20 типів завдань і бібліотеки з тисячами зображень і звуків — для будь-якого предмета, від дошкілля до старших класів.",
        },
        {
          title: "Учні без облікових записів",
          text: "Учні приєднуються до класу за кодом і своїм ім’ям. Без електронних адрес, без паролів і без встановлення.",
        },
        {
          title: "Результати кожного учня",
          text: "Дивіться, як кожен учень упорався з кожною грою та вікториною: бали, помилки, час і сторінки, до яких він дійшов.",
        },
        {
          title: "Будь-який пристрій",
          text: "Працює в браузері та в застосунках для iPad, iPhone, Android і Mac, тож підійде для пристроїв, які вже є у вашій школі.",
        },
        {
          title: "Конфіденційність понад усе",
          text: "Без реклами й без стеження. Дані класів використовуються лише для роботи класів, а за потреби ми підписуємо зі школою угоду про обробку даних.",
        },
        {
          title: "Ліцензії для команд і шкіл",
          text: "Одна ліцензія для кількох учителів із зручним керуванням. Для учнів Make It завжди безкоштовний.",
        },
      ],
      sections: [
        {
          kicker: "Класи",
          title: "Окремий клас для кожної групи",
          text: "Створіть клас для кожної групи, дайте учням його код і діліться з ними проєктами: з усім класом або лише з тими учнями, яким це потрібно.",
          bullets: ["Учні приєднуються за кодом", "Діліться з усім класом або з кількома учнями", "Переміщуйте учнів між класами, коли змінюються групи"],
        },
        {
          kicker: "Відстеження",
          title: "Знайте, як справи в кожного учня",
          text: "Ігри фіксують бали, помилки й час кожного учня, а вікторини додають кожну відповідь, рейтинг у реальному часі та статистику кожного запитання.",
          bullets: ["Результати кожної гри та кожної вікторини", "Відповіді та статистика за кожним запитанням", "Рейтинги, що мотивують клас"],
        },
        {
          kicker: "Створення й поширення",
          title: "Учителі створюють власні матеріали",
          text: "Кожен учитель за лічені хвилини адаптує завдання для своїх учнів — із власними текстами, зображеннями та голосом. Проєкти зберігаються в хмарі, і ними можна ділитися з іншими вчителями школи.",
          bullets: ["Програмувати не потрібно", "Копіюйте й адаптуйте проєкти колег", "Синхронізація на сайті та в застосунках"],
        },
      ],
      finalTitle: "Запровадьте Make It у своїй школі",
      finalSub: "Почніть безкоштовно з одного класу або напишіть нам, щоб отримати ліцензію для всіх ваших учителів.",
    },
    special: {
      title: "Make It для особливих потреб",
      eyebrow: "Make It для особливих освітніх потреб",
      heroTitle: "Завдання, адаптовані до кожного учня",
      heroSub: "З Make It учителі, терапевти й родини створюють ігри саме для конкретної дитини: з її фотографіями, знайомим голосом і в її власному темпі. Для спеціальної освіти, логопедичних занять, інклюзивних класів і для дому.",
      start: "Почати безкоштовно",
      second: "Спробувати приклад",
      tilesTitle: "Створено, щоб долати бар’єри",
      tiles: [
        { title: "Читати не обов’язково", text: "Інструкції та елементи можуть мати звук: один дотик — і учень чує, що потрібно зробити." },
        {
          title: "Помилки не караються",
          text: "Ігри винагороджують кожен успіх, ніколи не карають за помилки й дають стільки спроб, скільки потрібно.",
        },
        {
          title: "Їхній власний світ",
          text: "Використовуйте фото їхніх речей, місць і людей та записуйте свій голос, щоб учні чули когось знайомого.",
        },
        { title: "Зручний для читання текст", text: "Вибирайте розмір кожного тексту та шрифти, як-от OpenDyslexic або шкільний рукописний шрифт." },
        { title: "Спокій, коли потрібно", text: "Вимикайте звукові ефекти чи музику та вирішуйте, чи може учень вільно переходити між сторінками." },
        {
          title: "Прості жести",
          text: "Торкайтеся, перетягуйте, з’єднуйте й обводьте пальцем на планшеті: завдання, які водночас розвивають дрібну моторику.",
        },
      ],
      sections: [
        {
          kicker: "Персоналізація",
          title: "На мірку кожного учня",
          text: "Кожен проєкт можна адаптувати до однієї людини: її інтересів, словникового запасу й рівня. Продублюйте завдання й за мить налаштуйте його для іншого учня.",
          bullets: [
            "Ваші власні фото й малюнки",
            "Ваш власний записаний голос",
            "Розмір тексту й шрифти на вибір",
            "Тисячі зображень, зокрема про емоції та повсякдення",
          ],
        },
        {
          kicker: "Мотивація",
          title: "Позитивне підкріплення на кожній сторінці",
          text: "Кожен успіх супроводжують зірки та звуки, а після помилки учень просто пробує ще раз. В іграх немає обмежень часу, тож кожен може рухатися у власному темпі.",
          bullets: ["Необмежена кількість спроб", "Зірки та звуки після кожної сторінки", "Без поспіху й тиску часу"],
        },
        {
          kicker: "Відстеження",
          title: "Кожному учневі — власні завдання",
          text: "Діліться окремими проєктами з окремими учнями класу й дивіться, як справляється кожен: бали, помилки й час, сторінка за сторінкою.",
          bullets: ["Діліться лише з одним або кількома учнями", "Результати кожної гри", "Працює на планшетах, комп’ютерах і телефонах"],
        },
      ],
      finalTitle: "Створіть перше завдання для своїх учнів",
      finalSub: "Почати можна безкоштовно, і жодних технічних знань не потрібно.",
    },
  },
};

const id: AudienceText = {
  forTitle: "Make It untuk…",
  forSub: "Lihat bagaimana Make It membantu dalam setiap konteks.",
  schools: "Sekolah",
  special: "Kebutuhan khusus",
  schoolsLine: "Kelas, hasil, dan lisensi untuk seluruh sekolah.",
  specialLine: "Aktivitas yang disesuaikan untuk setiap peserta didik.",
  more: "Selengkapnya",
  pages: {
    schools: {
      title: "Make It untuk sekolah",
      eyebrow: "Make It untuk sekolah",
      heroTitle: "Permainan edukatif untuk seluruh sekolah",
      heroSub: "Guru membuat permainan dan kuis mereka sendiri dalam hitungan menit, membagikannya ke kelas mereka, dan memantau perkembangan setiap siswa. Di web, tablet, dan ponsel.",
      start: "Mulai gratis",
      second: "Hubungi kami",
      tilesTitle: "Mengapa sekolah memilih Make It",
      tiles: [
        {
          title: "Untuk setiap mata pelajaran dan usia",
          text: "Lebih dari 20 jenis aktivitas dan pustaka berisi ribuan gambar dan suara, siap untuk mata pelajaran apa pun, dari PAUD hingga sekolah menengah.",
        },
        {
          title: "Siswa tanpa akun",
          text: "Siswa bergabung ke kelas dengan kode dan nama mereka. Tanpa alamat email, tanpa kata sandi, dan tanpa perlu memasang apa pun.",
        },
        {
          title: "Hasil setiap siswa",
          text: "Lihat capaian setiap siswa di setiap permainan dan setiap kuis: skor, kesalahan, waktu, dan halaman yang mereka capai.",
        },
        {
          title: "Perangkat apa pun",
          text: "Berjalan di browser dan di aplikasi untuk iPad, iPhone, Android, dan Mac, sehingga cocok dengan perangkat yang sudah dimiliki sekolah Anda.",
        },
        {
          title: "Privasi adalah yang utama",
          text: "Tanpa iklan dan tanpa pelacakan. Data kelas hanya digunakan untuk menjalankan kelas, dan kami menandatangani perjanjian pemrosesan data dengan sekolah bila diperlukan.",
        },
        {
          title: "Lisensi untuk tim dan sekolah",
          text: "Satu lisensi untuk beberapa guru, dengan pengelola lisensi. Siswa selalu menggunakan Make It secara gratis.",
        },
      ],
      sections: [
        {
          kicker: "Kelas",
          title: "Satu kelas untuk setiap kelompok",
          text: "Buat kelas untuk setiap kelompok, berikan kodenya kepada siswa, dan bagikan proyek kepada mereka: kepada seluruh kelas, atau hanya kepada siswa yang membutuhkannya.",
          bullets: ["Siswa bergabung dengan kode", "Bagikan kepada seluruh kelas atau beberapa siswa", "Pindahkan siswa antarkelas saat kelompok berubah"],
        },
        {
          kicker: "Pemantauan",
          title: "Ketahui perkembangan setiap siswa",
          text: "Permainan mencatat skor, kesalahan, dan waktu setiap siswa, sedangkan kuis juga mencatat setiap jawaban, peringkat langsung, dan statistik setiap pertanyaan.",
          bullets: ["Hasil setiap permainan dan setiap kuis", "Jawaban dan statistik per pertanyaan", "Peringkat yang memotivasi kelas"],
        },
        {
          kicker: "Buat dan bagikan",
          title: "Guru membuat materi mereka sendiri",
          text: "Setiap guru menyesuaikan aktivitas dengan siswanya dalam hitungan menit, menggunakan teks, gambar, dan suara mereka sendiri. Proyek tersimpan di cloud dan dapat dibagikan kepada guru lain di sekolah.",
          bullets: ["Tidak perlu pemrograman", "Salin dan sesuaikan proyek dari rekan guru", "Tersinkron di web dan di aplikasi"],
        },
      ],
      finalTitle: "Bawa Make It ke sekolah Anda",
      finalSub: "Mulai gratis dengan satu kelas, atau hubungi kami untuk mendapatkan lisensi bagi semua guru Anda.",
    },
    special: {
      title: "Make It untuk kebutuhan khusus",
      eyebrow: "Make It untuk kebutuhan pendidikan khusus",
      heroTitle: "Aktivitas yang disesuaikan untuk setiap peserta didik",
      heroSub: "Dengan Make It, guru, terapis, dan keluarga membuat permainan yang disesuaikan untuk setiap individu: gambarnya, suaranya, dan kecepatannya sendiri. Untuk pendidikan khusus, terapi wicara, kelas inklusif, dan di rumah.",
      start: "Mulai gratis",
      second: "Coba demo",
      tilesTitle: "Dirancang untuk menghilangkan hambatan",
      tiles: [
        {
          title: "Tidak perlu membaca",
          text: "Instruksi dan elemen dapat diberi suara: cukup satu ketukan, dan peserta didik mendengar apa yang harus dilakukan.",
        },
        {
          title: "Kesalahan tidak merugikan mereka",
          text: "Permainan menghargai setiap keberhasilan, tidak pernah menghukum kesalahan, dan memberikan percobaan sebanyak yang diperlukan.",
        },
        {
          title: "Dunia mereka sendiri",
          text: "Gunakan foto benda, tempat, dan orang-orang di sekitar mereka, dan rekam suara Anda agar mereka mendengar suara orang yang mereka kenal.",
        },
        { title: "Teks yang mudah dibaca", text: "Pilih ukuran setiap teks dan font seperti OpenDyslexic atau font tulisan tangan sekolah." },
        {
          title: "Tenang saat diperlukan",
          text: "Matikan efek suara atau musik, dan tentukan apakah peserta didik dapat berpindah halaman dengan bebas.",
        },
        {
          title: "Gerakan sederhana",
          text: "Ketuk, seret, hubungkan, dan tebalkan garis dengan jari di tablet: aktivitas yang sekaligus melatih motorik halus.",
        },
      ],
      sections: [
        {
          kicker: "Personalisasi",
          title: "Dibuat khusus untuk setiap peserta didik",
          text: "Setiap proyek dapat disesuaikan untuk satu orang: minatnya, kosakatanya, dan tingkat kemampuannya. Duplikasikan aktivitas dan sesuaikan untuk peserta didik lain dalam sekejap.",
          bullets: [
            "Foto dan gambar Anda sendiri",
            "Rekaman suara Anda sendiri",
            "Ukuran teks dan font yang bisa dipilih",
            "Ribuan gambar, termasuk emosi dan kehidupan sehari-hari",
          ],
        },
        {
          kicker: "Motivasi",
          title: "Penguatan positif di setiap halaman",
          text: "Setiap keberhasilan dirayakan dengan bintang dan suara, dan kesalahan hanya membuat peserta didik mencoba lagi. Tidak ada batas waktu dalam permainan, sehingga setiap orang dapat belajar sesuai kecepatannya sendiri.",
          bullets: ["Percobaan tanpa batas", "Bintang dan suara setelah setiap halaman", "Tanpa tekanan waktu"],
        },
        {
          kicker: "Pemantauan",
          title: "Satu peserta didik, aktivitasnya sendiri",
          text: "Bagikan proyek tertentu kepada siswa tertentu di dalam kelas dan lihat capaian masing-masing: skor, kesalahan, dan waktu, halaman demi halaman.",
          bullets: ["Bagikan hanya kepada satu atau beberapa siswa", "Hasil setiap permainan", "Berfungsi di tablet, komputer, dan ponsel"],
        },
      ],
      finalTitle: "Buat aktivitas pertama untuk peserta didik Anda",
      finalSub: "Gratis untuk memulai dan Anda tidak memerlukan pengetahuan teknis apa pun.",
    },
  },
};

const vi: AudienceText = {
  forTitle: "Make It dành cho…",
  forSub: "Xem Make It hỗ trợ thế nào trong từng môi trường.",
  schools: "Trường học",
  special: "Nhu cầu đặc biệt",
  schoolsLine: "Lớp học, kết quả và giấy phép cho cả trường.",
  specialLine: "Hoạt động phù hợp với từng người học.",
  more: "Tìm hiểu thêm",
  pages: {
    schools: {
      title: "Make It cho trường học",
      eyebrow: "Make It cho trường học",
      heroTitle: "Trò chơi học tập cho cả trường",
      heroSub: "Giáo viên tự tạo trò chơi và câu đố chỉ trong vài phút, chia sẻ với lớp học của mình và theo dõi tiến độ của từng học sinh. Trên web, máy tính bảng và điện thoại.",
      start: "Bắt đầu miễn phí",
      second: "Liên hệ với chúng tôi",
      tilesTitle: "Vì sao các trường chọn Make It",
      tiles: [
        {
          title: "Cho mọi môn học và lứa tuổi",
          text: "Hơn 20 loại hoạt động cùng thư viện với hàng nghìn hình ảnh và âm thanh, sẵn sàng cho mọi môn học, từ mầm non đến trung học.",
        },
        {
          title: "Học sinh không cần tài khoản",
          text: "Học sinh tham gia lớp học bằng mã và tên của mình. Không cần địa chỉ email, không cần mật khẩu và không phải cài đặt gì.",
        },
        {
          title: "Kết quả của từng học sinh",
          text: "Xem từng học sinh làm bài thế nào trong mỗi trò chơi và mỗi câu đố: điểm, lỗi sai, thời gian và trang các em đã đến.",
        },
        {
          title: "Mọi thiết bị",
          text: "Chạy trên trình duyệt và trong ứng dụng cho iPad, iPhone, Android và Mac, nên phù hợp với các thiết bị trường bạn đã có.",
        },
        {
          title: "Quyền riêng tư là trên hết",
          text: "Không quảng cáo, không theo dõi. Dữ liệu lớp học chỉ được dùng để vận hành lớp học, và chúng tôi ký thỏa thuận xử lý dữ liệu với nhà trường khi cần.",
        },
        {
          title: "Giấy phép cho nhóm và trường học",
          text: "Một giấy phép cho nhiều giáo viên, kèm người quản lý giấy phép. Học sinh luôn dùng Make It miễn phí.",
        },
      ],
      sections: [
        {
          kicker: "Lớp học",
          title: "Mỗi nhóm một lớp học",
          text: "Tạo một lớp học cho mỗi nhóm, đưa mã cho học sinh và chia sẻ dự án với các em: với cả lớp, hoặc chỉ với những em cần.",
          bullets: ["Học sinh tham gia bằng mã", "Chia sẻ với cả lớp hoặc với một vài em", "Chuyển học sinh giữa các lớp học khi nhóm thay đổi"],
        },
        {
          kicker: "Theo dõi",
          title: "Nắm rõ tình hình của từng học sinh",
          text: "Trò chơi ghi lại điểm, lỗi sai và thời gian của từng học sinh; câu đố còn ghi thêm mọi câu trả lời, bảng xếp hạng trực tiếp và thống kê của từng câu hỏi.",
          bullets: ["Kết quả của mọi trò chơi và mọi câu đố", "Câu trả lời và thống kê theo từng câu hỏi", "Bảng xếp hạng tạo động lực cho cả lớp"],
        },
        {
          kicker: "Tạo và chia sẻ",
          title: "Giáo viên tự làm học liệu của mình",
          text: "Mỗi giáo viên điều chỉnh hoạt động cho học sinh của mình chỉ trong vài phút, với văn bản, hình ảnh và giọng nói của riêng mình. Dự án được lưu trên đám mây và có thể chia sẻ với các giáo viên khác trong trường.",
          bullets: ["Không cần biết lập trình", "Sao chép và điều chỉnh dự án của đồng nghiệp", "Đồng bộ trên web và trong ứng dụng"],
        },
      ],
      finalTitle: "Đưa Make It đến trường của bạn",
      finalSub: "Bắt đầu miễn phí với một lớp học, hoặc viết cho chúng tôi để có giấy phép cho tất cả giáo viên của bạn.",
    },
    special: {
      title: "Make It cho nhu cầu đặc biệt",
      eyebrow: "Make It cho nhu cầu giáo dục đặc biệt",
      heroTitle: "Hoạt động phù hợp với từng người học",
      heroSub: "Với Make It, giáo viên, nhà trị liệu và gia đình tạo ra những trò chơi phù hợp với từng người: hình ảnh, giọng nói và nhịp độ của riêng mỗi người. Dành cho giáo dục đặc biệt, trị liệu ngôn ngữ, lớp học hòa nhập và tại nhà.",
      start: "Bắt đầu miễn phí",
      second: "Chơi thử bản mẫu",
      tilesTitle: "Được thiết kế để xóa bỏ rào cản",
      tiles: [
        {
          title: "Không cần biết đọc",
          text: "Hướng dẫn và các thành phần có thể kèm âm thanh: chỉ một lần chạm là người học nghe được việc cần làm.",
        },
        {
          title: "Lỗi sai không bị tính",
          text: "Trò chơi khen thưởng mỗi lần làm đúng, không bao giờ phạt lỗi sai và cho phép thử bao nhiêu lần cũng được.",
        },
        {
          title: "Thế giới của riêng các em",
          text: "Dùng ảnh chụp đồ vật, nơi chốn và những người thân quen của các em, và ghi âm giọng nói của bạn để các em được nghe một người quen thuộc.",
        },
        {
          title: "Chữ dễ đọc",
          text: "Chọn cỡ chữ cho từng đoạn văn bản và các phông chữ như OpenDyslexic hoặc phông chữ viết tay dùng trong trường học.",
        },
        { title: "Yên tĩnh khi cần", text: "Tắt hiệu ứng âm thanh hoặc nhạc, và quyết định người học có được tự do chuyển giữa các trang hay không." },
        {
          title: "Thao tác đơn giản",
          text: "Chạm, kéo, nối và đồ theo bằng ngón tay trên máy tính bảng: các hoạt động cũng giúp rèn luyện kỹ năng vận động tinh.",
        },
      ],
      sections: [
        {
          kicker: "Cá nhân hóa",
          title: "Thiết kế riêng cho từng người học",
          text: "Mỗi dự án đều có thể điều chỉnh cho một người: sở thích, vốn từ và trình độ của người đó. Nhân bản một hoạt động và điều chỉnh cho người học khác chỉ trong chốc lát.",
          bullets: [
            "Ảnh và hình vẽ của riêng bạn",
            "Giọng nói do chính bạn ghi âm",
            "Tùy chọn cỡ chữ và phông chữ",
            "Hàng nghìn hình ảnh, bao gồm cảm xúc và đời sống hằng ngày",
          ],
        },
        {
          kicker: "Tạo động lực",
          title: "Khích lệ tích cực ở mỗi trang",
          text: "Mỗi lần làm đúng đều được chúc mừng bằng ngôi sao và âm thanh, còn khi sai thì người học chỉ cần thử lại. Trò chơi không giới hạn thời gian, để ai cũng có thể học theo nhịp độ của mình.",
          bullets: ["Thử không giới hạn số lần", "Ngôi sao và âm thanh sau mỗi trang", "Không áp lực thời gian"],
        },
        {
          kicker: "Theo dõi",
          title: "Mỗi người học, những hoạt động riêng",
          text: "Chia sẻ những dự án cụ thể với từng học sinh cụ thể trong lớp học và xem kết quả của từng em: điểm, lỗi sai và thời gian, theo từng trang.",
          bullets: ["Chia sẻ với chỉ một hoặc vài học sinh", "Kết quả của mọi trò chơi", "Chạy trên máy tính bảng, máy tính và điện thoại"],
        },
      ],
      finalTitle: "Tạo hoạt động đầu tiên cho người học của bạn",
      finalSub: "Bắt đầu miễn phí và bạn không cần kiến thức kỹ thuật nào.",
    },
  },
};

const cs: AudienceText = {
  forTitle: "Make It pro…",
  forSub: "Podívejte se, jak Make It pomáhá v různých prostředích.",
  schools: "Školy",
  special: "Speciální potřeby",
  schoolsLine: "Třídy, výsledky a licence pro celou školu.",
  specialLine: "Aktivity přizpůsobené každému žákovi.",
  more: "Zjistit více",
  pages: {
    schools: {
      title: "Make It pro školy",
      eyebrow: "Make It pro školy",
      heroTitle: "Výukové hry pro celou školu",
      heroSub: "Učitelé si během pár minut vytvoří vlastní hry a kvízy, sdílejí je se svými třídami a sledují pokrok každého žáka. Na webu, na tabletech i na telefonech.",
      start: "Začít zdarma",
      second: "Kontaktujte nás",
      tilesTitle: "Proč si školy vybírají Make It",
      tiles: [
        {
          title: "Pro každý předmět a věk",
          text: "Více než 20 typů aktivit a knihovny s tisíci obrázků a zvuků, připravené pro jakýkoli předmět, od mateřské školy až po střední.",
        },
        {
          title: "Žáci bez účtů",
          text: "Žáci se ke třídě připojí pomocí kódu a svého jména. Žádné e-mailové adresy, žádná hesla a nic se neinstaluje.",
        },
        {
          title: "Výsledky každého žáka",
          text: "Podívejte se, jak si každý žák vedl v každé hře a každém kvízu: skóre, chyby, čas a stránky, na které se dostal.",
        },
        {
          title: "Jakékoli zařízení",
          text: "Funguje v prohlížeči i v aplikacích pro iPad, iPhone, Android a Mac, takže se hodí k zařízením, která vaše škola už má.",
        },
        {
          title: "Soukromí na prvním místě",
          text: "Žádná reklama a žádné sledování. Data tříd se používají jen k provozu tříd, a pokud to škola potřebuje, podepíšeme s ní smlouvu o zpracování osobních údajů.",
        },
        { title: "Licence pro týmy a školy", text: "Jedna licence pro více učitelů, se správcem licence. Žáci používají Make It vždy zdarma." },
      ],
      sections: [
        {
          kicker: "Třídy",
          title: "Třída pro každou skupinu",
          text: "Vytvořte třídu pro každou skupinu, dejte žákům její kód a sdílejte s nimi projekty: s celou třídou, nebo jen se žáky, kteří je potřebují.",
          bullets: ["Žáci se připojí pomocí kódu", "Sdílení s celou třídou, nebo jen s některými", "Přesouvání žáků mezi třídami, když se skupiny změní"],
        },
        {
          kicker: "Sledování pokroku",
          title: "Vězte, jak se daří každému žákovi",
          text: "Hry zaznamenávají skóre, chyby a čas každého žáka a kvízy k tomu přidávají každou odpověď, žebříček v reálném čase a statistiky každé otázky.",
          bullets: ["Výsledky každé hry a každého kvízu", "Odpovědi a statistiky po jednotlivých otázkách", "Žebříčky, které motivují třídu"],
        },
        {
          kicker: "Tvorba a sdílení",
          title: "Učitelé si tvoří vlastní materiály",
          text: "Každý učitel během pár minut přizpůsobí aktivity svým žákům, s vlastními texty, obrázky a hlasem. Projekty se ukládají do cloudu a lze je sdílet s ostatními učiteli školy.",
          bullets: ["Bez programování", "Kopírujte a upravujte projekty kolegů", "Synchronizace na webu i v aplikacích"],
        },
      ],
      finalTitle: "Přineste Make It do své školy",
      finalSub: "Začněte zdarma s jednou třídou, nebo nám napište a získejte licenci pro všechny své učitele.",
    },
    special: {
      title: "Make It pro speciální potřeby",
      eyebrow: "Make It pro speciální vzdělávací potřeby",
      heroTitle: "Aktivity přizpůsobené každému žákovi",
      heroSub: "S Make It učitelé, terapeuti a rodiny vytvářejí hry na míru každému člověku: jeho obrázky, jeho hlas, jeho tempo. Pro speciální vzdělávání, logopedii, inkluzivní třídy i domácí prostředí.",
      start: "Začít zdarma",
      second: "Vyzkoušet ukázku",
      tilesTitle: "Navrženo tak, aby odstraňovalo bariéry",
      tiles: [
        { title: "Není potřeba číst", text: "Pokyny i prvky mohou mít zvuk: stačí klepnout a žák uslyší, co má dělat." },
        { title: "Chyby se nepočítají", text: "Hry odměňují každý úspěch, nikdy netrestají chyby a umožňují tolik pokusů, kolik je potřeba." },
        {
          title: "Jejich vlastní svět",
          text: "Použijte fotky jejich vlastních věcí, míst a lidí a nahrajte svůj hlas, aby slyšeli někoho, koho znají.",
        },
        { title: "Čitelný text", text: "Zvolte velikost každého textu a písma jako OpenDyslexic nebo školní psací písmo." },
        { title: "Klid, když je potřeba", text: "Vypněte zvukové efekty nebo hudbu a rozhodněte, zda se žák může mezi stránkami pohybovat volně." },
        {
          title: "Jednoduchá gesta",
          text: "Klepnout, přetáhnout, spojit a obtáhnout prstem na tabletu: aktivity, které zároveň rozvíjejí jemnou motoriku.",
        },
      ],
      sections: [
        {
          kicker: "Přizpůsobení",
          title: "Na míru každému žákovi",
          text: "Každý projekt lze přizpůsobit jednomu člověku: jeho zájmům, slovní zásobě a úrovni. Duplikujte aktivitu a během chvilky ji upravte pro dalšího žáka.",
          bullets: ["Vlastní fotky a kresby", "Vlastní nahraný hlas", "Volitelná velikost textu a písma", "Tisíce obrázků, včetně emocí a každodenního života"],
        },
        {
          kicker: "Motivace",
          title: "Pozitivní motivace na každé stránce",
          text: "Každý úspěch oslaví hvězdičky a zvuky a po chybě to žák prostě zkusí znovu. Hry nemají časové limity, takže každý může postupovat svým tempem.",
          bullets: ["Neomezený počet pokusů", "Hvězdičky a zvuky po každé stránce", "Žádný časový tlak"],
        },
        {
          kicker: "Sledování",
          title: "Jeden žák, vlastní aktivity",
          text: "Sdílejte konkrétní projekty s konkrétními žáky ve třídě a sledujte, jak se každému z nich daří: skóre, chyby a čas, stránku po stránce.",
          bullets: ["Sdílení jen s jedním nebo několika žáky", "Výsledky každé hry", "Funguje na tabletech, počítačích i telefonech"],
        },
      ],
      finalTitle: "Vytvořte první aktivitu pro své žáky",
      finalSub: "Začátek je zdarma a nepotřebujete žádné technické znalosti.",
    },
  },
};

const sk: AudienceText = {
  forTitle: "Make It pre…",
  forSub: "Pozrite sa, ako Make It pomáha v rôznych prostrediach.",
  schools: "Školy",
  special: "Špeciálne potreby",
  schoolsLine: "Triedy, výsledky a licencie pre celú školu.",
  specialLine: "Aktivity prispôsobené každému žiakovi.",
  more: "Zistiť viac",
  pages: {
    schools: {
      title: "Make It pre školy",
      eyebrow: "Make It pre školy",
      heroTitle: "Vzdelávacie hry pre celú školu",
      heroSub: "Učitelia si za pár minút vytvoria vlastné hry a kvízy, zdieľajú ich so svojimi triedami a sledujú pokrok každého žiaka. Na webe, na tabletoch aj v mobiloch.",
      start: "Začať zadarmo",
      second: "Kontaktujte nás",
      tilesTitle: "Prečo si školy vyberajú Make It",
      tiles: [
        {
          title: "Pre každý predmet a vek",
          text: "Viac ako 20 typov aktivít a knižnice s tisíckami obrázkov a zvukov, pripravené pre akýkoľvek predmet, od materskej školy až po strednú.",
        },
        {
          title: "Žiaci bez účtov",
          text: "Žiaci sa pripoja k triede pomocou kódu a svojho mena. Žiadne e-mailové adresy, žiadne heslá a nič na inštaláciu.",
        },
        {
          title: "Výsledky každého žiaka",
          text: "Pozrite sa, ako sa každému žiakovi darilo v každej hre a každom kvíze: skóre, chyby, čas a strany, na ktoré sa dostal.",
        },
        {
          title: "Akékoľvek zariadenie",
          text: "Funguje v prehliadači a v aplikáciách pre iPad, iPhone, Android a Mac, takže sa hodí k zariadeniam, ktoré vaša škola už má.",
        },
        {
          title: "Súkromie na prvom mieste",
          text: "Žiadna reklama a žiadne sledovanie. Údaje tried sa používajú len na fungovanie tried a ak to škola potrebuje, podpíšeme s ňou zmluvu o spracúvaní osobných údajov.",
        },
        {
          title: "Licencie pre tímy a školy",
          text: "Jedna licencia pre viacerých učiteľov so správcom licencie. Žiaci používajú Make It vždy zadarmo.",
        },
      ],
      sections: [
        {
          kicker: "Triedy",
          title: "Trieda pre každú skupinu",
          text: "Vytvorte triedu pre každú skupinu, dajte žiakom jej kód a zdieľajte s nimi projekty: s celou triedou alebo len so žiakmi, ktorí ich potrebujú.",
          bullets: [
            "Žiaci sa pripoja pomocou kódu",
            "Zdieľanie s celou triedou alebo len s niekoľkými žiakmi",
            "Presúvanie žiakov medzi triedami, keď sa skupiny zmenia",
          ],
        },
        {
          kicker: "Sledovanie",
          title: "Majte prehľad o každom žiakovi",
          text: "Hry zaznamenávajú skóre, chyby a čas každého žiaka a kvízy k tomu pridávajú každú odpoveď, rebríček v reálnom čase a štatistiky každej otázky.",
          bullets: ["Výsledky každej hry a každého kvízu", "Odpovede a štatistiky pre každú otázku", "Rebríčky, ktoré motivujú triedu"],
        },
        {
          kicker: "Tvorba a zdieľanie",
          title: "Učitelia si vytvárajú vlastné materiály",
          text: "Každý učiteľ za pár minút prispôsobí aktivity svojim žiakom – vlastnými textami, obrázkami a hlasom. Projekty sa ukladajú v cloude a dajú sa zdieľať s ďalšími učiteľmi školy.",
          bullets: ["Bez programovania", "Kopírovanie a úprava projektov od kolegov", "Synchronizácia na webe aj v aplikáciách"],
        },
      ],
      finalTitle: "Prineste Make It do svojej školy",
      finalSub: "Začnite zadarmo s jednou triedou alebo nám napíšte a získajte licenciu pre všetkých svojich učiteľov.",
    },
    special: {
      title: "Make It pre špeciálne potreby",
      eyebrow: "Make It pre špeciálne výchovno-vzdelávacie potreby",
      heroTitle: "Aktivity prispôsobené každému žiakovi",
      heroSub: "S Make It vytvárajú učitelia, terapeuti a rodiny hry šité na mieru každému človeku – s jeho obrázkami, jeho hlasom a v jeho tempe. Pre špeciálne vzdelávanie, logopédiu, inkluzívne triedy aj na doma.",
      start: "Začať zadarmo",
      second: "Vyskúšať ukážku",
      tilesTitle: "Navrhnuté tak, aby odstraňovalo bariéry",
      tiles: [
        { title: "Netreba vedieť čítať", text: "Pokyny aj prvky môžu mať zvuk: stačí ťuknúť a žiak počuje, čo má robiť." },
        { title: "Chyby sa nerátajú", text: "Hry odmeňujú každý úspech, nikdy netrestajú chyby a umožňujú toľko pokusov, koľko treba." },
        { title: "Ich vlastný svet", text: "Použite fotky ich vlastných vecí, miest a ľudí a nahrajte svoj hlas, aby počuli niekoho, koho poznajú." },
        { title: "Čitateľný text", text: "Vyberte veľkosť každého textu a písma ako OpenDyslexic alebo školské písané písmo." },
        { title: "Pokoj, keď je potrebný", text: "Vypnite zvukové efekty alebo hudbu a rozhodnite, či sa žiak môže medzi stranami pohybovať voľne." },
        {
          title: "Jednoduché gestá",
          text: "Ťukanie, presúvanie, spájanie a obťahovanie prstom na tablete: aktivity, ktoré rozvíjajú aj jemnú motoriku.",
        },
      ],
      sections: [
        {
          kicker: "Prispôsobenie",
          title: "Na mieru každému žiakovi",
          text: "Každý projekt sa dá prispôsobiť jednému človeku: jeho záujmom, slovnej zásobe a úrovni. Duplikujte aktivitu a v okamihu ju upravte pre iného žiaka.",
          bullets: [
            "Vlastné fotky a kresby",
            "Vlastný nahraný hlas",
            "Veľkosť textu a písma podľa výberu",
            "Tisíce obrázkov vrátane emócií a každodenného života",
          ],
        },
        {
          kicker: "Motivácia",
          title: "Pozitívna motivácia na každej strane",
          text: "Každý úspech sa oslávi hviezdičkami a zvukmi a po chybe môže žiak jednoducho skúsiť znova. Hry nemajú časové limity, takže každý môže postupovať vlastným tempom.",
          bullets: ["Neobmedzený počet pokusov", "Hviezdičky a zvuky po každej strane", "Bez časového tlaku"],
        },
        {
          kicker: "Sledovanie",
          title: "Jeden žiak, vlastné aktivity",
          text: "Zdieľajte konkrétne projekty s konkrétnymi žiakmi v triede a sledujte, ako sa darí každému z nich: skóre, chyby a čas, strana po strane.",
          bullets: ["Zdieľanie len s jedným alebo niekoľkými žiakmi", "Výsledky každej hry", "Funguje na tabletoch, počítačoch aj mobiloch"],
        },
      ],
      finalTitle: "Vytvorte prvú aktivitu pre svojich žiakov",
      finalSub: "Začiatok je zadarmo a nepotrebujete žiadne technické znalosti.",
    },
  },
};

const hu: AudienceText = {
  forTitle: "Kinek szól a Make It?",
  forSub: "Nézd meg, hogyan segít a Make It a különböző területeken.",
  schools: "Iskoláknak",
  special: "SNI-s tanulóknak",
  schoolsLine: "Osztályok, eredmények és licencek az egész iskolának.",
  specialLine: "Minden tanulóhoz igazított feladatok.",
  more: "Tudj meg többet",
  pages: {
    schools: {
      title: "A Make It iskoláknak",
      eyebrow: "A Make It iskoláknak",
      heroTitle: "Oktatójátékok az egész iskolának",
      heroSub: "A tanárok percek alatt elkészítik saját játékaikat és kvízeiket, megosztják őket az osztályaikkal, és nyomon követik minden diák fejlődését. A weben, tableten és telefonon.",
      start: "Kezdd el ingyen",
      second: "Írj nekünk",
      tilesTitle: "Miért a Make It mellett döntenek az iskolák?",
      tiles: [
        {
          title: "Minden tantárgyhoz és korosztályhoz",
          text: "Több mint 20 feladattípus és több ezer képet és hangot tartalmazó könyvtárak, bármilyen tantárgyhoz, az óvodától a középiskoláig.",
        },
        {
          title: "Diákok fiók nélkül",
          text: "A diákok egy kóddal és a nevükkel csatlakoznak az osztályhoz. Nem kell e-mail-cím, jelszó, és semmit sem kell telepíteni.",
        },
        {
          title: "Minden diák eredményei",
          text: "Nézd meg, hogyan teljesített minden egyes diák az egyes játékokban és kvízekben: pontszám, hibák, idő és az elért oldalak.",
        },
        {
          title: "Bármilyen eszközön",
          text: "Működik böngészőben, valamint iPad, iPhone, Android és Mac alkalmazásként, így illeszkedik az iskolátok meglévő eszközeihez.",
        },
        {
          title: "Az adatvédelem az első",
          text: "Nincsenek hirdetések és nincs nyomkövetés. Az osztályok adatait csak az osztályok működtetésére használjuk, és ha az iskolának szüksége van rá, adatfeldolgozási szerződést kötünk vele.",
        },
        { title: "Licencek csapatoknak és iskoláknak", text: "Egy licenc több tanárnak, központi kezeléssel. A diákoknak a Make It mindig ingyenes." },
      ],
      sections: [
        {
          kicker: "Osztályok",
          title: "Minden csoportnak saját osztály",
          text: "Hozz létre egy osztályt minden csoportnak, add meg a diákoknak a kódját, és oszd meg velük a projekteket: az egész osztállyal, vagy csak azokkal a diákokkal, akiknek szükségük van rájuk.",
          bullets: [
            "A diákok kóddal csatlakoznak",
            "Megosztás az egész osztállyal vagy csak néhány diákkal",
            "Diákok áthelyezése másik osztályba, ha változnak a csoportok",
          ],
        },
        {
          kicker: "Nyomon követés",
          title: "Tudd, hogyan halad minden diák",
          text: "A játékok rögzítik minden diák pontszámát, hibáit és idejét, a kvízek pedig ezen felül minden választ, élő ranglistát és minden kérdés statisztikáját is megmutatják.",
          bullets: ["Minden játék és minden kvíz eredménye", "Válaszok és statisztikák kérdésenként", "Ranglisták, amelyek motiválják az osztályt"],
        },
        {
          kicker: "Készítés és megosztás",
          title: "A tanárok maguk készítik el a tananyagaikat",
          text: "Minden tanár percek alatt a diákjaihoz igazíthatja a feladatokat, saját szövegekkel, képekkel és hanggal. A projektek a felhőbe mentődnek, és megoszthatók az iskola többi tanárával.",
          bullets: ["Nincs szükség programozásra", "Kollégák projektjeinek másolása és átalakítása", "Szinkronban a weben és az alkalmazásokban"],
        },
      ],
      finalTitle: "Legyen a Make It az iskolátokban is",
      finalSub: "Kezdd el ingyen egy osztállyal, vagy írj nekünk, ha licencet szeretnél az összes tanárotok számára.",
    },
    special: {
      title: "A Make It SNI-s tanulóknak",
      eyebrow: "A Make It sajátos nevelési igényű tanulóknak",
      heroTitle: "Minden tanulóhoz igazított feladatok",
      heroSub: "A Make It segítségével tanárok, terapeuták és családok mindenkire szabott játékokat készíthetnek: saját képekkel, ismerős hangokkal, egyéni tempóban. Gyógypedagógiához, logopédiához, inkluzív osztályokba és otthonra.",
      start: "Kezdd el ingyen",
      second: "Próbálj ki egy demót",
      tilesTitle: "Úgy terveztük, hogy lebontsa az akadályokat",
      tiles: [
        { title: "Nem kell olvasni", text: "Az utasításokhoz és az elemekhez hang is tartozhat: egy koppintás, és a tanuló hallja, mi a teendő." },
        {
          title: "A hiba nem baj",
          text: "A játékok minden sikert jutalmaznak, a hibákat sosem büntetik, és annyi próbálkozást engednek, amennyi csak kell.",
        },
        {
          title: "A saját világuk",
          text: "Használj fényképeket a saját tárgyaikról, a nekik ismerős helyekről és emberekről, és vedd fel a hangodat, hogy egy ismerős hangot halljanak.",
        },
        {
          title: "Jól olvasható szöveg",
          text: "Válaszd meg minden szöveg méretét, és használj olyan betűtípusokat, mint az OpenDyslexic vagy egy iskolai kézírásos betűtípus.",
        },
        {
          title: "Nyugalom, ha kell",
          text: "Kapcsold ki a hangeffektusokat vagy a zenét, és döntsd el, hogy a tanuló szabadon lapozhat-e az oldalak között.",
        },
        {
          title: "Egyszerű mozdulatok",
          text: "Koppintás, húzás, összekötés és átrajzolás ujjal a tableten: olyan feladatok, amelyek a finommotorikát is fejlesztik.",
        },
      ],
      sections: [
        {
          kicker: "Személyre szabás",
          title: "Minden tanulóra szabva",
          text: "Minden projekt egyetlen emberhez igazítható: az érdeklődéséhez, a szókincséhez és a szintjéhez. Duplikálj egy feladatot, és egy pillanat alatt átalakíthatod egy másik tanuló számára.",
          bullets: [
            "Saját fényképek és rajzok",
            "Saját felvett hang",
            "Választható szövegméret és betűtípusok",
            "Több ezer kép, köztük érzelmek és a mindennapi élet",
          ],
        },
        {
          kicker: "Motiválás",
          title: "Pozitív megerősítés minden oldalon",
          text: "Minden sikert csillagok és hangok ünnepelnek, a hiba után pedig a tanuló egyszerűen újra próbálkozhat. A játékokban nincs időkorlát, így mindenki a saját tempójában haladhat.",
          bullets: ["Korlátlan próbálkozás", "Csillagok és hangok minden oldal után", "Nincs időnyomás"],
        },
        {
          kicker: "Nyomon követés",
          title: "Egy tanuló, saját feladatok",
          text: "Oszd meg a kiválasztott projekteket az osztály kiválasztott diákjaival, és nézd meg, hogyan teljesít mindegyikük: pontszám, hibák és idő, oldalról oldalra.",
          bullets: ["Megosztás csak egy vagy néhány diákkal", "Minden játék eredménye", "Működik tableten, számítógépen és telefonon"],
        },
      ],
      finalTitle: "Készítsd el az első feladatot a tanulóidnak",
      finalSub: "Ingyenesen elkezdheted, és nincs szükség műszaki tudásra.",
    },
  },
};

const hr: AudienceText = {
  forTitle: "Make It za…",
  forSub: "Pogledaj kako Make It pomaže u svakom okruženju.",
  schools: "Škole",
  special: "Posebne potrebe",
  schoolsLine: "Razredi, rezultati i licence za cijelu školu.",
  specialLine: "Aktivnosti prilagođene svakom učeniku.",
  more: "Saznaj više",
  pages: {
    schools: {
      title: "Make It za škole",
      eyebrow: "Make It za škole",
      heroTitle: "Igre za učenje za cijelu školu",
      heroSub: "Učitelji u nekoliko minuta izrađuju vlastite igre i kvizove, dijele ih sa svojim razredima i prate napredak svakog učenika. Na webu, na tabletima i na mobitelima.",
      start: "Započni besplatno",
      second: "Kontaktiraj nas",
      tilesTitle: "Zašto škole biraju Make It",
      tiles: [
        {
          title: "Za svaki predmet i dob",
          text: "Više od 20 vrsta aktivnosti i zbirke s tisućama slika i zvukova, spremne za bilo koji predmet, od vrtića do srednje škole.",
        },
        {
          title: "Učenici bez računa",
          text: "Učenici se pridružuju razredu pomoću kôda i svog imena. Bez adresa e-pošte, bez lozinki i bez instaliranja.",
        },
        {
          title: "Rezultati svakog učenika",
          text: "Pogledaj kako je svaki učenik prošao u svakoj igri i svakom kvizu: bodovi, pogreške, vrijeme i stranice do kojih je stigao.",
        },
        {
          title: "Bilo koji uređaj",
          text: "Radi u pregledniku i u aplikacijama za iPad, iPhone, Android i Mac, pa se uklapa u uređaje koje tvoja škola već ima.",
        },
        {
          title: "Privatnost na prvom mjestu",
          text: "Bez oglasa i bez praćenja. Podaci razreda koriste se samo za rad razreda, a sa školom potpisujemo ugovor o obradi podataka kad joj je potreban.",
        },
        {
          title: "Licence za timove i škole",
          text: "Jedna licenca za više učitelja, s upraviteljem licence. Učenici Make It uvijek koriste besplatno.",
        },
      ],
      sections: [
        {
          kicker: "Razredi",
          title: "Razred za svaku skupinu",
          text: "Izradi razred za svaku skupinu, daj učenicima njegov kôd i dijeli projekte s njima: s cijelim razredom ili samo s učenicima kojima su potrebni.",
          bullets: [
            "Učenici se pridružuju pomoću kôda",
            "Dijeli s cijelim razredom ili samo s nekima",
            "Premještaj učenike iz razreda u razred kad se skupine promijene",
          ],
        },
        {
          kicker: "Praćenje",
          title: "Znaj kako napreduje svaki učenik",
          text: "Igre bilježe bodove, pogreške i vrijeme svakog učenika, a kvizovi uz to bilježe svaki odgovor, ljestvicu uživo i statistiku svakog pitanja.",
          bullets: ["Rezultati svake igre i svakog kviza", "Odgovori i statistika po pitanju", "Ljestvice koje motiviraju razred"],
        },
        {
          kicker: "Izradi i dijeli",
          title: "Učitelji izrađuju vlastite materijale",
          text: "Svaki učitelj u nekoliko minuta prilagođava aktivnosti svojim učenicima, s vlastitim tekstovima, slikama i glasom. Projekti se spremaju u oblak i mogu se dijeliti s drugim učiteljima škole.",
          bullets: ["Bez programiranja", "Kopiraj i prilagodi projekte kolega", "Sinkronizirano na webu i u aplikacijama"],
        },
      ],
      finalTitle: "Dovedi Make It u svoju školu",
      finalSub: "Započni besplatno s jednim razredom ili nam piši za licencu za sve svoje učitelje.",
    },
    special: {
      title: "Make It za posebne potrebe",
      eyebrow: "Make It za posebne odgojno-obrazovne potrebe",
      heroTitle: "Aktivnosti prilagođene svakom učeniku",
      heroSub: "Uz Make It učitelji, terapeuti i obitelji izrađuju igre prilagođene svakoj osobi: njezine slike, njezin glas, njezin tempo. Za edukacijsko-rehabilitacijski rad, logopediju, inkluzivne razrede i rad kod kuće.",
      start: "Započni besplatno",
      second: "Isprobaj primjer",
      tilesTitle: "Osmišljeno za uklanjanje prepreka",
      tiles: [
        { title: "Nije potrebno čitati", text: "Upute i elementi mogu imati zvuk: jedan dodir i učenik čuje što treba napraviti." },
        {
          title: "Pogreške se ne kažnjavaju",
          text: "Igre nagrađuju svaki uspjeh, nikad ne kažnjavaju pogreške i dopuštaju onoliko pokušaja koliko je potrebno.",
        },
        { title: "Njihov svijet", text: "Koristi fotografije njihovih stvari, mjesta i ljudi te snimi svoj glas da čuju nekoga koga poznaju." },
        { title: "Čitljiv tekst", text: "Odaberi veličinu svakog teksta i fontove poput OpenDyslexic ili školskog rukopisnog fonta." },
        {
          title: "Mir kad je potreban",
          text: "Isključi zvučne efekte ili glazbu i odluči može li učenik slobodno prelaziti s jedne stranice na drugu.",
        },
        { title: "Jednostavne geste", text: "Dodiruj, povlači, povezuj i iscrtavaj prstom na tabletu: aktivnosti koje razvijaju i finu motoriku." },
      ],
      sections: [
        {
          kicker: "Prilagodi",
          title: "Po mjeri svakog učenika",
          text: "Svaki se projekt može prilagoditi jednoj osobi: njezinim interesima, rječniku i razini. Dupliciraj aktivnost i u trenu je prilagodi drugom učeniku.",
          bullets: [
            "Vlastite fotografije i crteži",
            "Vlastiti snimljeni glas",
            "Veličina teksta i fontovi po izboru",
            "Tisuće slika, uključujući emocije i svakodnevni život",
          ],
        },
        {
          kicker: "Motiviraj",
          title: "Pozitivno potkrepljenje na svakoj stranici",
          text: "Svaki uspjeh slavi se zvjezdicama i zvukovima, a pogreška samo znači novi pokušaj. U igrama nema vremenskog ograničenja, pa svatko može napredovati svojim tempom.",
          bullets: ["Neograničen broj pokušaja", "Zvjezdice i zvukovi nakon svake stranice", "Bez vremenskog pritiska"],
        },
        {
          kicker: "Prati",
          title: "Jedan učenik, njegove aktivnosti",
          text: "Dijeli određene projekte s određenim učenicima u razredu i pogledaj kako svaki od njih napreduje: bodovi, pogreške i vrijeme, stranicu po stranicu.",
          bullets: ["Dijeli sa samo jednim ili nekoliko učenika", "Rezultati svake igre", "Radi na tabletima, računalima i mobitelima"],
        },
      ],
      finalTitle: "Izradi prvu aktivnost za svoje učenike",
      finalSub: "Početak je besplatan i ne trebaš nikakvo tehničko znanje.",
    },
  },
};

const bg: AudienceText = {
  forTitle: "Make It за…",
  forSub: "Вижте как Make It помага във всяка среда.",
  schools: "Училища",
  special: "Специални потребности",
  schoolsLine: "Класове, резултати и лицензи за цялото училище.",
  specialLine: "Дейности, адаптирани за всеки ученик.",
  more: "Научете повече",
  pages: {
    schools: {
      title: "Make It за училища",
      eyebrow: "Make It за училища",
      heroTitle: "Образователни игри за цялото училище",
      heroSub: "Учителите създават свои игри и викторини за минути, споделят ги с класовете си и следят напредъка на всеки ученик. В браузъра, на таблети и на телефони.",
      start: "Започнете безплатно",
      second: "Свържете се с нас",
      tilesTitle: "Защо училищата избират Make It",
      tiles: [
        {
          title: "За всеки предмет и възраст",
          text: "Над 20 вида дейности и библиотеки с хиляди изображения и звуци, готови за всеки предмет – от детската градина до гимназията.",
        },
        {
          title: "Ученици без профили",
          text: "Учениците се присъединяват към клас с код и името си. Без имейл адреси, без пароли и без нищо за инсталиране.",
        },
        {
          title: "Резултатите на всеки ученик",
          text: "Вижте как се е справил всеки ученик във всяка игра и всяка викторина: точки, грешки, време и до коя страница е стигнал.",
        },
        {
          title: "Всяко устройство",
          text: "Работи в браузъра и в приложенията за iPad, iPhone, Android и Mac, така че подхожда на устройствата, които училището ви вече има.",
        },
        {
          title: "Поверителността на първо място",
          text: "Без реклами и без проследяване. Данните на класовете се използват само за работата на класовете, а когато училището има нужда, подписваме с него споразумение за обработване на данни.",
        },
        {
          title: "Лицензи за екипи и училища",
          text: "Един лиценз за няколко учители, с администратор на лиценза. Учениците винаги използват Make It безплатно.",
        },
      ],
      sections: [
        {
          kicker: "Класове",
          title: "Клас за всяка група",
          text: "Създайте клас за всяка група, дайте кода му на учениците и споделяйте проекти с тях: с целия клас или само с учениците, които имат нужда от тях.",
          bullets: [
            "Учениците се присъединяват с код",
            "Споделяне с целия клас или с няколко ученици",
            "Преместване на ученици между класовете, когато групите се променят",
          ],
        },
        {
          kicker: "Проследяване",
          title: "Знайте как се справя всеки ученик",
          text: "Игрите записват точките, грешките и времето на всеки ученик, а викторините добавят всеки отговор, класиране на живо и статистика за всеки въпрос.",
          bullets: ["Резултати от всяка игра и всяка викторина", "Отговори и статистика за всеки въпрос", "Класиране, което мотивира класа"],
        },
        {
          kicker: "Създаване и споделяне",
          title: "Учителите създават свои собствени материали",
          text: "Всеки учител адаптира дейностите за своите ученици за минути, със собствени текстове, снимки и глас. Проектите се съхраняват в облака и могат да се споделят с други учители от училището.",
          bullets: ["Не е нужно програмиране", "Копирайте и адаптирайте проекти на колеги", "Синхронизация между уеб версията и приложенията"],
        },
      ],
      finalTitle: "Въведете Make It във вашето училище",
      finalSub: "Започнете безплатно с един клас или ни пишете, за да получите лиценз за всичките си учители.",
    },
    special: {
      title: "Make It за специални потребности",
      eyebrow: "Make It за специални образователни потребности",
      heroTitle: "Дейности, адаптирани за всеки ученик",
      heroSub: "С Make It учители, терапевти и семейства създават игри, съобразени с всеки човек: неговите снимки, неговия глас, неговото темпо. За специалното образование, логопедията, приобщаващите класове и у дома.",
      start: "Започнете безплатно",
      second: "Опитайте демо",
      tilesTitle: "Създаден да премахва бариерите",
      tiles: [
        {
          title: "Не е нужно да се чете",
          text: "Инструкциите и елементите могат да имат звук: едно докосване и ученикът чува какво трябва да направи.",
        },
        {
          title: "Грешките не се наказват",
          text: "Игрите възнаграждават всеки успех, никога не наказват грешките и позволяват колкото опити са нужни.",
        },
        { title: "Техният свят", text: "Използвайте снимки на техните вещи, места и хора и запишете гласа си, за да чуват някого, когото познават." },
        { title: "Четим текст", text: "Изберете размера на всеки текст и шрифтове като OpenDyslexic или училищен ръкописен шрифт." },
        {
          title: "Спокойствие, когато е нужно",
          text: "Изключете звуковите ефекти или музиката и решете дали ученикът може свободно да преминава между страниците.",
        },
        {
          title: "Прости жестове",
          text: "Докосване, плъзгане, свързване и очертаване с пръст на таблет: дейности, които развиват и фината моторика.",
        },
      ],
      sections: [
        {
          kicker: "Персонализиране",
          title: "По мярка за всеки ученик",
          text: "Всеки проект може да се адаптира за един човек: неговите интереси, речник и ниво. Дублирайте дейност и я нагласете за друг ученик за миг.",
          bullets: [
            "Ваши собствени снимки и рисунки",
            "Вашият собствен записан глас",
            "Размер на текста и шрифтове по избор",
            "Хиляди изображения, включително емоции и ежедневие",
          ],
        },
        {
          kicker: "Мотивиране",
          title: "Положително подкрепление на всяка страница",
          text: "Всеки успех се отбелязва със звезди и звуци, а при грешка ученикът просто опитва отново. В игрите няма ограничение на времето, така че всеки може да върви със своето темпо.",
          bullets: ["Неограничен брой опити", "Звезди и звуци след всяка страница", "Без натиск от времето"],
        },
        {
          kicker: "Проследяване",
          title: "Един ученик, неговите собствени дейности",
          text: "Споделяйте конкретни проекти с конкретни ученици от даден клас и вижте как се справя всеки от тях: точки, грешки и време, страница по страница.",
          bullets: ["Споделяне само с един или няколко ученици", "Резултати от всяка игра", "Работи на таблети, компютри и телефони"],
        },
      ],
      finalTitle: "Създайте първата дейност за вашите ученици",
      finalSub: "Започването е безплатно и не са ви нужни технически познания.",
    },
  },
};

const da: AudienceText = {
  forTitle: "Make It til…",
  forSub: "Se, hvordan Make It hjælper i forskellige sammenhænge.",
  schools: "Skoler",
  special: "Særlige behov",
  schoolsLine: "Klasser, resultater og licenser til hele skolen.",
  specialLine: "Aktiviteter tilpasset hver enkelt elev.",
  more: "Læs mere",
  pages: {
    schools: {
      title: "Make It til skoler",
      eyebrow: "Make It til skoler",
      heroTitle: "Lærerige spil til hele skolen",
      heroSub: "Lærerne laver deres egne spil og quizzer på få minutter, deler dem med deres klasser og følger hver elevs fremskridt. På nettet, på tablets og på telefoner.",
      start: "Kom gratis i gang",
      second: "Kontakt os",
      tilesTitle: "Derfor vælger skoler Make It",
      tiles: [
        {
          title: "Til alle fag og aldre",
          text: "Mere end 20 typer aktiviteter og biblioteker med tusindvis af billeder og lyde, klar til ethvert fag, fra børnehaveklassen til udskolingen.",
        },
        {
          title: "Elever uden konto",
          text: "Eleverne tilmelder sig en klasse med en kode og deres navn. Ingen e-mailadresser, ingen adgangskoder og intet at installere.",
        },
        {
          title: "Hver elevs resultater",
          text: "Se, hvordan hver elev har klaret sig i hvert spil og hver quiz: point, fejl, tid og hvor langt de nåede.",
        },
        {
          title: "Alle enheder",
          text: "Det virker i browseren og i apps til iPad, iPhone, Android og Mac, så det passer til de enheder, jeres skole allerede har.",
        },
        {
          title: "Privatliv først",
          text: "Ingen reklamer og ingen sporing. Klassedata bruges kun til at drive klasserne, og vi indgår en databehandleraftale med skolen, når der er brug for det.",
        },
        {
          title: "Licenser til teams og skoler",
          text: "Én licens til flere lærere med en licensadministrator. Eleverne bruger altid Make It gratis.",
        },
      ],
      sections: [
        {
          kicker: "Klasser",
          title: "En klasse til hver gruppe",
          text: "Opret en klasse til hver gruppe, giv eleverne klassekoden, og del projekter med dem: med hele klassen eller kun med de elever, der har brug for dem.",
          bullets: ["Eleverne tilmelder sig med en kode", "Del med hele klassen eller med nogle få", "Flyt elever mellem klasser, når grupperne ændrer sig"],
        },
        {
          kicker: "Opfølgning",
          title: "Vid, hvordan det går hver elev",
          text: "Spillene registrerer hver elevs point, fejl og tid, og quizzerne tilføjer hvert svar, en rangliste i realtid og statistik for hvert spørgsmål.",
          bullets: ["Resultater fra hvert spil og hver quiz", "Svar og statistik pr. spørgsmål", "Ranglister, der motiverer klassen"],
        },
        {
          kicker: "Skab og del",
          title: "Lærerne laver deres egne materialer",
          text: "Hver lærer tilpasser aktiviteterne til sine elever på få minutter med egne tekster, billeder og stemme. Projekterne gemmes i skyen og kan deles med skolens andre lærere.",
          bullets: ["Ingen kodning nødvendig", "Kopiér og tilpas kollegernes projekter", "Synkroniseret på nettet og i apps"],
        },
      ],
      finalTitle: "Få Make It ind på jeres skole",
      finalSub: "Start gratis med én klasse, eller skriv til os for at få en licens til alle jeres lærere.",
    },
    special: {
      title: "Make It til særlige behov",
      eyebrow: "Make It til specialpædagogik",
      heroTitle: "Aktiviteter tilpasset hver enkelt elev",
      heroSub: "Med Make It kan lærere, terapeuter og familier lave spil, der er skræddersyet til hver enkelt: deres billeder, deres stemme, deres tempo. Til specialundervisning, logopædi, inkluderende klasser og derhjemme.",
      start: "Kom gratis i gang",
      second: "Prøv en demo",
      tilesTitle: "Skabt til at fjerne barrierer",
      tiles: [
        { title: "Ingen læsning nødvendig", text: "Instruktioner og elementer kan have lyd: ét tryk, og eleven hører, hvad der skal gøres." },
        {
          title: "Fejl tæller ikke imod dem",
          text: "Spillene belønner hver succes, straffer aldrig fejl og giver så mange forsøg, som der er brug for.",
        },
        { title: "Deres egen verden", text: "Brug fotos af deres egne ting, steder og personer, og optag din stemme, så de hører en, de kender." },
        { title: "Læsevenlig tekst", text: "Vælg størrelsen på hver tekst og skrifttyper som OpenDyslexic eller en skrifttype med skolehåndskrift." },
        {
          title: "Ro, når der er brug for det",
          text: "Slå lydeffekterne eller musikken fra, og bestem, om eleven frit kan bevæge sig mellem siderne.",
        },
        {
          title: "Enkle bevægelser",
          text: "Tryk, træk, forbind og tegn efter med en finger på en tablet: aktiviteter, der samtidig træner finmotorikken.",
        },
      ],
      sections: [
        {
          kicker: "Tilpas",
          title: "Skræddersyet til hver elev",
          text: "Hvert projekt kan tilpasses én person: interesser, ordforråd og niveau. Dupliker en aktivitet, og tilpas den til en anden elev på et øjeblik.",
          bullets: [
            "Dine egne fotos og tegninger",
            "Din egen optagede stemme",
            "Valgfri tekststørrelse og skrifttyper",
            "Tusindvis af billeder, også af følelser og hverdagsliv",
          ],
        },
        {
          kicker: "Motiver",
          title: "Positiv forstærkning på hver side",
          text: "Hver succes fejres med stjerner og lyde, og efter en fejl kan eleven bare prøve igen. Spillene har ingen tidsgrænser, så alle kan arbejde i deres eget tempo.",
          bullets: ["Ubegrænsede forsøg", "Stjerner og lyde efter hver side", "Intet tidspres"],
        },
        {
          kicker: "Følg op",
          title: "Én elev, egne aktiviteter",
          text: "Del bestemte projekter med bestemte elever i en klasse, og se, hvordan det går hver af dem: point, fejl og tid, side for side.",
          bullets: ["Del med kun én eller nogle få elever", "Resultater fra hvert spil", "Virker på tablets, computere og telefoner"],
        },
      ],
      finalTitle: "Lav den første aktivitet til dine elever",
      finalSub: "Det er gratis at starte, og du behøver ingen teknisk viden.",
    },
  },
};

const nb: AudienceText = {
  forTitle: "Make It for …",
  forSub: "Se hvordan Make It hjelper i ulike sammenhenger.",
  schools: "Skoler",
  special: "Spesielle behov",
  schoolsLine: "Klasser, resultater og lisenser for hele skolen.",
  specialLine: "Aktiviteter tilpasset hver enkelt elev.",
  more: "Les mer",
  pages: {
    schools: {
      title: "Make It for skoler",
      eyebrow: "Make It for skoler",
      heroTitle: "Læringsspill for hele skolen",
      heroSub: "Lærerne lager sine egne spill og quizer på få minutter, deler dem med klassene sine og følger fremgangen til hver elev. På nettet, på nettbrett og på mobil.",
      start: "Kom i gang gratis",
      second: "Kontakt oss",
      tilesTitle: "Derfor velger skoler Make It",
      tiles: [
        {
          title: "For alle fag og aldre",
          text: "Over 20 typer aktiviteter og biblioteker med tusenvis av bilder og lyder, klare for alle fag, fra barnehage til ungdomsskole.",
        },
        {
          title: "Elever uten konto",
          text: "Elevene blir med i en klasse med en kode og navnet sitt. Ingen e-postadresser, ingen passord og ingenting å installere.",
        },
        {
          title: "Resultater for hver elev",
          text: "Se hvordan hver elev gjorde det i hvert spill og hver quiz: poeng, feil, tid og hvor langt de kom.",
        },
        {
          title: "Alle enheter",
          text: "Det fungerer i nettleseren og i appene for iPad, iPhone, Android og Mac, så det passer til enhetene skolen allerede har.",
        },
        {
          title: "Personvern først",
          text: "Ingen reklame og ingen sporing. Klassedata brukes bare til å drive klassene, og vi inngår en databehandleravtale med skolen når den trenger det.",
        },
        {
          title: "Lisenser for team og skoler",
          text: "Én lisens for flere lærere, med en lisensadministrator. Elevene bruker alltid Make It gratis.",
        },
      ],
      sections: [
        {
          kicker: "Klasser",
          title: "En klasse for hver gruppe",
          text: "Opprett en klasse for hver gruppe, gi elevene koden og del prosjekter med dem: med hele klassen, eller bare med elevene som trenger dem.",
          bullets: ["Elevene blir med via en kode", "Del med hele klassen eller med noen få", "Flytt elever mellom klasser når gruppene endres"],
        },
        {
          kicker: "Oppfølging",
          title: "Vit hvordan det går med hver elev",
          text: "Spillene registrerer poengsummen, feilene og tiden til hver elev, og quizene legger til hvert svar, en toppliste i sanntid og statistikk for hvert spørsmål.",
          bullets: ["Resultater for hvert spill og hver quiz", "Svar og statistikk per spørsmål", "Topplister som motiverer klassen"],
        },
        {
          kicker: "Lag og del",
          title: "Lærerne lager sitt eget materiell",
          text: "Hver lærer tilpasser aktivitetene til elevene sine på få minutter, med egne tekster, bilder og egen stemme. Prosjektene lagres i skyen og kan deles med andre lærere på skolen.",
          bullets: ["Ingen koding nødvendig", "Kopier og tilpass prosjekter fra kolleger", "Synkronisert på nettet og i appene"],
        },
      ],
      finalTitle: "Ta med Make It til skolen din",
      finalSub: "Kom i gang gratis med én klasse, eller skriv til oss for å få en lisens for alle lærerne dine.",
    },
    special: {
      title: "Make It for spesielle behov",
      eyebrow: "Make It for spesialundervisning",
      heroTitle: "Aktiviteter tilpasset hver enkelt elev",
      heroSub: "Med Make It lager lærere, terapeuter og familier spill som er skreddersydd for hver enkelt: egne bilder, egen stemme, eget tempo. For spesialundervisning, logopedi, inkluderende klasserom og hjemme.",
      start: "Kom i gang gratis",
      second: "Prøv en demo",
      tilesTitle: "Laget for å fjerne barrierer",
      tiles: [
        { title: "Ingen lesing nødvendig", text: "Instruksjoner og elementer kan ha lyd: ett trykk, og eleven hører hva som skal gjøres." },
        { title: "Feil teller ikke mot dem", text: "Spillene belønner all mestring, straffer aldri feil og gir så mange forsøk som trengs." },
        { title: "En verden de kjenner", text: "Bruk bilder av egne ting, steder og mennesker, og ta opp stemmen din, så de hører noen de kjenner." },
        { title: "Lesbar tekst", text: "Velg størrelsen på hver tekst og skrifttyper som OpenDyslexic eller en skoleskrift." },
        { title: "Rolig når det trengs", text: "Slå av lydeffektene eller musikken, og bestem om eleven kan bla fritt mellom sidene." },
        {
          title: "Enkle bevegelser",
          text: "Trykk, dra, koble sammen og spor med fingeren på et nettbrett: aktiviteter som også trener finmotorikken.",
        },
      ],
      sections: [
        {
          kicker: "Tilpass",
          title: "Skreddersydd for hver elev",
          text: "Hvert prosjekt kan tilpasses én person: interessene, ordforrådet og nivået. Dupliser en aktivitet og juster den for en annen elev på et øyeblikk.",
          bullets: [
            "Dine egne bilder og tegninger",
            "Din egen innspilte stemme",
            "Valgfri tekststørrelse og skrifttype",
            "Tusenvis av bilder, blant annet følelser og hverdagsliv",
          ],
        },
        {
          kicker: "Motiver",
          title: "Positiv forsterkning på hver side",
          text: "All mestring feires med stjerner og lyder, og feil betyr bare at eleven kan prøve igjen. Spillene har ingen tidsgrenser, så alle kan jobbe i sitt eget tempo.",
          bullets: ["Ubegrenset med forsøk", "Stjerner og lyder etter hver side", "Ingen tidspress"],
        },
        {
          kicker: "Følg opp",
          title: "Én elev, egne aktiviteter",
          text: "Del bestemte prosjekter med bestemte elever i en klasse, og se hvordan det går med hver av dem: poeng, feil og tid, side for side.",
          bullets: ["Del med bare én eller noen få elever", "Resultater for hvert spill", "Fungerer på nettbrett, datamaskiner og mobiler"],
        },
      ],
      finalTitle: "Lag den første aktiviteten for elevene dine",
      finalSub: "Det er gratis å komme i gang, og du trenger ingen tekniske forkunnskaper.",
    },
  },
};

const fi: AudienceText = {
  forTitle: "Make It eri tarpeisiin",
  forSub: "Katso, miten Make It auttaa eri ympäristöissä.",
  schools: "Koulut",
  special: "Erityinen tuki",
  schoolsLine: "Luokat, tulokset ja lisenssit koko koululle.",
  specialLine: "Jokaiselle oppijalle mukautettuja tehtäviä.",
  more: "Lue lisää",
  pages: {
    schools: {
      title: "Make It kouluille",
      eyebrow: "Make It kouluille",
      heroTitle: "Oppimispelejä koko koululle",
      heroSub: "Opettajat luovat omat pelinsä ja tietovisansa minuuteissa, jakavat ne luokilleen ja seuraavat jokaisen oppilaan edistymistä. Verkossa, tableteilla ja puhelimilla.",
      start: "Aloita ilmaiseksi",
      second: "Ota yhteyttä",
      tilesTitle: "Miksi koulut valitsevat Make It -palvelun",
      tiles: [
        {
          title: "Kaikkiin aineisiin ja ikäryhmiin",
          text: "Yli 20 tehtävätyyppiä ja kirjastot, joissa on tuhansia kuvia ja ääniä, valmiina mihin tahansa oppiaineeseen varhaiskasvatuksesta yläkouluun.",
        },
        {
          title: "Oppilaat ilman tilejä",
          text: "Oppilaat liittyvät luokkaan koodilla ja nimellään. Ei sähköpostiosoitteita, ei salasanoja eikä mitään asennettavaa.",
        },
        {
          title: "Jokaisen oppilaan tulokset",
          text: "Näe, miten kukin oppilas suoriutui jokaisesta pelistä ja tietovisasta: pisteet, virheet, aika ja sivut, joille hän eteni.",
        },
        {
          title: "Mikä tahansa laite",
          text: "Toimii selaimessa sekä iPad-, iPhone-, Android- ja Mac-sovelluksissa, joten se sopii koulusi nykyisiin laitteisiin.",
        },
        {
          title: "Yksityisyys edellä",
          text: "Ei mainoksia eikä seurantaa. Luokkien tietoja käytetään vain luokkien toimintaan, ja teemme koulun kanssa tietojenkäsittelysopimuksen, kun koulu sitä tarvitsee.",
        },
        {
          title: "Lisenssit tiimeille ja kouluille",
          text: "Yksi lisenssi usealle opettajalle lisenssinhallinnan kera. Oppilaille Make It on aina ilmainen.",
        },
      ],
      sections: [
        {
          kicker: "Luokat",
          title: "Oma luokka jokaiselle ryhmälle",
          text: "Luo luokka jokaiselle ryhmälle, anna oppilaille sen koodi ja jaa heille projekteja: koko luokalle tai vain niille oppilaille, jotka niitä tarvitsevat.",
          bullets: ["Oppilaat liittyvät koodilla", "Jaa koko luokalle tai muutamalle", "Siirrä oppilaita luokasta toiseen, kun ryhmät muuttuvat"],
        },
        {
          kicker: "Seuranta",
          title: "Tiedä, miten jokainen oppilas edistyy",
          text: "Pelit tallentavat jokaisen oppilaan pisteet, virheet ja ajan, ja tietovisat lisäksi jokaisen vastauksen, reaaliaikaisen tulostaulun ja jokaisen kysymyksen tilastot.",
          bullets: ["Jokaisen pelin ja tietovisan tulokset", "Vastaukset ja tilastot kysymyksittäin", "Tulostaulut, jotka motivoivat luokkaa"],
        },
        {
          kicker: "Luo ja jaa",
          title: "Opettajat tekevät omat materiaalinsa",
          text: "Jokainen opettaja mukauttaa tehtävät oppilailleen minuuteissa omilla teksteillään, kuvillaan ja äänellään. Projektit tallentuvat pilveen, ja niitä voi jakaa koulun muille opettajille.",
          bullets: ["Ohjelmointitaitoja ei tarvita", "Kopioi ja muokkaa kollegoiden projekteja", "Synkronoitu verkossa ja sovelluksissa"],
        },
      ],
      finalTitle: "Tuo Make It kouluusi",
      finalSub: "Aloita ilmaiseksi yhdellä luokalla tai kirjoita meille, niin saat lisenssin kaikille opettajillesi.",
    },
    special: {
      title: "Make It erityiseen tukeen",
      eyebrow: "Make It erityisen tuen tarpeisiin",
      heroTitle: "Jokaiselle oppijalle mukautettuja tehtäviä",
      heroSub: "Make It -palvelulla opettajat, terapeutit ja perheet luovat pelejä, jotka on räätälöity kullekin: omat kuvat, oma ääni, oma tahti. Erityisopetukseen, puheterapiaan, inklusiivisiin luokkiin ja kotiin.",
      start: "Aloita ilmaiseksi",
      second: "Kokeile demoa",
      tilesTitle: "Suunniteltu poistamaan esteitä",
      tiles: [
        { title: "Lukutaitoa ei tarvita", text: "Ohjeisiin ja elementteihin voi lisätä äänen: yksi napautus, ja oppija kuulee, mitä tehdä." },
        {
          title: "Virheistä ei rangaista",
          text: "Pelit palkitsevat jokaisesta onnistumisesta, eivät koskaan rankaise virheistä ja sallivat niin monta yritystä kuin tarvitaan.",
        },
        {
          title: "Oma tuttu maailma",
          text: "Käytä valokuvia oppijan omista tavaroista, paikoista ja ihmisistä, ja äänitä oma äänesi, jotta hän kuulee tutun ihmisen puhuvan.",
        },
        { title: "Helppolukuinen teksti", text: "Valitse jokaisen tekstin koko ja fontit, kuten OpenDyslexic tai koulukäsialaa muistuttava fontti." },
        {
          title: "Rauhallisesti tarvittaessa",
          text: "Kytke äänitehosteet tai musiikki pois ja päätä, saako oppija siirtyä sivulta toiselle vapaasti.",
        },
        {
          title: "Yksinkertaiset eleet",
          text: "Napauta, vedä, yhdistä ja jäljennä sormella tabletilla: tehtäviä, jotka harjoittavat myös hienomotoriikkaa.",
        },
      ],
      sections: [
        {
          kicker: "Mukauta",
          title: "Räätälöity jokaiselle oppijalle",
          text: "Jokaisen projektin voi mukauttaa yhdelle ihmiselle: hänen kiinnostuksen kohteisiinsa, sanavarastoonsa ja tasoonsa. Monista tehtävä ja muokkaa se hetkessä toiselle oppijalle sopivaksi.",
          bullets: ["Omat valokuvat ja piirrokset", "Oma äänitetty ääni", "Valittavat tekstikoot ja fontit", "Tuhansia kuvia, myös tunteista ja arjesta"],
        },
        {
          kicker: "Motivoi",
          title: "Positiivinen kannustus jokaisella sivulla",
          text: "Jokaista onnistumista juhlitaan tähdillä ja äänillä, ja virheen jälkeen oppija voi yksinkertaisesti yrittää uudelleen. Peleissä ei ole aikarajoja, joten jokainen voi edetä omaan tahtiinsa.",
          bullets: ["Rajattomasti yrityksiä", "Tähtiä ja ääniä jokaisen sivun jälkeen", "Ei aikapaineita"],
        },
        {
          kicker: "Seuraa",
          title: "Yksi oppija, omat tehtävät",
          text: "Jaa tiettyjä projekteja tietyille luokan oppilaille ja näe, miten kukin heistä suoriutuu: pisteet, virheet ja aika sivu sivulta.",
          bullets: ["Jaa vain yhdelle tai muutamalle oppilaalle", "Jokaisen pelin tulokset", "Toimii tableteilla, tietokoneilla ja puhelimilla"],
        },
      ],
      finalTitle: "Luo ensimmäinen tehtävä oppijoillesi",
      finalSub: "Aloittaminen on ilmaista, eikä se vaadi teknistä osaamista.",
    },
  },
};

const fa: AudienceText = {
  forTitle: "Make It برای…",
  forSub: "ببینید Make It در هر محیطی چطور کمک می‌کند.",
  schools: "مدرسه‌ها",
  special: "نیازهای ویژه",
  schoolsLine: "کلاس‌ها، نتایج و لایسنس برای کل مدرسه.",
  specialLine: "فعالیت‌هایی متناسب با هر یادگیرنده.",
  more: "بیشتر بدانید",
  pages: {
    schools: {
      title: "Make It برای مدرسه‌ها",
      eyebrow: "Make It برای مدرسه‌ها",
      heroTitle: "بازی‌های آموزشی برای کل مدرسه",
      heroSub: "معلمان در چند دقیقه بازی‌ها و آزمونک‌های خودشان را می‌سازند، آن‌ها را با کلاس‌هایشان به اشتراک می‌گذارند و پیشرفت هر دانش‌آموز را دنبال می‌کنند. در وب، روی تبلت و روی گوشی.",
      start: "رایگان شروع کنید",
      second: "تماس با ما",
      tilesTitle: "چرا مدرسه‌ها Make It را انتخاب می‌کنند",
      tiles: [
        {
          title: "برای هر درس و هر سنی",
          text: "بیش از 20 نوع فعالیت و کتابخانه‌هایی با هزاران تصویر و صدا، آماده برای هر درسی، از پیش‌دبستانی تا دبیرستان.",
        },
        {
          title: "دانش‌آموزان بدون حساب",
          text: "دانش‌آموزان با یک کد و نام خود به کلاس می‌پیوندند. بدون ایمیل، بدون رمز عبور و بدون نیاز به نصب هیچ چیز.",
        },
        {
          title: "نتایج هر دانش‌آموز",
          text: "ببینید هر دانش‌آموز در هر بازی و هر آزمونک چطور عمل کرده است: امتیاز، اشتباه‌ها، زمان و صفحه‌ای که به آن رسیده است.",
        },
        {
          title: "هر دستگاهی",
          text: "در مرورگر و در برنامه‌های آیپد، آیفون، اندروید و مک کار می‌کند، پس با دستگاه‌هایی که مدرسه‌تان از قبل دارد سازگار است.",
        },
        {
          title: "اول، حریم خصوصی",
          text: "بدون تبلیغات و بدون ردیابی. داده‌های کلاس فقط برای اداره کلاس‌ها استفاده می‌شوند و هر وقت مدرسه نیاز داشته باشد، با آن قرارداد پردازش داده امضا می‌کنیم.",
        },
        {
          title: "لایسنس برای تیم‌ها و مدرسه‌ها",
          text: "یک لایسنس برای چند معلم، همراه با یک مدیر لایسنس. دانش‌آموزان همیشه رایگان از Make It استفاده می‌کنند.",
        },
      ],
      sections: [
        {
          kicker: "کلاس‌ها",
          title: "یک کلاس برای هر گروه",
          text: "برای هر گروه یک کلاس بسازید، کد آن را به دانش‌آموزان بدهید و پروژه‌ها را با آن‌ها به اشتراک بگذارید: با کل کلاس، یا فقط با دانش‌آموزانی که به آن‌ها نیاز دارند.",
          bullets: [
            "دانش‌آموزان با یک کد به کلاس می‌پیوندند",
            "اشتراک‌گذاری با کل کلاس یا با چند نفر",
            "جابه‌جایی دانش‌آموزان بین کلاس‌ها وقتی گروه‌ها تغییر می‌کنند",
          ],
        },
        {
          kicker: "پیگیری",
          title: "بدانید هر دانش‌آموز چطور پیش می‌رود",
          text: "بازی‌ها امتیاز، اشتباه‌ها و زمان هر دانش‌آموز را ثبت می‌کنند، و آزمونک‌ها علاوه بر این، همه پاسخ‌ها، رتبه‌بندی زنده و آمار هر سؤال را هم در اختیارتان می‌گذارند.",
          bullets: ["نتایج هر بازی و هر آزمونک", "پاسخ‌ها و آمار هر سؤال", "رتبه‌بندی‌هایی که به کلاس انگیزه می‌دهند"],
        },
        {
          kicker: "ساختن و اشتراک‌گذاری",
          title: "معلمان مواد آموزشی خودشان را می‌سازند",
          text: "هر معلم در چند دقیقه فعالیت‌ها را با متن‌ها، تصاویر و صدای خودش متناسب با دانش‌آموزانش می‌کند. پروژه‌ها در فضای ابری ذخیره می‌شوند و می‌توان آن‌ها را با معلمان دیگر مدرسه به اشتراک گذاشت.",
          bullets: ["بدون نیاز به برنامه‌نویسی", "کپی و بومی‌سازی پروژه‌های همکاران", "همگام در وب و در برنامه‌ها"],
        },
      ],
      finalTitle: "Make It را به مدرسه‌تان بیاورید",
      finalSub: "با یک کلاس رایگان شروع کنید، یا برای دریافت لایسنس برای همه معلمانتان به ما بنویسید.",
    },
    special: {
      title: "Make It برای نیازهای ویژه",
      eyebrow: "Make It برای نیازهای ویژه آموزشی",
      heroTitle: "فعالیت‌هایی متناسب با هر یادگیرنده",
      heroSub: "با Make It، معلمان، درمانگران و خانواده‌ها بازی‌هایی متناسب با هر فرد می‌سازند: با تصاویر خودش، صدای خودش و سرعت خودش. برای آموزش استثنایی، گفتاردرمانی، کلاس‌های فراگیر و در خانه.",
      start: "رایگان شروع کنید",
      second: "یک نمونه را امتحان کنید",
      tilesTitle: "طراحی‌شده برای برداشتن موانع",
      tiles: [
        { title: "بدون نیاز به خواندن", text: "دستورالعمل‌ها و اجزا می‌توانند صدا داشته باشند: با یک لمس، یادگیرنده می‌شنود چه کاری باید انجام دهد." },
        {
          title: "اشتباه‌ها به ضررشان حساب نمی‌شود",
          text: "بازی‌ها به هر موفقیت پاداش می‌دهند، هرگز اشتباه‌ها را جریمه نمی‌کنند و هر چند بار که لازم باشد اجازه تلاش می‌دهند.",
        },
        {
          title: "دنیای خودشان",
          text: "از عکس‌های وسایل، مکان‌ها و آدم‌های آشنای خودشان استفاده کنید و صدای خودتان را ضبط کنید تا صدای کسی را بشنوند که می‌شناسند.",
        },
        { title: "متن خوانا", text: "اندازه هر متن را انتخاب کنید و قلم‌هایی مانند OpenDyslexic یا یک قلم دست‌نویس مدرسه‌ای را برگزینید." },
        {
          title: "آرامش در صورت نیاز",
          text: "جلوه‌های صوتی یا موسیقی را خاموش کنید و تصمیم بگیرید که یادگیرنده بتواند آزادانه بین صفحه‌ها جابه‌جا شود یا نه.",
        },
        {
          title: "حرکات ساده",
          text: "لمس کردن، کشیدن، وصل کردن و دنبال کردن خطوط با انگشت روی تبلت: فعالیت‌هایی که مهارت‌های حرکتی ظریف را هم تقویت می‌کنند.",
        },
      ],
      sections: [
        {
          kicker: "شخصی‌سازی",
          title: "متناسب با هر یادگیرنده",
          text: "هر پروژه را می‌توان با یک فرد تطبیق داد: با علایق، واژگان و سطح او. یک فعالیت را تکثیر کنید و در یک لحظه آن را برای یادگیرنده دیگری تنظیم کنید.",
          bullets: ["عکس‌ها و نقاشی‌های خودتان", "صدای ضبط‌شده خودتان", "انتخاب اندازه متن و قلم", "هزاران تصویر، از جمله احساسات و زندگی روزمره"],
        },
        {
          kicker: "ایجاد انگیزه",
          title: "تقویت مثبت در هر صفحه",
          text: "هر موفقیت با ستاره و صدا جشن گرفته می‌شود و اشتباه‌ها فقط فرصتی برای تلاش دوباره‌اند. در بازی‌ها محدودیت زمانی وجود ندارد، پس هر کس می‌تواند با سرعت خودش پیش برود.",
          bullets: ["تلاش‌های نامحدود", "ستاره و صدا بعد از هر صفحه", "بدون فشار زمانی"],
        },
        {
          kicker: "پیگیری",
          title: "هر یادگیرنده، فعالیت‌های خودش",
          text: "پروژه‌های مشخصی را با دانش‌آموزان مشخصی در یک کلاس به اشتراک بگذارید و ببینید هر کدام چطور عمل می‌کنند: امتیاز، اشتباه‌ها و زمان، صفحه به صفحه.",
          bullets: ["اشتراک‌گذاری فقط با یک یا چند دانش‌آموز", "نتایج هر بازی", "روی تبلت، رایانه و گوشی کار می‌کند"],
        },
      ],
      finalTitle: "اولین فعالیت را برای یادگیرندگانتان بسازید",
      finalSub: "شروع آن رایگان است و به هیچ دانش فنی نیاز ندارید.",
    },
  },
};

const ur: AudienceText = {
  forTitle: "Make It برائے…",
  forSub: "دیکھیں کہ Make It ہر ماحول میں کیسے مدد کرتا ہے۔",
  schools: "اسکول",
  special: "خصوصی ضروریات",
  schoolsLine: "پورے اسکول کے لیے کلاس رومز، نتائج اور لائسنس۔",
  specialLine: "ہر سیکھنے والے کے مطابق ڈھالی گئی سرگرمیاں۔",
  more: "مزید جانیں",
  pages: {
    schools: {
      title: "Make It اسکولوں کے لیے",
      eyebrow: "Make It اسکولوں کے لیے",
      heroTitle: "پورے اسکول کے لیے سیکھنے کے گیمز",
      heroSub: "اساتذہ منٹوں میں اپنے گیمز اور کوئزز بناتے ہیں، انہیں اپنے کلاس رومز کے ساتھ شیئر کرتے ہیں اور ہر طالب علم کی پیش رفت پر نظر رکھتے ہیں۔ ویب پر، ٹیبلیٹس پر اور فونز پر۔",
      start: "مفت شروع کریں",
      second: "ہم سے رابطہ کریں",
      tilesTitle: "اسکول Make It کیوں چنتے ہیں",
      tiles: [
        {
          title: "ہر مضمون اور ہر عمر کے لیے",
          text: "20 سے زیادہ قسم کی سرگرمیاں اور ہزاروں تصاویر اور آوازوں والی لائبریریاں، کسی بھی مضمون کے لیے تیار، نرسری سے سیکنڈری اسکول تک۔",
        },
        {
          title: "اکاؤنٹ کے بغیر طلبہ",
          text: "طلبہ کوڈ اور اپنے نام سے کلاس روم میں شامل ہوتے ہیں۔ نہ ای میل ایڈریس، نہ پاس ورڈ اور نہ کچھ انسٹال کرنا۔",
        },
        {
          title: "ہر طالب علم کے نتائج",
          text: "دیکھیں کہ ہر طالب علم نے ہر گیم اور ہر کوئز میں کیسی کارکردگی دکھائی: اسکور، غلطیاں، وقت اور وہ کن صفحات تک پہنچا۔",
        },
        {
          title: "کوئی بھی ڈیوائس",
          text: "یہ براؤزر میں اور iPad، iPhone، Android اور Mac کی ایپس میں کام کرتا ہے، اس لیے آپ کے اسکول میں پہلے سے موجود ڈیوائسز پر چل جاتا ہے۔",
        },
        {
          title: "رازداری سب سے پہلے",
          text: "نہ اشتہارات، نہ ٹریکنگ۔ کلاس روم کا ڈیٹا صرف کلاس رومز چلانے کے لیے استعمال ہوتا ہے، اور ضرورت ہو تو ہم اسکول کے ساتھ ڈیٹا پروسیسنگ کے معاہدے پر دستخط کرتے ہیں۔",
        },
        {
          title: "ٹیموں اور اسکولوں کے لیے لائسنس",
          text: "کئی اساتذہ کے لیے ایک لائسنس، لائسنس منیجر کے ساتھ۔ طلبہ ہمیشہ Make It مفت استعمال کرتے ہیں۔",
        },
      ],
      sections: [
        {
          kicker: "کلاس رومز",
          title: "ہر گروپ کے لیے ایک کلاس روم",
          text: "ہر گروپ کے لیے کلاس روم بنائیں، طلبہ کو اس کا کوڈ دیں اور ان کے ساتھ پروجیکٹس شیئر کریں: پوری کلاس کے ساتھ، یا صرف ان طلبہ کے ساتھ جنہیں ان کی ضرورت ہے۔",
          bullets: [
            "طلبہ کوڈ سے شامل ہوتے ہیں",
            "پوری کلاس کے ساتھ یا چند طلبہ کے ساتھ شیئر کریں",
            "گروپ بدلنے پر طلبہ کو ایک کلاس روم سے دوسرے میں منتقل کریں",
          ],
        },
        {
          kicker: "پیش رفت کی نگرانی",
          title: "جانیں کہ ہر طالب علم کیسا کر رہا ہے",
          text: "گیمز ہر طالب علم کا اسکور، غلطیاں اور وقت محفوظ کرتے ہیں، اور کوئزز میں اس کے علاوہ ہر جواب، لائیو درجہ بندی اور ہر سوال کے اعداد و شمار بھی شامل ہوتے ہیں۔",
          bullets: ["ہر گیم اور ہر کوئز کے نتائج", "ہر سوال کے جوابات اور اعداد و شمار", "درجہ بندی جو کلاس کا حوصلہ بڑھاتی ہے"],
        },
        {
          kicker: "بنائیں اور شیئر کریں",
          title: "اساتذہ اپنا مواد خود بناتے ہیں",
          text: "ہر استاد منٹوں میں سرگرمیوں کو اپنے طلبہ کے مطابق ڈھالتا ہے، اپنے متن، تصاویر اور آواز کے ساتھ۔ پروجیکٹس کلاؤڈ میں محفوظ ہوتے ہیں اور اسکول کے دوسرے اساتذہ کے ساتھ شیئر کیے جا سکتے ہیں۔",
          bullets: ["کوڈنگ کی کوئی ضرورت نہیں", "ساتھیوں کے پروجیکٹس کاپی کر کے اپنے مطابق ڈھالیں", "ویب اور ایپس پر ہم آہنگ"],
        },
      ],
      finalTitle: "Make It کو اپنے اسکول میں لائیں",
      finalSub: "ایک کلاس روم کے ساتھ مفت شروع کریں، یا اپنے تمام اساتذہ کے لیے لائسنس حاصل کرنے کے لیے ہمیں لکھیں۔",
    },
    special: {
      title: "Make It خصوصی ضروریات کے لیے",
      eyebrow: "Make It خصوصی تعلیمی ضروریات کے لیے",
      heroTitle: "ہر سیکھنے والے کے مطابق ڈھالی گئی سرگرمیاں",
      heroSub: "Make It کے ساتھ اساتذہ، تھراپسٹ اور گھر والے ہر فرد کے مطابق گیمز بناتے ہیں: اس کی تصاویر، اس کی آواز، اس کی رفتار۔ خصوصی تعلیم، اسپیچ تھراپی، جامع کلاس رومز اور گھر کے لیے۔",
      start: "مفت شروع کریں",
      second: "ڈیمو آزمائیں",
      tilesTitle: "رکاوٹیں دور کرنے کے لیے ڈیزائن کیا گیا",
      tiles: [
        { title: "پڑھنا ضروری نہیں", text: "ہدایات اور عناصر میں آواز شامل کی جا سکتی ہے: ایک ٹیپ، اور سیکھنے والا سن لیتا ہے کہ کیا کرنا ہے۔" },
        {
          title: "غلطیاں ان کے خلاف شمار نہیں ہوتیں",
          text: "گیمز ہر کامیابی کو سراہتے ہیں، غلطیوں پر کبھی سزا نہیں دیتے اور جتنی ضرورت ہو اتنی کوششوں کی اجازت دیتے ہیں۔",
        },
        {
          title: "ان کی اپنی دنیا",
          text: "ان کی اپنی چیزوں، جگہوں اور لوگوں کی تصاویر استعمال کریں، اور اپنی آواز ریکارڈ کریں تاکہ وہ کسی جانے پہچانے فرد کی آواز سنیں۔",
        },
        { title: "پڑھنے میں آسان متن", text: "ہر متن کا سائز چنیں اور OpenDyslexic یا اسکول کی خوش خطی جیسے فونٹس منتخب کریں۔" },
        { title: "ضرورت ہو تو سکون", text: "صوتی اثرات یا موسیقی بند کریں، اور طے کریں کہ سیکھنے والا صفحات کے درمیان آزادانہ آ جا سکتا ہے یا نہیں۔" },
        {
          title: "آسان اشارے",
          text: "ٹیبلیٹ پر انگلی سے ٹیپ کریں، گھسیٹیں، جوڑیں اور لکیروں پر چلیں: ایسی سرگرمیاں جو باریک حرکی مہارتوں کو بھی بہتر بناتی ہیں۔",
        },
      ],
      sections: [
        {
          kicker: "اپنے مطابق بنائیں",
          title: "ہر سیکھنے والے کے لیے خاص طور پر تیار",
          text: "ہر پروجیکٹ کو ایک فرد کے مطابق ڈھالا جا سکتا ہے: اس کی دلچسپیاں، اس کا ذخیرہ الفاظ اور اس کی سطح۔ کسی سرگرمی کی نقل بنائیں اور لمحوں میں اسے کسی دوسرے سیکھنے والے کے لیے بدل دیں۔",
          bullets: [
            "آپ کی اپنی تصاویر اور ڈرائنگز",
            "آپ کی اپنی ریکارڈ کی ہوئی آواز",
            "متن کا سائز اور فونٹس آپ کی پسند کے",
            "ہزاروں تصاویر، جن میں جذبات اور روزمرہ زندگی بھی شامل ہیں",
          ],
        },
        {
          kicker: "حوصلہ بڑھائیں",
          title: "ہر صفحے پر مثبت حوصلہ افزائی",
          text: "ہر کامیابی کا جشن ستاروں اور آوازوں سے منایا جاتا ہے، اور غلطی پر سیکھنے والا بس دوبارہ کوشش کرتا ہے۔ گیمز میں وقت کی کوئی حد نہیں، اس لیے ہر کوئی اپنی رفتار سے آگے بڑھ سکتا ہے۔",
          bullets: ["لامحدود کوششیں", "ہر صفحے کے بعد ستارے اور آوازیں", "وقت کا کوئی دباؤ نہیں"],
        },
        {
          kicker: "پیش رفت کی نگرانی",
          title: "ایک سیکھنے والا، اس کی اپنی سرگرمیاں",
          text: "کلاس روم میں مخصوص پروجیکٹس مخصوص طلبہ کے ساتھ شیئر کریں اور دیکھیں کہ ہر ایک کیسا کر رہا ہے: اسکور، غلطیاں اور وقت، صفحہ بہ صفحہ۔",
          bullets: ["صرف ایک یا چند طلبہ کے ساتھ شیئر کریں", "ہر گیم کے نتائج", "ٹیبلیٹس، کمپیوٹرز اور فونز پر کام کرتا ہے"],
        },
      ],
      finalTitle: "اپنے سیکھنے والوں کے لیے پہلی سرگرمی بنائیں",
      finalSub: "شروع کرنا مفت ہے اور کسی تکنیکی علم کی ضرورت نہیں۔",
    },
  },
};

const ms: AudienceText = {
  forTitle: "Make It untuk…",
  forSub: "Lihat bagaimana Make It membantu dalam setiap persekitaran.",
  schools: "Sekolah",
  special: "Keperluan khas",
  schoolsLine: "Kelas, keputusan dan lesen untuk seluruh sekolah.",
  specialLine: "Aktiviti yang disesuaikan untuk setiap pelajar.",
  more: "Ketahui lebih lanjut",
  pages: {
    schools: {
      title: "Make It untuk sekolah",
      eyebrow: "Make It untuk sekolah",
      heroTitle: "Permainan pembelajaran untuk seluruh sekolah",
      heroSub: "Guru mencipta permainan dan kuiz mereka sendiri dalam beberapa minit, berkongsi dengan kelas mereka dan memantau kemajuan setiap murid. Di web, pada tablet dan pada telefon.",
      start: "Mula secara percuma",
      second: "Hubungi kami",
      tilesTitle: "Mengapa sekolah memilih Make It",
      tiles: [
        {
          title: "Untuk setiap mata pelajaran dan peringkat umur",
          text: "Lebih daripada 20 jenis aktiviti dan pustaka dengan beribu-ribu imej dan bunyi, sesuai untuk apa-apa mata pelajaran, dari prasekolah hingga sekolah menengah.",
        },
        {
          title: "Murid tanpa akaun",
          text: "Murid menyertai kelas dengan kod dan nama mereka. Tiada alamat e-mel, tiada kata laluan dan tiada apa-apa yang perlu dipasang.",
        },
        {
          title: "Keputusan setiap murid",
          text: "Lihat prestasi setiap murid dalam setiap permainan dan setiap kuiz: skor, kesilapan, masa dan halaman yang dicapai.",
        },
        {
          title: "Mana-mana peranti",
          text: "Make It berfungsi dalam pelayar dan dalam aplikasi untuk iPad, iPhone, Android dan Mac, jadi ia sesuai dengan peranti yang sedia ada di sekolah anda.",
        },
        {
          title: "Privasi diutamakan",
          text: "Tiada iklan dan tiada penjejakan. Data kelas hanya digunakan untuk mengendalikan kelas, dan kami menandatangani perjanjian pemprosesan data dengan sekolah apabila diperlukan.",
        },
        {
          title: "Lesen untuk pasukan dan sekolah",
          text: "Satu lesen untuk beberapa orang guru, dengan pengurus lesen. Murid sentiasa menggunakan Make It secara percuma.",
        },
      ],
      sections: [
        {
          kicker: "Kelas",
          title: "Satu kelas untuk setiap kumpulan",
          text: "Cipta kelas untuk setiap kumpulan, berikan kodnya kepada murid dan kongsi projek dengan mereka: dengan seluruh kelas, atau hanya dengan murid yang memerlukannya.",
          bullets: [
            "Murid menyertai kelas dengan kod",
            "Kongsi dengan seluruh kelas atau dengan beberapa murid",
            "Alihkan murid antara kelas apabila kumpulan berubah",
          ],
        },
        {
          kicker: "Pemantauan",
          title: "Ketahui prestasi setiap murid",
          text: "Permainan merekodkan skor, kesilapan dan masa setiap murid, manakala kuiz turut merekodkan setiap jawapan, kedudukan secara langsung dan statistik setiap soalan.",
          bullets: ["Keputusan setiap permainan dan setiap kuiz", "Jawapan dan statistik bagi setiap soalan", "Kedudukan yang memotivasikan kelas"],
        },
        {
          kicker: "Cipta dan kongsi",
          title: "Guru menghasilkan bahan mereka sendiri",
          text: "Setiap guru menyesuaikan aktiviti untuk murid mereka dalam beberapa minit, dengan teks, gambar dan suara mereka sendiri. Projek disimpan dalam awan dan boleh dikongsi dengan guru lain di sekolah.",
          bullets: ["Tiada pengekodan diperlukan", "Salin dan suaikan projek rakan sejawat", "Disegerakkan di web dan dalam aplikasi"],
        },
      ],
      finalTitle: "Bawa Make It ke sekolah anda",
      finalSub: "Mula secara percuma dengan satu kelas, atau hubungi kami untuk mendapatkan lesen bagi semua guru anda.",
    },
    special: {
      title: "Make It untuk keperluan khas",
      eyebrow: "Make It untuk keperluan pendidikan khas",
      heroTitle: "Aktiviti yang disesuaikan untuk setiap pelajar",
      heroSub: "Dengan Make It, guru, ahli terapi dan keluarga mencipta permainan yang disesuaikan untuk setiap individu: gambar mereka, suara mereka, rentak mereka. Untuk pendidikan khas, terapi pertuturan, kelas inklusif dan di rumah.",
      start: "Mula secara percuma",
      second: "Cuba demo",
      tilesTitle: "Direka untuk menghapuskan halangan",
      tiles: [
        {
          title: "Tidak perlu membaca",
          text: "Arahan dan elemen boleh mempunyai bunyi: sekali ketik, pelajar terus mendengar apa yang perlu dilakukan.",
        },
        {
          title: "Kesilapan tidak dikira",
          text: "Permainan menghargai setiap kejayaan, tidak sekali-kali mengenakan penalti atas kesilapan dan membenarkan seberapa banyak percubaan yang diperlukan.",
        },
        {
          title: "Dunia mereka sendiri",
          text: "Gunakan foto barang, tempat dan orang yang mereka kenali, dan rakam suara anda supaya mereka mendengar suara seseorang yang mereka kenal.",
        },
        { title: "Teks yang mudah dibaca", text: "Pilih saiz setiap teks dan fon seperti OpenDyslexic atau fon tulisan tangan sekolah." },
        {
          title: "Tenang apabila perlu",
          text: "Matikan kesan bunyi atau muzik, dan tentukan sama ada pelajar boleh bergerak antara halaman dengan bebas.",
        },
        {
          title: "Gerak isyarat yang mudah",
          text: "Ketik, seret, sambung dan surih dengan jari pada tablet: aktiviti yang turut melatih kemahiran motor halus.",
        },
      ],
      sections: [
        {
          kicker: "Peribadikan",
          title: "Disesuaikan khas untuk setiap pelajar",
          text: "Setiap projek boleh disesuaikan untuk seorang individu: minat, perbendaharaan kata dan tahapnya. Buat salinan aktiviti dan ubah suai untuk pelajar lain dalam sekelip mata.",
          bullets: [
            "Foto dan lukisan anda sendiri",
            "Rakaman suara anda sendiri",
            "Saiz teks dan fon yang boleh dipilih",
            "Beribu-ribu imej, termasuk emosi dan kehidupan seharian",
          ],
        },
        {
          kicker: "Motivasikan",
          title: "Peneguhan positif pada setiap halaman",
          text: "Setiap kejayaan diraikan dengan bintang dan bunyi, dan kesilapan hanya memberi peluang kepada pelajar untuk mencuba lagi. Tiada had masa dalam permainan, jadi semua orang boleh belajar mengikut rentak sendiri.",
          bullets: ["Percubaan tanpa had", "Bintang dan bunyi selepas setiap halaman", "Tiada tekanan masa"],
        },
        {
          kicker: "Pantau",
          title: "Seorang pelajar, aktivitinya sendiri",
          text: "Kongsi projek tertentu dengan murid tertentu dalam kelas dan lihat prestasi setiap seorang: skor, kesilapan dan masa, halaman demi halaman.",
          bullets: ["Kongsi dengan seorang atau beberapa murid sahaja", "Keputusan setiap permainan", "Berfungsi pada tablet, komputer dan telefon"],
        },
      ],
      finalTitle: "Cipta aktiviti pertama untuk pelajar anda",
      finalSub: "Percuma untuk bermula dan anda tidak memerlukan sebarang pengetahuan teknikal.",
    },
  },
};

const az: AudienceText = {
  forTitle: "Make It kimlər üçün…",
  forSub: "Make It-in hər mühitdə necə kömək etdiyinə baxın.",
  schools: "Məktəblər",
  special: "Xüsusi ehtiyaclar",
  schoolsLine: "Bütün məktəb üçün siniflər, nəticələr və lisenziyalar.",
  specialLine: "Hər öyrənənə uyğunlaşdırılmış fəaliyyətlər.",
  more: "Ətraflı",
  pages: {
    schools: {
      title: "Make It məktəblər üçün",
      eyebrow: "Make It məktəblər üçün",
      heroTitle: "Bütün məktəb üçün öyrədici oyunlar",
      heroSub: "Müəllimlər bir neçə dəqiqəyə öz oyunlarını və testlərini yaradır, onları sinifləri ilə paylaşır və hər şagirdin irəliləyişini izləyirlər. Vebdə, planşetlərdə və telefonlarda.",
      start: "Pulsuz başlayın",
      second: "Bizimlə əlaqə",
      tilesTitle: "Məktəblər niyə Make It-i seçir",
      tiles: [
        {
          title: "Hər fənn və yaş üçün",
          text: "20-dən çox fəaliyyət növü və minlərlə şəkil və səsdən ibarət kitabxanalar, məktəbəqədər təhsildən orta məktəbədək istənilən fənn üçün hazırdır.",
        },
        {
          title: "Hesabsız şagirdlər",
          text: "Şagirdlər kod və adları ilə sinfə qoşulurlar. Nə e-poçt ünvanı, nə şifrə lazımdır, nə də nəyisə quraşdırmaq.",
        },
        {
          title: "Hər şagirdin nəticələri",
          text: "Hər şagirdin hər oyunda və hər testdə necə çıxış etdiyini görün: xallar, səhvlər, vaxt və çatdığı səhifələr.",
        },
        {
          title: "İstənilən cihaz",
          text: "Brauzerdə və iPad, iPhone, Android və Mac tətbiqlərində işləyir, ona görə də məktəbinizdə artıq olan cihazlara uyğun gəlir.",
        },
        {
          title: "Məxfilik ön planda",
          text: "Reklam və izləmə yoxdur. Sinif məlumatları yalnız siniflərin işləməsi üçün istifadə olunur və ehtiyac olduqda məktəblə məlumatların emalı haqqında müqavilə imzalayırıq.",
        },
        {
          title: "Komandalar və məktəblər üçün lisenziyalar",
          text: "Bir neçə müəllim üçün lisenziya meneceri olan bir lisenziya. Şagirdlər Make It-dən həmişə pulsuz istifadə edir.",
        },
      ],
      sections: [
        {
          kicker: "Siniflər",
          title: "Hər qrup üçün bir sinif",
          text: "Hər qrup üçün sinif yaradın, onun kodunu şagirdlərə verin və layihələri onlarla paylaşın: bütün siniflə və ya yalnız ehtiyacı olan şagirdlərlə.",
          bullets: [
            "Şagirdlər kodla qoşulur",
            "Bütün siniflə və ya bir neçə şagirdlə paylaşın",
            "Qruplar dəyişəndə şagirdləri siniflər arasında köçürün",
          ],
        },
        {
          kicker: "İzləmə",
          title: "Hər şagirdin vəziyyətini bilin",
          text: "Oyunlar hər şagirdin xalını, səhvlərini və vaxtını qeyd edir, testlər isə buna hər cavabı, canlı reytinqi və hər sualın statistikasını əlavə edir.",
          bullets: [
            "Hər oyunun və hər testin nəticələri",
            "Hər sual üzrə cavablar və statistika",
            "Sinfi həvəsləndirən reytinqlər",
          ],
        },
        {
          kicker: "Yaradın və paylaşın",
          title: "Müəllimlər öz materiallarını hazırlayır",
          text: "Hər müəllim fəaliyyətləri bir neçə dəqiqəyə öz mətnləri, şəkilləri və səsi ilə şagirdlərinə uyğunlaşdırır. Layihələr buludda saxlanılır və məktəbin digər müəllimləri ilə paylaşıla bilər.",
          bullets: [
            "Proqramlaşdırma bilmək lazım deyil",
            "Həmkarların layihələrini kopyalayın və uyğunlaşdırın",
            "Vebdə və tətbiqlərdə sinxron",
          ],
        },
      ],
      finalTitle: "Make It-i məktəbinizə gətirin",
      finalSub: "Bir siniflə pulsuz başlayın və ya bütün müəllimləriniz üçün lisenziya almaq üçün bizə yazın.",
    },
    special: {
      title: "Make It xüsusi ehtiyaclar üçün",
      eyebrow: "Make It xüsusi təhsil ehtiyacları üçün",
      heroTitle: "Hər öyrənənə uyğunlaşdırılmış fəaliyyətlər",
      heroSub: "Make It ilə müəllimlər, terapevtlər və ailələr hər insana uyğun oyunlar yaradır: onun şəkilləri, onun səsi, onun tempi. Xüsusi təhsil, loqopediya, inklüziv siniflər və ev üçün.",
      start: "Pulsuz başlayın",
      second: "Demonu sınayın",
      tilesTitle: "Maneələri aradan qaldırmaq üçün hazırlanıb",
      tiles: [
        {
          title: "Oxumaq lazım deyil",
          text: "Təlimatlar və elementlər səsli ola bilər: bir toxunuş və öyrənən nə etməli olduğunu eşidir.",
        },
        {
          title: "Səhvlər əleyhinə sayılmır",
          text: "Oyunlar hər uğuru mükafatlandırır, səhvləri heç vaxt cəzalandırmır və lazım olduğu qədər cəhd etməyə imkan verir.",
        },
        {
          title: "Öz dünyası",
          text: "Onun öz əşyalarının, yerlərinin və insanlarının fotolarından istifadə edin və tanıdığı birinin səsini eşitməsi üçün səsinizi yazın.",
        },
        {
          title: "Oxunaqlı mətn",
          text: "Hər mətnin ölçüsünü və OpenDyslexic və ya məktəb əlyazması kimi şriftləri seçin.",
        },
        {
          title: "Lazım olanda sakitlik",
          text: "Səs effektlərini və ya musiqini söndürün və öyrənənin səhifələr arasında sərbəst keçə bilib-bilməyəcəyinə özünüz qərar verin.",
        },
        {
          title: "Sadə jestlər",
          text: "Planşetdə barmaqla toxunun, sürüşdürün, birləşdirin və xətt çəkin: incə motorikanı da inkişaf etdirən fəaliyyətlər.",
        },
      ],
      sections: [
        {
          kicker: "Fərdiləşdirin",
          title: "Hər öyrənən üçün fərdi hazırlanıb",
          text: "Hər layihəni bir insana uyğunlaşdırmaq olar: onun maraqlarına, söz ehtiyatına və səviyyəsinə. Fəaliyyətin surətini çıxarın və bir anda başqa öyrənən üçün tənzimləyin.",
          bullets: [
            "Öz fotolarınız və rəsmləriniz",
            "Öz yazdığınız səs",
            "Seçmək üçün mətn ölçüləri və şriftlər",
            "Emosiyalar və gündəlik həyat da daxil olmaqla minlərlə şəkil",
          ],
        },
        {
          kicker: "Həvəsləndirin",
          title: "Hər səhifədə müsbət stimul",
          text: "Hər uğur ulduzlar və səslərlə qeyd olunur, səhv isə sadəcə yenidən cəhd etmək imkanı verir. Oyunlarda vaxt limiti yoxdur, ona görə də hər kəs öz tempi ilə irəliləyə bilər.",
          bullets: [
            "Limitsiz cəhd",
            "Hər səhifədən sonra ulduzlar və səslər",
            "Vaxt təzyiqi yoxdur",
          ],
        },
        {
          kicker: "İzləyin",
          title: "Bir öyrənən, öz fəaliyyətləri",
          text: "Konkret layihələri sinifdəki konkret şagirdlərlə paylaşın və onların hər birinin necə çıxış etdiyini görün: səhifə-səhifə xal, səhvlər və vaxt.",
          bullets: [
            "Yalnız bir və ya bir neçə şagirdlə paylaşın",
            "Hər oyunun nəticələri",
            "Planşetlərdə, kompüterlərdə və telefonlarda işləyir",
          ],
        },
      ],
      finalTitle: "Öyrənənləriniz üçün ilk fəaliyyəti yaradın",
      finalSub: "Başlamaq pulsuzdur və heç bir texniki bilik tələb olunmur.",
    },
  },
};

const uz: AudienceText = {
  forTitle: "Make It kimlar uchun…",
  forSub: "Make It har bir sohada qanday yordam berishini koʻring.",
  schools: "Maktablar",
  special: "Alohida ehtiyojlar",
  schoolsLine: "Butun maktab uchun sinflar, natijalar va litsenziyalar.",
  specialLine: "Har bir oʻquvchiga moslashtirilgan mashgʻulotlar.",
  more: "Batafsil",
  pages: {
    schools: {
      title: "Maktablar uchun Make It",
      eyebrow: "Maktablar uchun Make It",
      heroTitle: "Butun maktab uchun oʻquv oʻyinlari",
      heroSub: "Oʻqituvchilar bir necha daqiqada oʻz oʻyinlari va viktorinalarini yaratadi, ularni oʻz sinflari bilan ulashadi va har bir oʻquvchining oʻzlashtirishini kuzatib boradi. Vebda, planshetlarda va telefonlarda.",
      start: "Bepul boshlash",
      second: "Biz bilan bogʻlaning",
      tilesTitle: "Nega maktablar Make It xizmatini tanlaydi",
      tiles: [
        {
          title: "Har qanday fan va yosh uchun",
          text: "20 dan ortiq turdagi mashgʻulotlar hamda minglab rasm va tovushlardan iborat kutubxonalar maktabgacha taʼlimdan oʻrta maktabgacha boʻlgan har qanday fan uchun tayyor.",
        },
        {
          title: "Hisobsiz oʻquvchilar",
          text: "Oʻquvchilar sinfga kod va ismi bilan qoʻshiladi. E-pochta ham, parol ham kerak emas, hech narsa oʻrnatish shart emas.",
        },
        {
          title: "Har bir oʻquvchining natijalari",
          text: "Har bir oʻquvchi har bir oʻyin va viktorinada qanday natija koʻrsatganini koʻring: ballar, xatolar, vaqt va qaysi sahifagacha yetib borgani.",
        },
        {
          title: "Istalgan qurilma",
          text: "Brauzerda hamda iPad, iPhone, Android va Mac ilovalarida ishlaydi, shuning uchun maktabingizdagi mavjud qurilmalarga mos keladi.",
        },
        {
          title: "Maxfiylik birinchi oʻrinda",
          text: "Reklama ham, kuzatuv ham yoʻq. Sinf maʼlumotlari faqat sinflar ishlashi uchun ishlatiladi, zarur boʻlsa, maktab bilan maʼlumotlarni qayta ishlash toʻgʻrisida shartnoma tuzamiz.",
        },
        {
          title: "Jamoalar va maktablar uchun litsenziyalar",
          text: "Bir nechta oʻqituvchi uchun bitta litsenziya va litsenziya administratori. Oʻquvchilar Make It xizmatidan doim bepul foydalanadi.",
        },
      ],
      sections: [
        {
          kicker: "Sinflar",
          title: "Har bir guruh uchun alohida sinf",
          text: "Har bir guruh uchun sinf yarating, oʻquvchilarga uning kodini bering va loyihalarni ular bilan ulashing: butun sinf bilan yoki faqat kerakli oʻquvchilar bilan.",
          bullets: [
            "Oʻquvchilar kod orqali qoʻshiladi",
            "Butun sinf yoki bir nechta oʻquvchi bilan ulashing",
            "Guruhlar oʻzgarganda oʻquvchilarni sinflar oʻrtasida koʻchiring",
          ],
        },
        {
          kicker: "Kuzatish",
          title: "Har bir oʻquvchining ahvolidan xabardor boʻling",
          text: "Oʻyinlar har bir oʻquvchining balli, xatolari va vaqtini qayd etadi, viktorinalar esa bunga har bir javob, jonli reyting va har bir savol statistikasini qoʻshadi.",
          bullets: [
            "Har bir oʻyin va har bir viktorina natijalari",
            "Har bir savol boʻyicha javoblar va statistika",
            "Sinfni ruhlantiradigan reytinglar",
          ],
        },
        {
          kicker: "Yarating va ulashing",
          title: "Oʻqituvchilar oʻz materiallarini oʻzlari tayyorlaydi",
          text: "Har bir oʻqituvchi bir necha daqiqada mashgʻulotlarni oʻz matnlari, rasmlari va ovozi bilan oʻquvchilariga moslashtiradi. Loyihalar bulutda saqlanadi va maktabdagi boshqa oʻqituvchilar bilan ulashilishi mumkin.",
          bullets: [
            "Dasturlash shart emas",
            "Hamkasblarning loyihalaridan nusxa olib moslashtiring",
            "Vebda va ilovalarda sinxronlanadi",
          ],
        },
      ],
      finalTitle: "Make It xizmatini maktabingizga olib keling",
      finalSub: "Bitta sinf bilan bepul boshlang yoki barcha oʻqituvchilaringiz uchun litsenziya olish maqsadida bizga yozing.",
    },
    special: {
      title: "Alohida ehtiyojlar uchun Make It",
      eyebrow: "Alohida taʼlim ehtiyojlari uchun Make It",
      heroTitle: "Har bir oʻquvchiga moslashtirilgan mashgʻulotlar",
      heroSub: "Make It yordamida oʻqituvchilar, terapevtlar va oilalar har bir insonga moslangan oʻyinlar yaratadi: uning rasmlari, uning ovozi, uning surʼati. Maxsus taʼlim, logopediya, inklyuziv sinflar va uy sharoiti uchun.",
      start: "Bepul boshlash",
      second: "Namunani sinab koʻring",
      tilesTitle: "Toʻsiqlarni olib tashlash uchun yaratilgan",
      tiles: [
        {
          title: "Oʻqish shart emas",
          text: "Koʻrsatmalar va elementlar ovozli boʻlishi mumkin: bir bosish bilan oʻquvchi nima qilish kerakligini eshitadi.",
        },
        {
          title: "Xatolar hisobga olinmaydi",
          text: "Oʻyinlar har bir muvaffaqiyatni ragʻbatlantiradi, xatolar uchun hech qachon jazolamaydi va kerakli miqdorda urinishga imkon beradi.",
        },
        {
          title: "Oʻz olami",
          text: "Uning buyumlari, joylari va yaqinlarining suratlaridan foydalaning, oʻz ovozingizni yozib oling, shunda u tanish ovozni eshitadi.",
        },
        {
          title: "Oʻqilishi oson matn",
          text: "Har bir matnning oʻlchamini hamda OpenDyslexic yoki maktab yozuv shrifti kabi shriftlarni tanlang.",
        },
        {
          title: "Kerak boʻlganda sokinlik",
          text: "Ovoz effektlari yoki musiqani oʻchiring va oʻquvchi sahifalar oʻrtasida erkin oʻta oladimi yoki yoʻqligini oʻzingiz hal qiling.",
        },
        {
          title: "Oddiy harakatlar",
          text: "Planshetda barmoq bilan bosing, torting, bogʻlang va chizing: bu mashgʻulotlar mayda motorikani ham rivojlantiradi.",
        },
      ],
      sections: [
        {
          kicker: "Moslashtiring",
          title: "Har bir oʻquvchiga moslab yaratilgan",
          text: "Har bir loyihani bitta insonga moslashtirish mumkin: uning qiziqishlari, soʻz boyligi va darajasiga. Mashgʻulotni nusxalang va bir zumda boshqa oʻquvchiga moslang.",
          bullets: [
            "Oʻz suratlaringiz va chizmalaringiz",
            "Oʻzingiz yozib olgan ovoz",
            "Tanlash mumkin boʻlgan matn oʻlchami va shriftlar",
            "Minglab rasmlar, jumladan his-tuygʻular va kundalik hayot",
          ],
        },
        {
          kicker: "Ruhlantiring",
          title: "Har bir sahifada ijobiy ragʻbatlantirish",
          text: "Har bir muvaffaqiyat yulduzchalar va tovushlar bilan nishonlanadi, xato esa shunchaki yana urinib koʻrish imkonini beradi. Oʻyinlarda vaqt chegarasi yoʻq, shuning uchun har kim oʻz surʼatida harakat qila oladi.",
          bullets: [
            "Cheksiz urinishlar",
            "Har bir sahifadan keyin yulduzchalar va tovushlar",
            "Vaqt bosimi yoʻq",
          ],
        },
        {
          kicker: "Kuzatib boring",
          title: "Bitta oʻquvchi, oʻz mashgʻulotlari",
          text: "Muayyan loyihalarni sinfdagi muayyan oʻquvchilar bilan ulashing va ularning har biri qanday natija koʻrsatayotganini kuzating: ball, xatolar va vaqt, sahifama-sahifa.",
          bullets: [
            "Faqat bitta yoki bir nechta oʻquvchi bilan ulashing",
            "Har bir oʻyin natijalari",
            "Planshetlarda, kompyuterlarda va telefonlarda ishlaydi",
          ],
        },
      ],
      finalTitle: "Oʻquvchilaringiz uchun birinchi mashgʻulotni yarating",
      finalSub: "Boshlash bepul va hech qanday texnik bilim talab qilinmaydi.",
    },
  },
};

const fil: AudienceText = {
  forTitle: "Make It para sa…",
  forSub: "Tingnan kung paano nakatutulong ang Make It sa bawat sitwasyon.",
  schools: "Mga Paaralan",
  special: "Espesyal na pangangailangan",
  schoolsLine: "Mga klase, resulta at license para sa buong paaralan.",
  specialLine: "Mga aktibidad na iniangkop sa bawat mag-aaral.",
  more: "Alamin pa",
  pages: {
    schools: {
      title: "Make It para sa mga paaralan",
      eyebrow: "Make It para sa mga paaralan",
      heroTitle: "Mga larong pang-aral para sa buong paaralan",
      heroSub: "Gumagawa ang mga guro ng sarili nilang mga laro at quiz sa loob ng ilang minuto, ini-share ang mga ito sa kanilang mga klase at sinusubaybayan ang progreso ng bawat estudyante. Sa web, sa tablet at sa phone.",
      start: "Magsimula nang libre",
      second: "Makipag-ugnayan sa amin",
      tilesTitle: "Bakit pinipili ng mga paaralan ang Make It",
      tiles: [
        {
          title: "Para sa bawat subject at edad",
          text: "Higit sa 20 uri ng aktibidad at mga library na may libu-libong larawan at tunog, handa para sa anumang subject, mula preschool hanggang high school.",
        },
        {
          title: "Mga estudyanteng walang account",
          text: "Sumasali ang mga estudyante sa klase gamit ang code at ang kanilang pangalan. Walang email address, walang password at walang kailangang i-install.",
        },
        {
          title: "Resulta ng bawat estudyante",
          text: "Tingnan kung paano nagawa ng bawat estudyante ang bawat laro at quiz: iskor, mali, oras at ang mga pahinang naabot nila.",
        },
        {
          title: "Kahit anong device",
          text: "Gumagana ito sa browser at sa mga app para sa iPad, iPhone, Android at Mac, kaya bagay ito sa mga device na mayroon na ang iyong paaralan.",
        },
        {
          title: "Privacy ang inuuna",
          text: "Walang advertising at walang tracking. Ginagamit lang ang data ng klase para patakbuhin ang mga klase, at pumipirma kami ng data processing agreement sa paaralan kapag kailangan nito.",
        },
        {
          title: "License para sa mga team at paaralan",
          text: "Isang license para sa ilang guro, may license manager. Laging libre ang Make It para sa mga estudyante.",
        },
      ],
      sections: [
        {
          kicker: "Mga Klase",
          title: "Isang klase para sa bawat grupo",
          text: "Gumawa ng klase para sa bawat grupo, ibigay sa mga estudyante ang code nito at i-share sa kanila ang mga proyekto: sa buong klase, o sa mga estudyanteng nangangailangan lang.",
          bullets: [
            "Sumasali ang mga estudyante gamit ang code",
            "I-share sa buong klase o sa iilan",
            "Ilipat ang mga estudyante sa ibang klase kapag nagbago ang mga grupo",
          ],
        },
        {
          kicker: "Pagsubaybay",
          title: "Alamin kung kumusta ang bawat estudyante",
          text: "Itinatala ng mga laro ang iskor, mga mali at oras ng bawat estudyante, at idinadagdag ng mga quiz ang bawat sagot, live na ranking at istatistika ng bawat tanong.",
          bullets: [
            "Resulta ng bawat laro at bawat quiz",
            "Mga sagot at istatistika bawat tanong",
            "Mga ranking na nagpapasigla sa klase",
          ],
        },
        {
          kicker: "Gumawa at mag-share",
          title: "Ang mga guro ang gumagawa ng sarili nilang materyales",
          text: "Iniaangkop ng bawat guro ang mga aktibidad sa kanyang mga estudyante sa loob ng ilang minuto, gamit ang sarili niyang teksto, larawan at boses. Naka-save sa cloud ang mga proyekto at puwedeng i-share sa ibang guro ng paaralan.",
          bullets: [
            "Hindi kailangang marunong mag-code",
            "Kopyahin at iangkop ang mga proyekto ng mga kasamahan",
            "Naka-sync sa web at sa mga app",
          ],
        },
      ],
      finalTitle: "Dalhin ang Make It sa iyong paaralan",
      finalSub: "Magsimula nang libre sa isang klase, o sumulat sa amin para makakuha ng license para sa lahat ng iyong guro.",
    },
    special: {
      title: "Make It para sa espesyal na pangangailangan",
      eyebrow: "Make It para sa mga mag-aaral na may espesyal na pangangailangan",
      heroTitle: "Mga aktibidad na iniangkop sa bawat mag-aaral",
      heroSub: "Sa Make It, gumagawa ang mga guro, therapist at pamilya ng mga larong angkop sa bawat tao: sa kanyang mga larawan, sa kanyang boses, sa kanyang bilis. Para sa special education, speech therapy, inclusive na klase at sa bahay.",
      start: "Magsimula nang libre",
      second: "Subukan ang demo",
      tilesTitle: "Dinisenyo para alisin ang mga hadlang",
      tiles: [
        {
          title: "Hindi kailangang magbasa",
          text: "Puwedeng may tunog ang mga instruction at elemento: isang tap lang at maririnig ng mag-aaral ang dapat gawin.",
        },
        {
          title: "Hindi binibilang laban sa kanila ang mga mali",
          text: "Ginagantimpalaan ng mga laro ang bawat tagumpay, hindi kailanman pinaparusahan ang mga mali at pinapayagan ang kahit ilang pagsubok.",
        },
        {
          title: "Ang sarili nilang mundo",
          text: "Gumamit ng mga larawan ng sarili nilang mga gamit, lugar at tao, at i-record ang iyong boses para marinig nila ang taong kilala nila.",
        },
        {
          title: "Madaling basahing teksto",
          text: "Piliin ang laki ng bawat teksto at mga font tulad ng OpenDyslexic o school handwriting font.",
        },
        {
          title: "Tahimik kapag kailangan",
          text: "I-off ang sound effects o ang musika, at magpasya kung puwedeng malayang lumipat ng pahina ang mag-aaral.",
        },
        {
          title: "Simpleng galaw",
          text: "Mag-tap, mag-drag, magdugtong at magbakat gamit ang daliri sa tablet: mga aktibidad na nagsasanay rin ng fine motor skills.",
        },
      ],
      sections: [
        {
          kicker: "I-personalize",
          title: "Sukat na sukat sa bawat mag-aaral",
          text: "Puwedeng iangkop ang bawat proyekto sa iisang tao: sa kanyang mga hilig, bokabularyo at antas. I-duplicate ang isang aktibidad at baguhin ito para sa ibang mag-aaral sa isang iglap.",
          bullets: [
            "Sarili mong mga larawan at drawing",
            "Sarili mong na-record na boses",
            "Laki ng teksto at mga font na mapagpipilian",
            "Libu-libong larawan, kasama ang mga damdamin at pang-araw-araw na buhay",
          ],
        },
        {
          kicker: "Magpasigla",
          title: "Positive reinforcement sa bawat pahina",
          text: "Ipinagdiriwang ang bawat tagumpay gamit ang mga bituin at tunog, at ang mga mali ay pagkakataon lang para sumubok ulit. Walang limitasyon sa oras sa mga laro, kaya makakausad ang lahat sa sarili nilang bilis.",
          bullets: [
            "Walang limitasyong pagsubok",
            "Mga bituin at tunog pagkatapos ng bawat pahina",
            "Walang pressure sa oras",
          ],
        },
        {
          kicker: "Subaybayan",
          title: "Isang mag-aaral, sariling mga aktibidad",
          text: "I-share ang mga partikular na proyekto sa mga partikular na estudyante sa isang klase at tingnan kung paano ang bawat isa: iskor, mga mali at oras, pahina por pahina.",
          bullets: [
            "I-share sa isa o iilang estudyante lang",
            "Resulta ng bawat laro",
            "Gumagana sa tablet, computer at phone",
          ],
        },
      ],
      finalTitle: "Gawin ang unang aktibidad para sa iyong mga mag-aaral",
      finalSub: "Libreng magsimula at hindi mo kailangan ng anumang teknikal na kaalaman.",
    },
  },
};

const TEXTS: Record<string, AudienceText> = { en, ca, es, fr, pt, it, de, ar, he, eu, gl, nl, pl, ro, sv, el, tr, ru, uk, id, vi, cs, sk, hu, hr, bg, da, nb, fi, fa, ur, ms, az, uz, fil };

export function audienceText(): AudienceText {
  return TEXTS[currentLanguage()] ?? en;
}
